/*
 * Toutes les chaînes de l'interface, dans les deux langues.
 *
 * Le français est typé contre l'anglais : une clé présente dans l'un et pas
 * dans l'autre est une erreur de compilation, pas une chaîne manquante
 * découverte en production par un utilisateur québécois. C'est le mécanisme
 * du site du studio, et il vaut ici pour la même raison — sauf qu'ici la
 * langue par défaut est le français, parce que le premier lieu est à
 * Montréal.
 */

export const en = {
  app: {
    name: 'Portage',
    loading: 'Loading…',
    error: 'Something went wrong.',
    retry: 'Try again',
    cancel: 'Cancel',
    chooseFile: 'Choose a file',
    noFile: 'No file chosen',
    save: 'Save',
    delete: 'Delete',
    refresh: 'Refresh',
    close: 'Close',
    confirm: 'Confirm',
    search: 'Search',
    today: 'Today',
    all: 'All',
    none: 'None',
    filters: 'Filters',
    results: 'results',
    of: 'of',
    forbidden: 'Your role does not give you access to this. Ask an owner or manager.',
  },
  theme: {
    label: 'Theme',
    system: 'Match system',
    light: 'Light',
    dark: 'Dark',
  },
  auth: {
    signIn: 'Sign in',
    email: 'Email',
    password: 'Password',
    signOut: 'Sign out',
    invalidCredentials: 'That email and password do not match.',
    notInVenueTeam: 'Your password is right, but your account is not in this venue’s team. Ask someone who manages the team to add you (Team → Roles), then try again a few seconds later.',
    /* Une session coupée par un changement de mot de passe est une sécurité
       qui FONCTIONNE. Sans cette phrase, elle se lit comme une panne. */
    sessionRevoked: 'The password on this account changed. Sign in again.',
    forgot: 'Forgotten your password?',
    forgotHint: 'We will send you a link to choose a new one.',
    forgotSend: 'Send the link',
    forgotSent: 'Check your inbox',
    forgotSentHint:
      'If that address has an account, a link is on its way. It expires in an hour. Nothing changes until you use it.',
    backToSignIn: 'Back to sign in',
    choosePassword: 'Choose a new password',
    newPassword: 'New password',
    newPasswordHint: 'At least ten characters.',
    tooShort: 'Ten characters minimum.',
    linkExpired: 'This link has expired or has already been used. Ask for a new one.',
    tagline: 'The back office that carries the load.',
    madeWith: 'Made with',
    by: 'by',
  },
  platform: {
    wrong: 'What needs attention',
    allGood: 'Nothing to fix',
    allGoodHint: 'Every headset is reporting, every screen has been reached, every published title can be bought.',
    fleet: 'The fleet',
    devices: 'Devices',
    deployed: 'Deployed',
    inCase: 'In their case',
    silent: 'Silent',
    silentShort: 'silent',
    byVenue: 'Where they are',
    catalogue: 'What runs where',
    media: 'briefings',
    noMedia: 'No briefing',
    upcoming: 'upcoming',
    noPrice: 'no price',
    noSession: 'no session',
    draft: 'Being prepared',
    statesTitle: 'What these three states mean',
    stateLive: 'Sellable',
    stateLiveHint: 'Published, with a price and an upcoming session. Its public page is online and the booking funnel works.',
    stateStuck: 'Online but stuck',
    stateStuckHint: 'Published without a price, or without an upcoming session: the page is visible and nothing can be bought on it. To fix now.',
    stateDraftHint: 'Not published. Its public page is offline — and nothing else is affected: counter sales, scanning, registration and live ops all work, and tickets sold elsewhere stay valid.',
    statesFoot: 'An instance is born unpublished on purpose: with no price and no session, publishing it would put a visible, unbuyable page online.',
    noTitles: 'No title deployed yet',
    noTitlesHint: 'Drop a title on a venue from the console to provision an instance there.',
    alerts: {
      devices_offline: 'headsets have gone silent',
      devices_unclaimed: 'devices belong to nobody',
      screens_never_seen: 'screens have never called in',
      deliveries_failed: 'partner sign-ups failed',
      venues_orphan: 'venues have no studio entity',
      instances_unsellable: 'published titles cannot be bought',
      titles_without_media: 'titles have no briefing',
    },
  },
  planning: {
    title: 'Schedule',
    autofill: 'Fill the week',
    publish: 'Publish',
    publishTitle: 'Publish this week?',
    publishBody: '{n} draft shifts become visible, and everyone concerned gets their own by email. What is already published does not move.',
    hours: 'Hours',
    clashOne: 'overlap',
    clashes: 'overlaps',
    clashWith: 'Also scheduled at {venue}, {from} – {to}. Nobody is in two places at once — unless they move between them.',
    addShift: 'Add a shift',
    removeShift: 'Remove this shift',
    from: 'From',
    to: 'To',
    shiftHint: 'Everyone in this range is assigned to every session that starts inside it. Sessions added later are picked up too.',
    noTeam: 'Nobody works at this venue yet',
    noTeamHint: 'Add someone under Positions, next to this tab — a schedule needs people before it needs hours.',
    availability: 'Availability',
    addRule: 'Add a window',
    ruleKind: 'Kind',
    kindAvailable: 'Available',
    kindUnavailable: 'Away',
    ruleMode: 'Repeat',
    modeWeekly: 'Every week',
    modeDates: 'Specific dates',
    weekday: 'Day',
    ruleNote: 'Reason',
    noRules: 'No availability declared',
    noRulesHint: 'Declare who works which days, and “Fill the week” proposes a schedule you can adjust. Away beats available — a holiday always wins over a usual day.',
    mine: 'My schedule',
    mineEmpty: 'No shift ahead',
    mineEmptyHint: 'Your schedule appears here once the venue publishes it.',
    sessionsCovered: 'sessions',
  },
  board: {
    roomCut: 'No contact with the cloud',
    roomNeverSynced: 'never synced',
    roomStillWorks: 'The board, the headsets and the session keep working. Everything will go up when the link returns.',
    roomRefused: '{n} refused by the cloud',
    roomPending: '{n} action(s) waiting to go up',
    xroamDown: 'This experience runs on XROAM and cannot start without the link.',
    xroamNext: 'Note the person and reschedule, or offer an experience that still runs.',
    pilotedFromRoom: 'Piloted from the room',
    lastContact: 'last contact {age} ago',
    takeOver: 'Take over',
    groupWord: 'Group',
    totalTime: 'Time in the experience, from entering the room to finishing',
    totalTimeShort: 'Total',
    householdWord: '{name} household',
    changeHeadset: 'Change',
    changeHeadsetHint: 'Give this person another headset. The previous one is released in the engine, the new one linked.',
    changeHeadsetLong: 'Change headset',
    changeHeadsetFor: 'Another headset for {name}',
    slotHint: 'Slot on the ticket',
    experienceBand: 'Experience: {name}',
    householdPlain: 'Came together',
    selectStart: 'Group',
    selectDone: 'Done',
    selectHint: 'Tap the cards that travel together.',
    selectCount: '{n} selected',
    groupMixed: 'These cards belong to two different experiences.',
    groupNotSupported: 'This experience does not run in groups — everyone enters as their headset is ready.',
    groupTooLarge: 'This experience takes at most {n} people at a time.',
    groupThem: 'Group them',
    ungroupThem: 'Ungroup',
    grouped: 'Cards grouped.',
    ungrouped: 'Cards ungrouped.',
    groupWaitingTitle: 'A member is still waiting',
    groupWaitingBody: 'Someone in this group has no headset yet. Sending this person in alone leaves them on the other side without the others.',
    groupWaitingGo: 'Send in anyway',
    /* « Temps réel » et non « Opérations » : l'onglet voisin est le matériel,
       qui est de l'exploitation lui aussi. Ce qui distingue celui-ci est qu'il
       montre l'instant présent. */
    title: 'Board',
    languageSent: 'Language requested: {language}.',
    groupLanguage: 'Group language',
    empty: 'Nobody on site',
    emptyHint: 'A card appears here the moment a ticket is scanned — one per person, not one per booking.',
    here: 'here for {n} min',
    pair: 'Pair a headset',
    unpair: 'release',
    start: 'Started',
    finish: 'Finished ✓',
    noDevice: 'No headset free — they are all out or being cleaned.',
    otherDevice: 'another ({n})',
    silent: 'silent',
    overtime: 'over',
    tuneHeadset: 'Headset settings',
    headsetSettings: 'Headset settings',
    volume: 'Volume',
    ipd: 'Interpupillary distance',
    ipdAsked: 'Interpupillary distance last asked for — the headset does not report it',
    notWorn: 'Headset is not being worn',
    silentClosing: 'Headset silent for {n} min · ends automatically in {left} min',
    enginePairedHint: 'Paired by the engine (its kiosk linked this person to the headset), not by a gesture.',
    engineLinked: 'Linked to headset {n} in {engine} by Portage — nothing to do in its app.',
    engineWaiting: 'Will be linked in {engine} as soon as it has registered this person.',
    engineErr: 'Not linked in {engine}:',
    engineErrNumber: 'Portage does not know this headset’s {engine} number.',
    engineErrDevice: 'headset {n} does not exist in {engine}.',
    engineErrSetup: 'the {engine} Operator App is not set up for this venue.',
    engineErrRefused: '{engine} refused or did not answer; Portage retries every minute.',
    engineCreatedHint: 'Created by the engine: this user is unknown to Portage — no registration, no waiver or consent on our side.',
    engineOutside: 'outside Portage',
    unregistered: 'Not registered',
    lobby: 'Lobby',
    unregisteredWorn: 'worn, not registered',
    attach: 'Attach to a card…',
    attachTitle: 'Attach {name} to a card',
    attachHint: 'The person was registered, but their headset was never paired. Their card takes over the headset and enters the room at the time the visit actually started; the “{name}” card goes away.',
    attachNone: 'No card is waiting for a headset for this experience.',
    attachDo: 'Attach',
    attachRegistered: 'registered',
    attachArrived: 'arrived',
    unregisteredHint: 'This headset was started without anyone going through registration: no waiver and no consent on file. The card carries the headset’s name, as XROAM does, and ends on its own when the visit ends. The headset is not offered for pairing in the meantime.',
    noImage: 'No image',
    imageYes: 'Image authorised',
    imageYesHint: 'The venue is filming and this person has authorised the use of their image.',
    menu: 'More',
    devIgnoredOne: '{headset} announces a dev visitor ({name}): ignored, this venue is not a sandbox.',
    devIgnoredMany: '{n} headsets announce a dev visitor, ignored — this venue is not a sandbox: {list}.',
    devIgnoredHint: 'To test here, switch the venue to sandbox (studio console), or create the card by hand.',
    noImageHint: 'The venue is filming and this person has not authorised the use of their image: keep them out of shot, or blur them.',
    silentClosed: 'Ended automatically: the headset went silent',
    finishing: 'End of the experience: headset taken off, the card moves to Done within a minute',
    finishingManual: 'End of the experience: headset taken off, move the card to Done',
    storageLowHint: 'Free disk space on the headset, below the venue’s threshold. Free some space before it runs out.',
    sceneOverrun: '{scene}: {elapsed} min, {expected} planned',
    deviceQuiet: 'Headset has gone quiet',
    deviceLost: 'Headset unreachable',
    tooHot: 'Headset about to shut down — too hot',
    throttling: 'Headset is throttling — the image will stutter',
    thermalAndroid: 'chip: Android state {level}/6, {headroom} % of the throttling threshold',
    unplayable: 'Too hot to hold VR frame rate — get them out before they feel sick',
    tempUnknown: 'No headset reports a temperature yet — nothing is being hidden.',
    headroomHint:
      'How far along the way to thermal throttling, where 100% is the throttling point. This headset reports no degrees — the Android API that does needs a system permission.',
    batteryFlat: 'Battery about to die',
    autoStarts: 'Starts on its own',
    autoStartsHint: 'This experience starts when the group is complete.',
    helpTitle: 'Log that we had to help',
    helpHeading: 'Helped {name}',
    helpBody:
      'Leaves a mark on the card for whoever takes over, and goes to the studio with the headset’s state and the chapter the person is in.',
    helpCategory: 'What happened',
    helpCategories: {
      comfort: 'Discomfort or unwell',
      lost: 'Stuck or lost in the experience',
      fitting: 'Headset fitting',
      tracking: 'Tracking, position, recentre',
      audio_visual: 'Sound or image',
      freeze: 'Freeze or black screen (restarted)',
      app_crash: 'The app crashed',
      session_restart: 'Session restarted',
      headset_swap: 'Headset swapped',
      battery: 'Battery or charging',
      avatars: 'Avatars out of sync',
      network: 'Network or server',
      other: 'Other',
    } as Record<string, string>,
    helpHistory: 'Already helped on this card',
    helpReason: 'What happened (optional)',
    helpReasonHint: 'Headset badly adjusted, scared visitor, scene stuck…',
    helpConfirm: 'Log it',
    helpDone: 'Intervention logged',
    remove: 'Take out of the queue',
    removeTitle: 'Take {name} out?',
    removeBody:
      'The card leaves the board and the headset goes back to the tray. Nothing is deleted: the times already recorded are kept, and rescanning the ticket will not bring the card back.',
    removeReason: 'Why (optional)',
    removeReasonHint: 'Headset failed, visitor gave up, card opened by mistake…',
    removeConfirm: 'Take out',
    allExperiences: 'All experiences',
    stages: {
      arrived: 'Arrived',
      registered: 'Registered',
      paired: 'Paired',
      in_room: 'In the experience',
      done: 'Finished',
    },
  },
  /*
   * LES RETOURS D'ACTION.
   *
   * Peu de chaînes, volontairement. Un message par bouton se serait traduit
   * par cinquante phrases dont quarante disent « c'est fait » : ce qu'on
   * nomme est ce qui n'est pas évident à l'écran, le reste tombe dans
   * `saved` ou `failed`.
   */
  toast: {
    saved: 'Saved.',
    deleted: 'Deleted.',
    sent: 'Sent.',
    failed: 'That did not go through.',
    visitRemoved: '{name} is out of the queue.',
    visitAttached: '{device}’s visit now belongs to {name}.',
    visitRemovedHeadset: 'The headset is back in the tray.',
    published: 'Schedule published — the team has been emailed.',
    filled: '{n} shift(s) proposed. Nothing is published yet.',
    revoked: 'Access revoked.',
    activated: 'Now live.',
    deactivated: 'Switched off.',
    duplicated: 'Copy created.',
    imported: '{n} contact(s) imported.',
    screenSent: 'Sent to the screen.',
    assigned: 'Assigned.',
    unassigned: 'Removed.',
  },
  /*
   * LE MÉNAGE DU VOLUME.
   *
   * Les libellés parlent de POIDS avant de parler de nombre : trois fichiers
   * ne décident rien, deux gigaoctets transportés chaque semaine décident.
   */
  orphans: {
    title: 'Files nothing points at any more',
    banner: '{n} file(s) nothing points at any more ({size}) — deleted automatically on {when}.',
    bannerBlocked: '{n} file(s) nothing points at any more ({size}) — too many for the night purge, which leaves them alone: check them, then delete by hand.',
    show: 'Details',
    hide: 'Hide',
    purgeOn: 'Deleted on',
    purgeNow: 'Delete now',
    hint: 'Deleting a title takes its media rows with it and leaves the files behind. These are on the volume and referenced by nothing; the night purge deletes them after three days in a row unclaimed.',
    weight: '{size} carried by every backup — {copies} archives in the retention window, so {total} of storage and upload.',
    file: 'File',
    size: 'Size',
    age: 'On disk',
    days: '{n} d',
    purge: 'Delete them',
    purged: '{n} file(s) deleted.',
    confirmTitle: 'Delete {n} file(s)?',
    confirmBody:
      'They leave the disk for good — {size} reclaimed. Nothing in the database points at them, and the server rechecks that before deleting. A backup restored from before today still holds them.',
  },
  legal: {
    title: 'Terms and privacy',
    hint: 'Shown at the end of registration, next to the venue’s and the partner’s. One address per language — a consent given on a text nobody can read is not a consent.',
    terms: 'Terms',
    privacy: 'Privacy',
  },
  maps: {
    title: 'Space maps',
    hint: 'Survey the room with ONE headset, then push the file to the rest. Surveying each headset separately gives slightly different maps, so visitors do not see each other in the same place.',
    empty: 'No map stored yet.',
    file: 'File (.zip)',
    name: 'Name',
    platform: 'Captured on',
    anyPlatform: 'Any platform',
    store: 'Store',
    storing: 'Storing…',
    stored: 'Map stored',
    target: 'Push to',
    pickGroup: 'Pick a group…',
    push: 'Push',
    pushing: 'Pushing…',
    queued: 'Queued on {n} headsets — installing takes a few minutes',
    noneInRoom: 'No map for this room yet. Survey the room with one headset, export the map, and store it here.',
    history: '{n} earlier version(s)',
    newVersion: 'Store a new survey',
    firstMap: 'Store the room survey',
    noneQueued: 'No headset of this room can take this map (other brand, or not able to install maps).',
    skipped: '{n} set aside',
    status: 'in service on {ok} · pending {pending} · refused {failed}',
    notInCloudYet: 'not yet in Portage Cloud',
    pushRoom: 'Install on the room’s headsets',
    pushAgain: 'Install this version again',
    fileHint: 'The .zip the headset exports (Pico: map, markers, boundary). One file per brand.',
    studioHint: 'Maps belong to rooms: they are stored and installed from the room (Portage Room, or Rooms in Portage Cloud). Read-only here, for support.',
    where: 'Venue · room',
    date: 'Stored',
    download: 'Download',
    why: {
      platform_mismatch: 'another platform',
      device_not_placed: 'not in a venue yet',
      no_server: 'no experience server to carry it',
    },
  },
  map: {
    title: 'Map',
    heatNone: 'People only',
    heatLive: 'Live heat',
    heatDay: 'Today',
    heatLiveHint: 'How many people per 50 cm cell right now, smoothed — like Creator’s simulation.',
    heatDayHint: 'Time spent per 50 cm cell since the start of the day ({day}) — red = the busiest cells.',
    heatDayTotal: '{h} h of presence so far.',
    heatShow: 'Show heat',
    heatHide: 'Hide heat',
    heatOpacity: 'Opacity',
    noPlan: 'No floor plan yet',
    noPlanHint:
      'The plan is imported from Creator by the studio (MCP tool set_floor_plan). It arrives here at the next sync.',
    feedNever: 'No positions yet',
    feedNeverHint: 'The experience server is not sending positions on this network.',
    feedStopped: 'Position feed interrupted',
    feedStoppedHint:
      'The dots are hidden on purpose: showing where people were a minute ago would send you to the wrong place.',
    onFloor: 'on the floor',
    scale: '2 m',
    count: '{n} on the floor plan',
    adrift: '{n} off the plan',
    adriftHint:
      'Their position falls outside the floor plan — usually a headset that has not been re-centred on the reference station, not a visitor in a wall.',
    unaligned: '{n} lost alignment',
    unalignedHint:
      'These headsets no longer hold the room frame — either the room says so, or their position jumped faster than a person can move. Usually a visitor who held the system button down. The position looks plausible and is wrong, so they are not drawn. Re-centre them.',
  },
  nav: {
    platform: 'Platform',
    backTo: 'Back to {name}',
    backToVenue: 'Back to the venue',
    more: 'More entries below',
    venueLabel: 'Venue',
    collapse: 'Collapse the menu',
    expand: 'Expand the menu',
    /* Le même mot pour les deux types de compte : seul le CONTENU change. */
    dashboard: 'Dashboard',
    today: 'Today',
    calendar: 'Calendar',
    bookings: 'Bookings',
    customers: 'Customers',
    experiences: 'Experiences',
    fleet: 'Headsets',
    rooms: 'Rooms',
    welcome: 'Welcome desk',
    staff: 'Staff',
    settings: 'Settings',
    groupToday: 'The day',
    analytics: 'Analytics',
    groupSell: 'Selling',
    groupSetup: 'Set up',
  },
  dashboard: {
    title: 'Today',
    occupancy: 'Occupancy',
    trend: 'Last 30 days',
    vsPrevious: 'vs. previous 15 days',
    seatsSold: 'Seats sold',
    revenue: 'Revenue today',
    upcomingWeek: 'Next seven days',
    needsAttention: 'Needs attention',
    allClear: 'Nothing needs you right now.',
    onDuty: 'On duty',
    noSessions: 'No sessions scheduled today.',
    sessionsToday: 'Sessions today',
    alertStaffMissing: 'No operator assigned',
    alertBelowMinimum: 'Below minimum, starts soon',
    alertCheckInIncomplete: 'Check-in incomplete',
  },
  calendar: {
    title: 'Calendar',
    day: 'Day',
    week: 'Week',
    month: 'Month',
    list: 'List',
    allExperiences: 'All experiences',
    seatsLeft: 'left',
    full: 'Full',
    empty: 'Empty',
    cancelled: 'Cancelled',
    closed: 'Closed',
    private: 'Private',
    noSessions: 'No sessions in this range.',
    previous: 'Previous',
    next: 'Next',
  },
  session: {
    time: 'Time',
    unassign: 'Remove from this session',
    staffMissing: '{count} still needed',
    roster: 'Roster',
    capacity: 'Capacity',
    whereWeAre: 'Where we are',
    group: 'The group',
    settings: 'Configuration',
    arrived: 'Arrived',
    inExperience: 'In the experience',
    notStarted: 'Not started',
    /*
     * Une salle XROAM ne délimite pas une séance : sa machine à états tourne
     * depuis le démarrage de son serveur. Nommer la durée pour ce qu'elle est
     * vaut mieux que la taire — un champ absent fait chercher, un chiffre sans
     * unité juste fait conclure.
     */
    serverUpFor: 'Server up for',
    inferredVisit: 'visit inferred from the room’s state',
    runStates: {
      pending: 'Waiting',
      briefing: 'Briefing',
      running: 'Running',
      paused: 'Paused',
      ended: 'Over',
      aborted: 'Cut short',
    },
    sold: 'Sold',
    held: 'On hold',
    available: 'Available',
    walkIn: 'No ticket needed',
    walkInToggle: 'People can walk in without a booking',
    walkInHint:
      'For a playtest, a press visit or a run-through. Registration stops asking for a booking reference, and the experience still gets its sign-ups.',
    guests: 'Guests',
    staff: 'Operators',
    assignGuide: 'Assign an operator',
    noTeamHint: 'Nobody works at this venue yet. Add someone under Team › Positions, then assign them here.',
    noGuests: 'Nobody booked yet.',
    checkIn: 'Check in',
    checkedIn: 'Checked in',
    noShow: 'No-show',
    internalNote: 'Internal note',
    addBooking: 'Add a booking',
    cancelSession: 'Cancel session',
    changeCapacity: 'Change capacity',
  },
  booking: {
    reference: 'Reference',
    status: 'Status',
    source: 'Source',
    seats: 'Seats',
    total: 'Total',
    contact: 'Contact',
    bookedOn: 'Booked',
    pending: 'On hold',
    confirmed: 'Confirmed',
    cancelled: 'Cancelled',
    expired: 'Expired',
    checkedIn: 'Checked in',
    noShow: 'No-show',
    online: 'Online',
    counter: 'Counter',
    phone: 'Phone',
    import: 'Imported',
    partner: 'Partner',
    cancelBooking: 'Cancel booking',
    departure: 'Departure',
    searchPlaceholder: 'Reference, name, email, phone',
    noResults: 'No bookings match.',
  },
  counter: {
    title: 'Sell at the counter',
    /* Le libellé COURT de la barre du haut : deux gestes côte à côte, le titre
       complet y prendrait la moitié de la largeur. */
    action: 'Sell',
    chooseSession: 'Choose a session',
    tickets: 'Tickets',
    customer: 'Customer',
    payment: 'Payment',
    method: 'Method',
    card: 'Card',
    cash: 'Cash',
    check: 'Cheque',
    invoice: 'Invoice',
    other: 'Other',
    collectNow: 'Take payment now',
    payLater: 'Hold without payment',
    marketingOptIn: 'They agreed to hear about upcoming events',
    complete: 'Complete sale',
    sold: 'Sold.',
  },
  campaigns: {
    title: 'Campaigns',
    status: 'Status',
    journeys: 'Automations',
    newCampaign: 'New campaign',
    name: 'Campaign name',
    draft: 'Draft',
    scheduled: 'Scheduled',
    sending: 'Sending',
    sent: 'Sent',
    failed: 'Failed',
    audience: 'To',
    audienceAll: 'All subscribed contacts',
    exclude: 'Do not send to',
    subject: 'Subject',
    content: 'Content',
    review: 'Review',
    recipients: 'Recipients',
    opened: 'Opened',
    clicked: 'Clicked',
    revenue: 'Revenue',
    attributedBookings: 'Bookings',
    send: 'Send now',
    sendConfirm: 'Send to {count} contacts?',
    locked: 'A sent campaign can no longer be edited.',
    noCampaigns: 'No campaigns yet.',
    addBlock: 'Add',
    blockHeading: 'Heading',
    blockText: 'Paragraph',
    blockButton: 'Button',
    blockImage: 'Image',
    blockDivider: 'Divider',
    blockExperience: 'Experience',
    blockExperienceHint: 'Filled from the catalogue at send time',
    preview: 'Preview',
    remove: 'Remove',
    attributionNote: 'Revenue booked within 7 days of the send.',
    journeyReminder: 'Day-before reminder',
    journeyPostVisit: 'After the visit',
    journeyAbandonedCart: 'Abandoned cart',
    journeyWinback: 'Win-back',
    journeyReminderDesc: 'Practical details the day before. Cuts no-shows.',
    journeyPostVisitDesc: 'Thanks, a review request, and what to see next.',
    journeyAbandonedCartDesc: 'A booking left unfinished gets one nudge.',
    journeyWinbackDesc: 'Visitors who have not come back in six months.',
    enabled: 'On',
    disabled: 'Off',
    timing: 'Timing',
    hoursBefore: 'hours before',
    hoursAfter: 'hours after',
    daysBefore: 'days before',
    daysAfter: 'days after',
    runNow: 'Run now',
  },
  unsubscribe: {
    title: 'Email preferences',
    subscribed: 'You are subscribed to news from {venue}.',
    already: 'You are unsubscribed. You will not receive any more marketing emails.',
    confirm: 'Unsubscribe',
    resubscribe: 'Subscribe again',
    done: 'Done. You are unsubscribed.',
    resubscribed: 'You are subscribed again.',
    invalid: 'This link is not valid. It may have been truncated by your email client.',
    note: 'This only affects marketing emails. Booking confirmations and reminders for tickets you have already bought will still reach you.',
  },
  modify: {
    title: 'Modify',
    reschedule: 'Move to another time',
    uncheck: 'Undo check-in',
    uncheckHint: 'Removes their cards from the board. Refused if anyone has a headset.',
    uncheckBlocked: 'Someone is already on their way — handle them on the board first.',
    refund: 'Refund',
    quantity: 'Change guests',
    cancel: 'Cancel booking',
    balanceDue: 'Balance due',
    refundDue: 'Refund due',
    settled: 'Settled',
    paid: 'Paid',
    amount: 'Amount',
    reason: 'Reason',
    chooseTime: 'Choose a new time',
    sameExperience: 'Only times for the same experience are shown.',
    checkInCleared: 'Moving a booking clears its check-in.',
    refundHint: 'You cannot refund more than what was collected.',
    apply: 'Apply',
    payments: 'Payments',
    tickets: 'Ticket codes',
  },
  onboarding: {
    title: 'Check-in',
    welcome: 'Welcome',
    findBooking: 'Find your booking',
    emailLabel: 'The email you booked with',
    or: 'or',
    referenceLabel: 'Your booking reference',
    findIt: 'Continue',
    notFound: 'We could not find a booking. Check the spelling, or use your booking reference.',
    noneHere: 'This venue has no registration set up yet. Nothing to fill in — we will welcome you at the door.',
    notServedHere: 'That form is not the one used here. Ask the front desk for the right link.',
    ambiguous: 'Several bookings match. Please use your booking reference.',
    /* Le lieu n'a pas dit quelle œuvre ce parcours dessert. Le visiteur n'y
       peut rien : la phrase s'adresse donc à qui peut agir. */
    experienceRequired: 'We could not tell which experience to register you for. Please let the front desk know — nothing has been saved.',
    /* Chaque défaut NOMMÉ, à l'étape où il se corrige (14/10). */
    invalid: {
      email: 'The email address is not valid. Check it (for example name@example.com).',
      emailRequired: 'An email address is required for an adult.',
      date: 'This date is not valid.',
      birthdateFuture: 'The date of birth cannot be in the future.',
      phone: 'This phone number is not valid.',
      number: 'Enter a number.',
      signature: 'The signature could not be read. Please sign again.',
      guardian: 'Enter the name of the parent or guardian.',
      required: 'This field is required.',
      field: 'This answer was refused. Please check it.',
      preferences: 'One of your preferences was refused. Please choose again.',
      emailShared: 'Each adult needs their own email address.',
    },
    serverRefused: 'Your registration was not sent: one answer needs fixing. You are back where it can be corrected — nothing you entered has been lost.',
    /*
     * Posé à quelqu'un qui a dit ne pas avoir de réservation, et dont
     * l'adresse en porte pourtant une. La question s'adresse à la seule
     * personne qui connaît la réponse, et le refus doit être aussi facile
     * que l'acceptation — une adresse familiale peut servir à deux groupes
     * du même soir.
     */
    claimTitle: 'Is one of these yours?',
    claimHint: 'We found a booking for that email address. Picking it keeps you and your group together at the door.',
    claimNone: 'None of these — continue without a booking',
    whichTicket: 'Which ticket are you registering for?',
    ticketDone: 'Done',
    ticketLeft: 'to fill in',
    whoFor: 'Who are you checking in?',
    whoForHint: 'Everyone in your group needs to be registered. You can do it one after another on this device.',
    adult: 'Adult',
    minor: 'Child',
    done: 'Done',
    alreadyOnFile: 'Already on file',
    remaining: 'Still to do',
    startFor: 'Register',
    venueTerms: '{venue} — I accept its terms and its privacy policy.',
    imageRightsTitle: 'Image rights · optional',
    imageRights: 'I authorise {studio} and {operator} to use photos and videos in which I appear, taken during my visit, to promote the experience (social media, websites, trade shows, advertising), without compensation, for five years. I can withdraw this authorisation by writing to the venue; content already published stays online.',
    imageRightsSingle: 'I authorise {studio} to use photos and videos in which I appear, taken during my visit, to promote the experience (social media, websites, trade shows, advertising), without compensation, for five years. I can withdraw this authorisation by writing to the venue; content already published stays online.',
    engineTerms: 'Terms of service',
    enginePrivacy: 'Privacy policy',
    minorNotice: 'A child does not sign. An adult present signs for them.',
    guardianLabel: 'Name of the adult signing',
    signHere: 'Sign here',
    clear: 'Clear',
    next: 'Next',
    back: 'Back',
    finish: 'Finish',
    allDone: 'Everyone is registered.',
    allDoneHint: 'Show this screen at the entrance, or just go in — the desk can see it.',
    oneDone: 'Registered.',
    another: 'Register someone else',
    required: 'Required',
    yourEmail: 'Your email',
    step: 'Step',
    of: 'of',
    walkIn: 'I don’t have a booking yet',
    walkInHint: 'Fill it in now and we will match it to your ticket when you buy it.',
    whichExperience: 'Which experience are you here for?',
    emailLabelPlain: 'Your email',
    findYou: 'Your email, so we can find you if you have been here before.',
    householdTitle: 'Who is here tonight?',
    householdHint:
      'We already know these people. Tick who came with you — anyone not ticked stays off tonight’s list.',
    householdEmpty: 'First time here',
    householdEmptyHint: 'We have nothing on file for that address. Register below, it takes a minute.',
    householdCovered: 'Nothing to sign',
    householdAdd: 'Someone else is with me',
    /* Venus ensemble sans réservation (14/10). */
    householdAddAdult: 'Add an adult',
    householdAddChild: 'Add a child',
    householdChildAfterAdult: 'Register the adult who signs for a child first: their name and email are carried over.',
    householdToday: 'Already registered tonight',
    householdTogetherTitle: 'Who is with you?',
    householdTogetherHint: 'Register each person in turn — adults first, then the children they sign for.',
    householdFinish: 'No, that’s everyone',
    walkInEmailHint: 'You register yourself first; then you can add the people who came with you.',
    /* Le parcours sans réservation, revu (15/10) : soi d'abord, puis les autres. */
    householdNextTitle: 'Is anyone else with you?',
    householdNextHint: 'Add each person who came with you tonight. An adult fills in their own details; a child is registered by the adult responsible for them.',
    householdStartSelf: 'Start my registration',
    whoSelf: 'Your registration',
    whoSelfHint: 'Start with yourself. You can add the people with you right after.',
    whoCompanion: 'An adult who came with you',
    whoCompanionHint: 'This person’s own details, with their own email address.',
    whoChild: 'A child you are responsible for',
    whoChildHint: '{name} signs for this child.',
    whoKnown: 'Registering {name}',
    whoSeat: 'Place {n} of {total}',
    whoSeatNamed: '{name} — place {n} of {total}',
    whoSeatHint: 'The details of the person taking this place.',
    whoSeatChildHint: 'A child: the adult responsible for them signs.',
    householdSign: 'Continue',
    householdReady: 'We are ready',
    householdBack: 'Back',
    whichExperienceHint:
      'We run several. Pick yours so the room knows you when you walk in — and so you read the right consent form.',
  },
  /*
   * L'écran de la PORTE, pas celui de la composition.
   *
   * `ob` est l'atelier — on y écrit les étapes, on y gère les tablettes, et il
   * demande `onboarding:manage`. `forms` est le geste d'à côté : montrer le
   * code à quelqu'un qui doit encore s'enregistrer. Deux vocabulaires séparés
   * parce que ce sont deux postes, et que celui de la porte ne doit pas
   * hériter des mots de l'autre.
   */
  forms: {
    title: 'Forms',
    pick: 'Pick a form to show its code.',
    hint: 'Have them scan it with their own phone. It opens this form, not the venue default.',
    none: 'No registration form is published here yet. They are written and published under Onboarding.',
  },
  ob: {
    title: 'Onboarding',
    newFlow: 'New flow',
    appliesTo: 'Applies to',
    wholeVenue: 'This venue',
    useHere: 'Use it here',
    notUsedHere: 'Not used here',
    detachHint: 'Stop using it here. The flow itself stays.',
    alsoAt: 'Also at',
    crossBorder: 'Assigned in more than one country — check the waiver holds under both laws.',
    printSheet: 'Printable sheet',
    wallQr: 'Wall QR code',
    printTitle: 'Check in before you go in',
    wallQrHint: 'Print it and stick it where the queue forms. It opens this flow, not the venue default.',
    forThisFlow: 'For this flow',
    followsVenue: 'Follows the venue',
    tabletFlow: 'Opens',
    flows: 'Flows',
    kiosks: 'Tablets',
    submissions: 'Signed',
    active: 'Active',
    inactive: 'Draft',
    activate: 'Activate',
    deactivate: 'Deactivate',
    duplicate: 'Duplicate',
    steps: 'Steps',
    registration: 'Registration',
    nda: 'NDA',
    waiver: 'Waiver',
    privacy: 'Privacy policy',
    addStep: 'Add a step',
    needsAStep:
      'Add at least one step. The NDA, the waiver and the privacy policy stay optional.',
    publish: 'Publish',
    published: 'Published',
    version: 'Version',
    languages: 'Languages',
    links: 'Where to fill it in',
    kioskUrl: 'Tablet at the entrance',
    wallUrl: 'QR code for the queue',
    venueQrHint: 'This one follows whichever flow is active here. For a specific flow — one per experience — open that flow and print its own code.',
    emailLink: 'Link in the confirmation email',
    copy: 'Copy',
    copied: 'Copied',
    newKiosk: 'Add a tablet',
    kioskName: 'Where is it?',
    revoke: 'Revoke',
    revoked: 'Revoked',
    lastSeen: 'Last seen',
    never: 'Never',
    signedAt: 'Signed',
    channel: 'Filled from',
    noFlows: 'No onboarding flow yet. Create one to collect registrations, an NDA or a waiver.',
    inUse: 'This flow has been signed and cannot be deleted.',
    blockField: 'Question',
    blockConsent: 'Checkbox',
    blockSignature: 'Signature',
    blockHeadingHint: 'A title',
    blockTextHint: 'A paragraph',
    blockFieldHint: 'Name, email, anything',
    blockConsentHint: 'Must be ticked',
    blockSignatureHint: 'Drawn with a finger',
    blockImageHint: 'A picture',
    blockDividerHint: 'A separating line',
    fieldKey: 'Key',
    fieldKeyHint: 'Stable name used for export. Lowercase, no spaces.',
    fieldType: 'Type',
    label: 'Label',
    help: 'Help text',
    required: 'Required',
    preview: 'What visitors see',
  },
  /*
   * Le bridage volontaire du studio.
   *
   * Le vocabulaire évite « incarner » et « se connecter en tant que » : il ne
   * s'agit pas d'usurper une identité mais de RENONCER à des capacités, et le
   * journal continue de porter le nom du studio. « Voir comme » dit ce que
   * c'est — un point de vue, pas un compte.
   */
  viewAs: {
    label: 'View as',
    full: 'Studio (everything)',
    hint: 'Drop your capabilities down to a position, to see what it sees.',
    active: 'Viewing as {role}',
    readOnly: 'Read-only while you do.',
    exit: 'Back to full access',
    refused: 'Not while you are viewing as another position. Leave that mode first.',
  },
  scan: {
    title: 'Scan',
    /* Identique au titre ici, et déclaré quand même : la barre du haut et la
       navigation sont deux endroits, et le jour où l'un des deux change on ne
       veut pas devoir démêler lequel. */
    action: 'Scan',
    hint: 'Scan a ticket, or type its code and press Enter.',
    placeholder: 'Ticket code',
    valid: 'Let them in',
    onboardingMissing: 'Let them in — send to a tablet',
    scanToFill: 'Scan this with your phone',
    scanToFillHint: 'Opens their registration, already matched to this booking — nothing to type.',
    onboardingMissingHint:
      'Registration is not finished — that can be the form, the NDA or the waiver, depending on what this experience asks for. Point them at a tablet or the QR code on the wall.',
    alreadyIn: 'Already checked in',
    alreadyInAgo: 'Scanned {n} min ago',
    alreadyInJust: 'Scanned a moment ago',
    wrongDay: 'Not today’s ticket',
    wrongDayHint: 'This ticket is for {date}. Find the right booking, or let them in anyway.',
    wrongTime: 'Wrong time slot',
    wrongTimeHint: 'This ticket departs at {time}. Ask them to come back, or let them in anyway.',
    letInAnyway: 'Let them in anyway',
    overridden: 'Let in by override',
    void: 'Ticket cancelled',
    notFound: 'Unknown code',
    readAs: 'Read as ',
    registrations: 'Registered',
    pushed: '{n} sent to the experience',
    departure: 'Departure',
    scanAnother: 'Next',
  },
  desk: {
    title: 'Field mode',
    enter: 'Field mode',
    enterHint: 'Full screen, camera on, one hand free. Scan, the day, the form, the screens — and nothing else.',
    scan: 'Scan',
    form: 'Registration form',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    screens: 'Screens',
    leave: 'Exit',
    leaveTitle: 'Leave field mode?',
    leaveBody: 'You go back to the full back office. Nothing is lost — you can come back in one tap.',
    enterTitle: 'Switch to field mode?',
    enterBody: 'Full screen: the camera, the day, the form and the screens, in finger-sized targets. The rest of the back office is hidden until you leave.',
    noScreensHint: 'Declare the venue’s televisions in Fleet → Devices, then drive them from here.',
    screenSoundHint: 'A television only plays sound after someone has touched it once. Tap the screen after switching it on.',
    today: 'Today',
    startCamera: 'Use the camera',
    cameraInsecure:
      'The camera needs a secure page. This one is served over plain HTTP, and no browser opens a camera there — on any phone.',
    cameraUnsupported: 'This browser cannot read QR codes. Type the code below, or use a hardware scanner.',
    cameraDenied: 'Camera access refused. Allow it in your browser settings, or type the code below.',
    aim: 'Point at the ticket',
    scanNext: 'Next ticket',
    household: 'Who is with them',
    minor: 'Minor',
    adult: 'Adult',
    seatOne: 'seat',
    seatMany: 'seats',
    visits: 'visits',
    signed: 'signed',
    showWaiver: 'Show this to them',
    showWaiverHint: 'They scan it with their own phone and sign on the spot. No need to send them to a tablet.',
    findByName: 'Find by name',
    arrived: 'arrived',
    arrivedOne: 'arrived',
    expected: 'expected',
    walkInHint: 'Someone without a booking is sold to at the counter, on the front-desk computer — not from here.',
    /* « À qui ce casque ? » — l'onglet Casque (tag NFC, QR ou numéro). */
    headset: 'Headset',
    headsetNfc: 'Scan a headset tag (NFC)',
    headsetNfcListening: 'Hold the phone against the headset tag…',
    headsetNfcUnavailable: 'NFC is only read by Chrome on Android, from the cloud app. Use the headset QR or its number.',
    headsetNfcDenied: 'NFC refused or switched off. Turn it on in the phone settings, or use the QR.',
    headsetQr: 'Scan the headset QR',
    headsetAim: 'Point at the headset QR',
    headsetNumber: 'Headset number or name',
    headsetFind: 'Find',
    headsetUnknown: 'Unknown headset in this venue.',
    headsetFor: 'Give this headset to',
    headsetFree: 'Free headset',
    headsetSilent: 'silent',
    headsetEngineOk: 'Linked in {engine} too.',
    headsetEngineFree: 'Nobody on it in {engine}.',
    headsetEngineOther: '{engine} has {name} on this headset, not this person.',
    headsetEngineStale: '{engine} still has {name} on it — the next link replaces them.',
    headsetRelink: 'Redo the link in {engine}',
    headsetRelinked: 'Link redone.',
    headsetEngineNobody: 'nobody',
    headsetGiveTo: 'Give it to…',
    headsetNobodyWaiting: 'Nobody registered is waiting for a headset.',
    headsetGiven: 'Headset given to {name}.',
    headsetNext: 'Scan another headset',
  },
  /*
   * Le PARC du studio : ce qu'on possède, et où c'est en ce moment.
   */
  fleet: {
    dupTitle: 'Same address {ip}: probably one headset under two records',
    dupSeen: 'seen {when}',
    dupNever: 'never seen',
    dupMerge: 'Merge',
    dupConfirmTitle: 'Merge these two records?',
    dupConfirmBody: '“{keep}” is kept, it is the one reporting. It takes from “{drop}” what it lacks (name, serial, room, studio claim), then “{drop}” goes away. Its visits, commands and history follow. Only do it if it really is the same headset: an address may have been handed to another one.',
    dupMerged: 'Records merged.',
    dupTitleLabel: 'Two records named “{name}”: probably one headset',
    rowMenu: 'Actions',
    mergeWith: 'Merge with…',
    mergeSelected: 'Merge the 2…',
    mergeTitle: 'Merge two records',
    mergeHint: 'The same headset under two identities. The kept record takes the other’s serial, name, inventory group and XROAM number; visits, incidents and installs of both are gathered on it.',
    mergeKeep: 'Keep',
    mergeKeepHint: 'Keep the one that reports: the headset aims at it.',
    mergeOther: 'Other record',
    mergePick: 'Pick a headset',
    mergeDo: 'Merge',
    mergeReports: 'reporting',
    mergeNever: 'not reporting',
    unplace: 'Take out of the room',
    unplaced: '{name} is out of the room.',
    remove: 'Delete the record…',
    removeTitle: 'Delete {name}?',
    removeBody: 'The record leaves the fleet, here and in the cloud. Only a record that does not report, is not in the inventory and has no history can be deleted; a duplicate is merged instead.',
    removeDo: 'Delete',
    removed: 'Record deleted.',
    removeErrors: {
      online: 'This headset is reporting: its record would come back at the next heartbeat.',
      in_inventory: 'This headset is in the inventory: take it out of the room instead.',
      has_history: 'This record has a history (visits, incidents): merge it with the headset’s other record instead.',
    } as Record<string, string>,
    openGroup: 'Open',
    sameAs: 'Same machine as',
    sameAsPick: 'Pick a record…',
    groupDevicesCount: '{n} headset(s) in this group',
    groupNote: 'Note',
    groupParent: 'Inside',
    groupMembers: 'Its headsets ({n})',
    groupEmpty: 'No headset in this group yet.',
    removeFromGroupN: 'Remove {n} from the group',
    showInDevices: 'Show in Devices',
    groupAdd: 'Add headsets',
    searchDevices: 'Name or serial',
    addToGroupN: 'Add {n} to the group',
    subgroups: 'Subgroups',
    deleteGroup: 'Delete the group',
    deleteGroupTitle: 'Delete “{name}”?',
    groupCycle: 'A group cannot go inside itself or one of its subgroups.',
    seenTitle: '{n} devices seen in your venues, not attached yet',
    seenHint: 'They reported from one of the studio’s venues without having been enrolled, so they belong to nobody and do not show in the fleet. Attach the ones the studio owns; leave an operator’s own headsets aside.',
    seenVenue: 'Seen at',
    tabDevices: 'Devices',
    tabGroups: 'Groups',
    tabPresets: 'Headset presets',
    rename: 'Rename',
    deleteGroupHint: 'Deletes the group, not its headsets: a group is a label.',
    removeFromGroup: 'Remove from this group',
    inventory: 'Studio inventory',
    groupsTitle: 'The studio’s groups',
    groupName: 'Group',
    groupCount: 'Devices',
    claimPicked: 'Attach {n} to the studio',
    claimAll: 'Attach all',
    claimed: '{n} devices attached to the studio.',
    ignorePicked: 'Ignore {n}',
    ignored: '{n} device(s) ignored: they are no longer offered.',
    showIgnored: 'Show ignored ({n})',
    hideIgnored: 'Hide the ignored',
    ignoredTitle: 'Ignored',
    unignore: 'Stop ignoring',
    unignored: 'Offered again.',
    kindOne: { headset: 'Headset', server: 'Server', tracker: 'Tracker', router: 'Router', other: 'Other' } as Record<string, string>,
    renameDevice: 'Rename',
    renamedSync: 'Renamed. The room picks up the new name at its next sync.',
    title: 'Fleet',
    wholeFleet: 'Whole fleet',
    device: 'Device',
    serial: 'Serial number',
    whereIsIt: 'Where it is',
    lastSeen: 'Last heard from',
    neverSeen: 'Never',
    atStudio: 'At the studio',
    none: 'No devices enrolled yet.',
    noneHint:
      'Enrol them from the manufacturer’s delivery note — one serial number per line. They can then be moved to a venue as a batch.',
    enrol: 'Enrol devices',
    enrolAction: 'Enrol',
    kind: 'Kind',
    headset: 'Headsets',
    server: 'Servers',
    tracker: 'Trackers',
    router: 'Routers',
    other: 'Other',
    paste: 'Serial numbers',
    pasteHint:
      'One per line: serial number, then the label written on the case, and optionally the headset’s address on the venue network. Tabs, semicolons and commas all work — paste straight from the spreadsheet. The address is recognised by its shape, so column order does not matter.',
    willEnrol: '{n} devices ready',
    selected: '{n} selected',
    moveTo: 'Move to…',
    bringHome: 'Bring back to the studio',
    bringHomeTo: 'Bring back to the studio ({venue})',
    addToGroup: 'Add to group…',
    newGroup: 'New group',
    newGroupHint:
      'A group is a label, not a container: a headset can be in “Casper fleet” and in “Massive test #1” at the same time. Moving a group moves its sub-groups too.',
    groupPlaceholder: 'Massive test #1',
    topLevel: 'Top level',
    under: 'Inside {name}',
    importTitle: 'Import a fleet sheet',
    importHint:
      'The other half of the network scan: the scan finds what answers, the import records what you know. Matching is done on the MAC address — an IP changes with the DHCP lease, a MAC designates the object.',
    importFile: 'CSV file',
    importPaste: 'Or paste the rows',
    importIgnore: '— ignore —',
    importFields: {
      hardwareId: 'Serial number',
      macAddress: 'MAC address',
      ipAddress: 'IP address',
      label: 'Name',
    },
    importCount: '{n} rows read',
    importBadMac: '{n} rows carry something in the MAC column that is not an address. They will be refused, not guessed — check the column before importing.',
    importGo: 'Import',
    importing: 'Importing…',
    importDone: '{c} created · {u} updated · {s} refused',
    importWhy: {
      bad_mac: 'unreadable MAC address',
      no_match: 'no serial number, and no headset with this MAC',
      mac_taken: 'this MAC belongs to another headset',
      empty: 'neither serial number nor MAC',
    },
    createGroup: 'Create',
  },
  remote: {
    title: 'Remote config',
    lede: 'What the experience plays in every room, decided by the studio. Everything here is a draft: nothing reaches a room before it is published.',
    steps: 'Steps',
    stepKeys: 'Declare',
    stepValues: 'Default values',
    stepVariants: 'Variants and age settings',
    stepPublish: 'Publish and history',
    useInUnity: 'Use these variables in Unity',
    useInUnityHint: 'A studio token lets the Unity editor read them and generate its C# class.',
    pickTitle: 'Pick an experience',
    noTitles: 'No experience declares variables yet.',
    instances: 'Venues playing it (their own settings)',
    noInstances: 'No venue plays this experience yet.',
    keys: 'Variables',
    template: 'Default values',
    environments: 'Variants',
    audiences: 'Age settings',
    publications: 'History',
    venueValues: 'This venue',
    perTicket: 'Per ticket type',
    activeEnvironment: 'Active variant',
    tests: 'A/B tests',
    devices: 'Devices',
    back: 'Back to the experience',
    inherit: 'Inherit',
    inherited: 'Inherited from {source}',
    fromSchema: 'schema default',
    fromTemplate: 'default values',
    fromVenue: 'venue',
    fromEnvironment: 'variant',
    save: 'Save draft',
    saved: 'Draft saved — publish to send it to the rooms.',
    publish: 'Publish',
    publishNote: 'What changes, in a line (optional)',
    publishedAs: 'Published as n° {n}.',
    republish: 'Go back to this one',
    noPublication: 'Nothing published yet: rooms play the schema defaults.',
    publication: 'Publication {n}',
    schemaVersion: 'schema {n}',
    live: 'In service',
    warnings: 'Left out when the package was built',
    key: 'Key',
    labelEn: 'Label (English)',
    labelFr: 'Label (French)',
    type: 'Type',
    typeNumber: 'Number',
    typeBoolean: 'Yes / no',
    typeEnum: 'Choice',
    typeText: 'Text',
    typeList: 'List of objects',
    open: 'Open',
    search: 'Search a variable',
    onlySet: 'Changed only',
    counts: '{n} variables · {set} changed',
    sectionCount: '{n} variable(s)',
    listCount: '{n} item(s)',
    listFields: 'Fields of an item',
    listFieldsHint: 'Each item of the list has these fields — one column each in the values. A field left empty keeps the experience’s own default.',
    addField: 'Add a field',
    addRow: 'Add an item',
    removeRow: 'Remove',
    yes: 'Yes',
    no: 'No',
    defaultValue: 'Default',
    min: 'Min',
    max: 'Max',
    step: 'Step',
    unit: 'Unit',
    options: 'Choices (value = label, one per line)',
    scope: 'Scope',
    scopeRoom: 'Room — the shared scene',
    scopeHeadset: 'Headset — this visitor only',
    scopeHint: 'A room key cannot differ inside a group: it is tested by session and never follows age. A headset key can differ from one visitor to the next.',
    studioOnly: 'Studio only',
    addKey: 'Add a variable',
    removeKey: 'Remove',
    publishSchema: 'Publish the variables (version {n})',
    schemaHint: 'The Unity project reads these variables through a generated class. Renaming one makes a new one: settings made on the old one are left out and flagged.',
    none: 'None',
    newEnvironment: 'New variant',
    environmentName: 'Name',
    deleteEnvironment: 'Delete',
    noEnvironments: 'No variant yet. A variant is a named set of changes to the default values: “Winter”, “Short briefing”.',
    environmentHint: 'Variants belong to the experience, shared by every venue: that is what makes a test comparable from one venue to another.',
    newAudience: 'New age setting',
    minAge: 'From age',
    maxAge: 'Up to age',
    minorOnly: 'Minors only (waiver box)',
    audienceHint: 'An age setting is a care rule, not a test: the same values for everyone it matches, read from the waiver’s age on the day of the session. Headset variables only.',
    noAudiences: 'No age setting yet.',
    newTest: 'New test',
    testName: 'Name',
    arms: 'Arms',
    control: 'Control (no variant)',
    weight: 'Share %',
    addArm: 'Add an arm',
    eligibility: 'Who takes part (headset tests only)',
    byRoom: 'Drawn by session',
    byHeadset: 'Drawn by visitor',
    testHint: 'One test at a time. The arms copy the variants as published. A test touching a room variable is drawn by session, with no condition on people.',
    start: 'Start the test',
    stop: 'Stop',
    stopWith: 'Stop — {arm} wins',
    winnerHint: 'The winner becomes the venue’s active variant in its draft. Publish to play it everywhere.',
    running: 'Running',
    ended: 'Ended',
    noTests: 'No test yet.',
    holds: 'holds publication {n}',
    holdsStale: 'still on publication {n}',
    holdsNothing: 'reports nothing yet',
    originDefault: 'compiled defaults',
    originCached: 'last package received',
    originRemote: 'received from the cloud',
    ticketType: 'Ticket type',
    genders: { female: 'Women', male: 'Men', nonbinary: 'Non-binary', undisclosed: 'Undisclosed' },
    tokens: 'Tokens for Unity',
    tokensHint: 'The Unity editor reads an experience’s variables with one of these to generate its C# class. Put it in the PORTAGEROOM_STUDIO_TOKEN environment variable of the workstation — never in the project, never in a build. It reads variables and nothing else.',
    tokenLabel: 'What it is for (e.g. Atlantis workstation)',
    newToken: 'Issue a token',
    tokenOnce: 'Copy it now: it will never be shown again.',
    revoke: 'Revoke',
    revoked: 'Revoked',
    lastUsed: 'last used {when}',
    neverUsed: 'never used',
    noTokens: 'No token yet.',
    errors: {
      out_of_range: 'A value is outside the key’s bounds.',
      unknown_param: 'This key is not in the schema.',
      weights_must_sum_to_100: 'The shares must add up to 100.',
      test_already_running: 'A test is already running for this experience.',
      environment_not_published: 'Publish the template first: this environment is not in service.',
      room_test_is_by_session: 'A test on a room key is drawn by session: it cannot have conditions on people.',
      audience_room_key: 'An audience only sets headset keys.',
      environment_name_taken: 'This name is already used in the library.',
      audience_name_taken: 'This name is already used.',
      environment_needs_ticket_type: 'An environment is set in the template; here, only per ticket type.',
      one_control_only: 'One control arm only.',
      duplicate_arm: 'The same environment twice in one test.',
    } as Record<string, string>,
  },
  lbe: {
    column: 'LBE map',
    asked: 'LBE map check requested on {n} headset{s}: the answer arrives with their next report.',
    code: 'PICO code {code}',
    expected: 'Expected LBE map',
    expectedHint: 'The .zip that PICO Business Manager deployed, by file name. Written to the headsets on install and “Reconfigure”; each headset then checks it is the map in effect. Empty: Portage expects nothing.',
    pick: 'Pick a map seen on the headsets',
    none: 'No map expected',
    state: {
      in_effect: 'Expected map in effect',
      not_in_effect: 'Another map is in effect',
      missing: 'Expected map not on the headset',
      unknown: 'Could not check',
      not_configured: 'No map expected on this headset',
      unread: 'Not reported (SDK before 0.17.31, or app closed)',
    },
  },
  awake: {
    tab: 'LBE map & sleep',
    keep: 'Keep headsets awake',
    on: 'During preparation and the experience',
    keepHint: 'PICO enterprise headsets only (SDK 0.17.32). Unplugged and before the end of the visit, the headset never goes to sleep: it keeps its app and its LBE map. It gets its own sleep delay back when plugged in, when the visit ends, or when the app closes. Written to the headsets on install and “Reconfigure”.',
    column: 'Sleep',
    held: 'Kept awake',
    normal: 'Normal sleep',
    normalHint: 'Plugged in, or visit over: the headset has its own delay back.',
    unread: 'Not reported',
  },
  zl: {
    title: 'Zone layout',
    hint: 'ZoneLayout.json, exported from Unity: where each scene sits and faces, inactive corridors, moved crossings. Each upload is a new version; the latest is offered in Portage Room, where the operator pushes it with “Update”.',
    noExperience: 'No experience runs in this room yet.',
    none: 'No layout yet: the app keeps its built-in placement.',
    upload: 'Upload ZoneLayout.json',
    uploadNew: 'New version',
    uploading: 'Uploading…',
    published: 'Version {v} published',
    withWarnings: 'check the scenes below',
    version: 'Version {v} · exported {saved} · {scenes} scenes',
    by: 'by {who}, {when}',
    olderOne: 'earlier version',
    olderMany: 'earlier versions',
    unchecked: 'Scenes not checked: this experience has no imported scene timing.',
    unknownScenes: 'Unknown to the experience:',
    missingScenes: 'Not placed (keep the build’s placement):',
    column: 'Layout',
    pushOption: 'Zone layout',
    pushVersion: '{name} · version {v}, exported {saved}',
    pushHint: 'Placed in files/ZoneLayout.json, then the app restarts. Ticked when a selected headset is not up to date.',
    state: {
      ok: 'Up to date (v{v})',
      outdated: 'Earlier version (v{v}, expected v{expected})',
      modified: 'Modified: matches no published version',
      missing: 'Missing on the headset',
      none_expected: 'No layout for this room',
    },
    errors: {
      invalid_json: 'This file is not valid JSON.',
      not_a_zone_layout: 'This is not a ZoneLayout.json (no _layout._sceneTransformDataList).',
      too_large: 'The file is too large.',
      experience_not_in_room: 'This experience does not run in this room.',
      room_not_found: 'Room not found.',
    },
  },
  broom: {
    heading: 'Rooms',
    add: 'Room',
    namePlaceholder: 'Room name',
    nameTaken: 'A room in this venue already has that name.',
    noExperience: 'No experience yet',
    noServer: 'no server',
    keyWord: 'key',
    keysWord: 'keys',
    toPlace: 'To place',
    toPlaceLine: '{n} to place in a room',
    late: 'Behind',
    toPlaceHint: 'Keys or servers belong to no room.',
    noRoom: 'no room',
    name: 'Name',
    experiences: 'What runs here',
    experiencesHint: 'The same experience can run in several rooms.',
    venueHasNone: 'This venue has no experience yet: drop one from the catalogue first.',
    server: 'Server',
    serverHint: 'The server places itself here on its first heartbeat with one of this room’s keys.',
    noServerYet: 'No server has reported yet.',
    online: 'online',
    silent: 'silent',
    keys: 'Ingest keys',
    keysHint: 'What lets this room’s experience server write to Portage. One per server machine: the build and the room instance read it from the same portageroom.json. Restricted to the experience running on that machine, it bounds what a leak allows. Never in a headset.',
    noKeys: 'No key for this room.',
    keyFor: 'For',
    allExperiences: 'Every experience',
    newKey: 'New key',
    keyOnce: 'Copy it now: it will never be shown again. It goes into the portageroom.json file on the server’s machine — C:\\ProgramData\\Portage\\portageroom.json, {"ingestKey": "…"} — which the room instance reads too.',
    lastUsed: 'Last used {when}',
    neverUsed: 'Never used',
    revoke: 'Revoke',
    revokeTitle: 'Revoke “{label}”?',
    revokeBody: 'The server holding it stops writing to Portage on its next send. This cannot be undone: it will need a new key.',
    unplacedTitle: 'Not in a room',
    placeIn: 'Move to…',
    copyId: 'Copy ID',
    dropped: '{n} events dropped',
    droppedVisits: ', including {n} visit starts or ends',
    droppedHint: 'Its outbox was full after a long time offline: some details are missing from Analytics.',
    instance: 'Room instance {commit}',
    instanceBehind: 'behind the cloud',
    instanceUntracked: 'version before tracking',
    venueBehind: 'Room behind',
    venueBehindHint: 'A room instance of this venue runs an older Portage than the cloud. It still plays; update it when convenient.',
    unplacedServersHint: 'A server without a room is placed from the venue’s Rooms page, or by its next heartbeat with a room key.',
  },
  admin: {
    title: 'Portage console',
    venues: 'Venues',
    titles: 'Experiences',
    instances: 'Instances',
    newVenue: 'New venue',
    nominalHint:
      'Nominal values, belonging to the studio’s experience. A venue that adds it inherits them; if it changes them afterwards, it changes only its own.',
    headsetsHint: 'Simultaneous headsets the experience needs. Used to size a fleet.',
    titleActive: 'Available for licensing',
    editTitle: 'Edit experience',
    newTitle: 'New experience',
    slug: 'Slug',
    name: 'Name',
    timezone: 'Time zone',
    currency: 'Currency',
    owner: 'Owner',
    ownerHint: 'A venue is created with the person who runs it. Without one, nobody can get in.',
    members: 'People',
    upcoming: 'Upcoming sessions',
    duration: 'Duration',
    capacity: 'Nominal capacity',
    headsets: 'Headsets required',
    minAge: 'Minimum age',
    guides: 'Operators required',
    bounds: 'Remote variables',
    boundsHint: 'Declared once by the studio. Every venue playing this experience inherits them, and the Unity build never receives a value outside them.',
    noBounds: 'No bounds published yet. The build has nothing to obey.',
    boundsVersion: 'Version',
    playedIn: 'Played in',
    playedInNone: 'No venue plays it yet. Drag it onto a venue in Distribution.',
    toDistribution: 'Manage in Distribution',
    local: 'The venue’s own',
    unlink: 'Unlink',
    attach: 'Link…',
    attachTo: 'Link “{name}” to a studio experience',
    attachHint: 'Created by the venue itself. Once linked, it inherits the studio experience’s bounds and remote control; its sessions and sales stay.',
    noEntity: 'No entity',
    venueSettings: 'Venue settings',
    venueActive: 'Venue active',
    sandbox: 'Sandbox',
    perfReporting: 'Measure headset performance',
    sceneOverrun: 'Scene running long alert',
    storageLow: 'Low disk alert',
    storageLowHint: 'Below this free space, a headset is flagged orange on its board card and in the fleet. Measured at the start and end of each visit (SDK 0.17.24). 0 = never.',
    sceneOverrunHint: 'When someone spends longer in their scene than its planned duration (the experience’s Reed timing) plus this percentage, their card blinks red on the room board until they change scene. 0 = never.',
    perfReportingHint: 'Frame rate, memory, heat and battery per scene, in Analytics. Off: the venue’s headsets measure and send nothing. Reaches the headsets with the next configuration package.',
    sandboxHint: 'For tests only: the one venue where Claude sessions register test visitors (MCP import_visitors). Never a real venue.',
    venueActiveHint: 'When inactive, its public ticketing stops answering, it leaves the venue picker and its members can no longer open it.',
    offer: 'Offer',
    offerHint: 'The modules this operator pays for, à la carte. A module turned off is hidden everywhere — menu, API, MCP, background jobs — and its data stays.',
    moduleCore: 'Operations',
    moduleCoreHint: 'Always included: rooms and headset fleet, live board, map, screens, registration, player analytics, field mode.',
    moduleBilletterie: 'Ticketing',
    moduleBilletterieHint: 'Calendar, today, bookings, counter sales, scan, prices and scheduling, resources, public ticketing, partner tickets.',
    moduleMarketing: 'Marketing',
    moduleMarketingHint: 'Customers, campaigns, journeys, websites, sales and audience analytics.',
    moduleEquipe: 'Team & roster',
    moduleEquipeHint: 'Roster, availability, my roster, guide assignment.',
    operatorActive: 'Active operator',
    operatorActiveHint: 'When inactive, none of its venues opens to its members any more, and its public pages stop answering. The studio still gets in.',
    noTitles: 'No experience in the catalogue yet.',
    slugTaken: 'That slug is already taken.',
    superadminOnly: 'This is the studio console. Only Rabaska staff reach it.',
    sheetTab: 'Overview',
    apkTab: 'APK',
    variablesTab: 'Variables',
    visitorTab: 'Visitor',
    timingsTab: 'Timing',
    pagesTab: 'Pages',
    openVariables: 'Open the variables',
    board: 'Distribution',
    boardHint: 'Drag an experience onto a venue to make it available there. It arrives unpublished, on the studio’s nominal values.',
    studioCatalogue: 'Studio experiences',
    newOperator: 'New operator',
    newVenueHere: 'New venue',
    legalName: 'Legal name',
    legalNameHint: 'Optional. Appears on invoices, not in the product.',
    create: 'Create',
    addTitle: 'Add an experience',
    allTitlesProvisioned: 'Every studio experience is already here.',
    alreadyProvisioned: 'That experience is already at this venue.',
    noTitlesHint: 'Create one under Experiences: it is the studio’s experience, not a venue’s copy of it.',
    noVenuesYet: 'No venue yet.',
    noVenuesHint: 'It is born with its owner, so someone can actually walk in.',
    noBoundsShort: 'No bounds',
    removeHint: 'Remove: nothing hangs off this copy yet.',
    unlinkHint: 'Unlink from the studio experience. The copy stays, and keeps what it sold.',
    venueWord: 'venue',
    venuesWord: 'venues',
    contactWord: 'contact',
    contactsWord: 'contacts',
    memberWord: 'member',
    membersWord: 'members',
    slotWord: 'slot',
    slotsWord: 'slots',
    bookingWord: 'booking',
    bookingsWord: 'bookings',
    headsetsWord: 'headsets',
  },
  team: {
    title: 'Team',
    /* L'onglet des POSTES, à côté du planning. « Équipe » nomme l'écran
       entier : le réutiliser pour un de ses deux onglets ferait croire que
       l'autre n'en est pas. */
    roles: 'Positions',
    invite: 'Invite someone',
    byEmail: 'By email',
    existingAccount: 'Existing account',
    searchPlaceholder: 'Name or email',
    searchAll: 'Every Portage account (studio): type at least two characters.',
    searchOperator: 'People from your other venues, where you manage the team.',
    noCandidates: 'No account to add.',
    alsoIn: 'also in {venues}',
    addExisting: 'Add to this venue',
    outOfScope: 'This account belongs to another organisation: ask the studio to add it to this venue.',
    inviteHint:
      'The account is created right away. No email is sent yet — you will get a link to hand over, and they choose their own password.',
    name: 'Name',
    email: 'Email',
    jobTitle: 'Job title',
    jobTitleHint:
      'What goes on a business card. Purely descriptive — permissions are the position below.',
    jobTitlePlaceholder: 'Head of Communications',
    preset: 'Position',
    presetHint: 'A position grants a set of permissions. Adjust them below for this person only.',
    custom: 'Adjusted',
    followsPreset: 'Follows the position',
    resetToPreset: 'Back to the position',
    permissions: 'Permissions',
    lastSeen: 'Last sign-in',
    studioAccount: 'Studio Rabaska account: it already has full access to every venue, in the cloud and in the rooms, without being on the team. Its role here changes nothing — you can remove it.',
    never: 'Never',
    remove: 'Remove from this venue',
    removeHint: 'The account stays, along with everything they checked in. Only their access to this venue ends.',
    /*
     * Un LIEN, pas un mot de passe. Le libellé dit ce qu'on tient et ce qu'il
     * vaut : 48 heures, un usage. Sans la durée, le lien finit dans un fil de
     * discussion « pour plus tard ».
     */
    resetLink: 'Link to open the account',
    resetLinkHint:
      'Valid 48 hours, single use. Hand it over directly — the person chooses their own password. Nothing is shown again.',
    resetLinkCopy: 'Copy the link',
    cannotDemoteSelf: 'You cannot take away your own team management.',
    lastTeamManager: 'Someone has to be able to manage the team. Give it to another person first.',
    cannotRemoveSelf: 'You cannot remove yourself.',
    noMembers: 'Nobody else has access yet.',
    groups: {
      day: 'The day',
      tickets: 'Tickets',
      money: 'Money',
      customers: 'Customers and marketing',
      catalogue: 'Experiences',
      ops: 'Live ops',
      onboarding: 'Onboarding',
      admin: 'Administration',
    },
  },
  settings: {
    scanBefore: 'Scan opens before the start',
    scanBeforeHint: 'How long before its session a ticket is accepted at the entrance.',
    scanAfter: 'Scan closes after the start',
    scanAfterHint: 'How long after the start a late ticket is still accepted.',
    scanRescan: 'Second scan tolerated for',
    scanRescanHint: 'A ticket scanned twice within this delay is a double tap, not a second entry.',
    contents: 'Contents',
    title: 'Settings',
    venue: 'Venue',
    team: 'Team',
    role: 'Role',
    emailOutbox: 'Sent emails',
    openMailpit: 'Open the local inbox',
    mailpitHint: 'In development every email lands in Mailpit instead of a real inbox.',
    provider: 'Provider',
    noWrite: 'Read-only for now — editing venue details comes with the catalogue screens.',
    language: 'Language',
    slug: 'Slug',
    timezone: 'Time zone',
    currency: 'Currency',
    name: 'Display name',
    phone: 'Phone',
    city: 'City',
    region: 'Region',
    postalCode: 'Postal code',
    tagline: 'Tagline',
    studioOwned: 'The studio keeps this',
    legalHint: 'Yours, as the operator. The studio’s and the partner’s are declared elsewhere and appear on their own line.',
    filmingTitle: 'Filming in progress',
    filmingHint: 'Turn on while you film for promotion (tests, shows). Registration then offers an optional checkbox, separate from the waiver, to authorise the use of one’s image; on the board, the cards of people who did not give it are marked “No image” — keep them out of shot, or blur them.',
    studioOwnedHint: 'The slug is in public booking URLs and in emails already sent; the time zone and currency would relabel every session and every amount already taken. Ask Rabaska to change them.',
    contact: 'Contact',
    address: 'Address',
    brand: 'Brand',
    selling: 'Selling rules',
    legalName: 'Legal name',
    website: 'Website',
    accentColor: 'Accent colour',
    ticketRequired: 'Ticket required at the door',
    autoProbe: '{names}: in probe, its headsets announce nothing — its cards always move by hand.',
    autoSilent: '{names}: its headsets have never announced a start or an end. In automatic mode, its cards won’t move until its APK tracks visits (Portage Room SDK: room.TrackVisits).',
    startMode: 'Card advancement',
    startModeHint: 'Who moves a card when the experience starts and ends. In automatic mode the room reports it headset by headset and the board follows — what you want when one person handles forty-five headsets. Manual keeps every move a deliberate gesture.',
    silentClose: 'Auto-end a silent card',
    forgotten: 'Remove a forgotten card',
    forgottenHint: 'A card paired but never started for this long is removed by the room (visitor left without playing, test card left behind). It does not enter the visit history. 0 = never.',
    silentCloseHint: 'An in-room card whose headset has been asleep or unreachable without interruption for this long is ended by the room (app crashed, headset set down). The card warns at half-time. 0 = never.',
    tempUnit: 'Temperature unit',
    tempUnitHint:
      'Display only. Headsets always report Celsius, and so do their own throttling and shutdown thresholds.',
    startModeOperator: 'By hand',
    pairMode: 'Headset pairing',
    pairModeHint:
      'Automatic: the room gives each registered person the best free headset (charged, not worn, in the right room), for experiences that pair during registration. The team can still change it from the card. By hand: the team picks every headset.',
    startModeAuto: 'Automatic',
    ticketRequiredHint:
      'On means a ticket opens the visit — the usual case. Off means the tablet is the door: people are registered on site, without a booking. Sessions, capacity and selling are unaffected.',
    onlineCutoff: 'Online sales close',
    onlineCutoffHint: 'Minutes before departure.',
    cartTtl: 'Cart lifetime',
    cartTtlHint: 'Minutes before held seats are released.',
    cancellationWindow: 'Cancellation window',
    cancellationWindowHint: 'Hours before departure a visitor can still cancel.',
    reminderLead: 'Reminder sent',
    reminderLeadHint: 'Hours before departure.',
    minutes: 'min',
    hours: 'h',
    saved: 'Saved.',
    capabilities: 'Can do',
    you: 'you',
    /*
     * Les noms de POSTE, et non les clés de preset.
     *
     * La base stocke `desk`, `scan`, `guide`. Les afficher tels quels
     * demanderait à un opérateur d'apprendre notre vocabulaire interne pour
     * lire son propre écran d'équipe. Ils vivent ici, dans les deux langues,
     * comme tout le reste — un écran à moitié traduit est précisément le
     * défaut que le typage de `fr` contre `en` existe pour empêcher.
     */
    roles: {
      owner: 'Owner',
      manager: 'Management',
      marketing: 'Marketing',
      finance: 'Finance',
      guide: 'Operator',
      desk: 'Front desk',
      scan: 'Scan station',
      staff: 'Staff',
    },
  },
  sites: {
    title: 'Websites',
    noneForExperience: 'No page for this experience yet.',
    fromCatalogueHint: 'A page pulls its duration, age, entry price and address from this catalogue entry — nothing to retype.',
    createElsewhere: 'Pages are created from Websites, where the subdomain and the DNS record live.',
    newSite: 'New site',
    subdomain: 'Subdomain',
    hostname: 'Address',
    status: 'Status',
    draft: 'Draft',
    comingSoon: 'Coming soon',
    open: 'Open',
    ended: 'Ended',
    dns: 'DNS',
    dnsNone: 'Not created',
    dnsPending: 'Propagating',
    dnsActive: 'Live',
    dnsFailed: 'Failed',
    createDns: 'Create the DNS record',
    platformOnly: 'Only a Rabaska admin can create a subdomain — the domain belongs to the studio.',
    sections: 'Sections',
    theme: 'Colours',
    seo: 'Search engines',
    accent: 'Accent',
    background: 'Background',
    textColor: 'Text',
    enable: 'Shown',
    leads: 'Email signups',
    noSites: 'No site yet. Create one to give this experience its own page.',
    visit: 'Open the site',
    derived: 'Filled from the catalogue',
    sectionHero: 'Hero',
    sectionGallery: 'What’s inside',
    sectionPractical: 'Practical info',
    sectionBooking: 'Booking',
    sectionReviews: 'Reviews',
    sectionGroups: 'Groups',
    sectionFaq: 'FAQ',
    sectionLocation: 'Getting there',
    sectionText: 'Free text',
    sectionNewsletter: 'Email signup',
    resetSections: 'Restore the default template',
    subdomainHint: 'Printed on posters and sent in emails. Pick it once — changing it later breaks every link already out there.',
    hostnameTaken: 'That subdomain is already used by another page.',
  },
  cat: {
    edit: 'Edit',
    newExperience: 'New experience',
    sheet: 'Details',
    prices: 'Prices',
    schedule: 'Schedule',
    slug: 'Slug',
    name: 'Name',
    tagline: 'Tagline',
    description: 'Description',
    knowBefore: 'Know before you go',
    duration: 'Duration (min)',
    turnover: 'Turnover (min)',
    capacity: 'Capacity',
    minParticipants: 'Minimum to run',
    minAge: 'Minimum age',
    groupSize: 'Group size',
    preassign: 'Pair headsets during registration',
    preassignHint:
      'The tech room prepares headsets while visitors sign. Leave it off in a small venue that hands them out as people arrive.',
    autoStart: 'Starts on its own',
    autoStartHint:
      'The experience starts as soon as the group is complete. Operators no longer press “Started” on each card — someone still opens the session.',
    staffRequired: 'Operators needed',
    published: 'Published',
    heroImage: 'Hero image',
    newPrice: 'Add a price',
    priceName: 'Name',
    priceAmount: 'Price',
    weight: 'Seats used',
    audience: 'Audience',
    audienceAdult: 'Adult',
    audienceMinor: 'Child',
    audienceAny: 'Unspecified',
    publicPrice: 'Sold online',
    activePrice: 'Active',
    newSchedule: 'Add a schedule',
    from: 'From',
    to: 'To',
    weekdays: 'Days',
    firstDeparture: 'First departure',
    lastDeparture: 'Last departure',
    every: 'Every',
    minutes: 'min',
    previewSchedule: 'Preview',
    departures: 'departures',
    sessionsCreated: 'sessions created',
    tooTight: 'The interval is shorter than the experience — departures will overlap.',
    deleteSchedule: 'Delete',
    noPrices: 'No price yet. Add one before selling.',
    noSchedules: 'No schedule yet. Add one to create sessions.',
  },
  welcomeMirror: {
    hint: 'The venue’s tablets and TVs, as Portage Cloud sees them. They are declared, set up and revoked from the cloud.',
    notYet: 'The room has not received the list from the cloud yet: it arrives with the next sync.',
  },
  uploads: {
    sending: 'Sending {name} — {pct} %',
    keepOpen: 'You can switch screens; do not close this tab.',
  },
  screens: {
    title: 'Screens',
    newScreen: 'Add a screen',
    screenName: 'Where is it?',
    unknown: 'This screen is not recognised. Ask the front desk for a new address.',
    tapToEnable: 'Tap to start',
    stateOnline: 'Online',
    stateOffline: 'Offline',
    stateError: 'Error',
    lastPlayed: 'Last briefing',
    neverPlayed: 'Never played a briefing',
    stillLoading: 'Loading the briefing…',
    stuck: 'The briefing is not advancing.',
    stuckHint: 'Playback was allowed but nothing is moving — check the file and the screen’s audio output.',
    playFailed: 'This briefing will not play. Check the file, not the remote.',
    tapHint: 'This browser refused to start the sound. One touch allows it for the whole session — a properly set-up screen never asks.',
    qrCaption: 'Scan to register before you go in',
    remote: 'Screens',
    play: 'Play',
    pause: 'Pause',
    stop: 'Clear',
    pageKind: 'Page',
    showQr: 'Show the QR code',
    noScreens: 'No screen yet.',
    noKiosks: 'No tablet yet.',
    noKiosksHint: 'Declare one, then open its address on the tablet. It stays on that page all day.',
    noScreensHint: 'Declare one, then type its address into the TV browser. Nothing to install.',
    noMedia: 'No video for the experiences you play. The studio uploads them with the experience.',
    resume: 'Resume',
    ackSent: 'Sent — waiting for the screen…',
    ackTaken: 'The screen picked it up.',
    ackSilent: 'The screen is not answering. Is it switched on, and is it this venue’s?',
    briefing: 'Briefing',
    trailer: 'Trailer',
    lastSeen: 'Last seen',
    never: 'never',
    address: 'Address to type into the TV',
  },
  group: {
    title: 'My venues',
    allVenues: 'Every venue',
    enterHint: 'Pick a venue to work in it',
    revenue: 'Revenue',
    seats: 'Seats',
    contacts: 'Contacts',
    lastDays: 'Last {n} days',
    sharedContacts: '{n} known at more than one venue',
    noSharedYet: 'None known at more than one venue yet.',
    byVenue: 'Venue by venue',
    upcoming: 'Booked ahead',
    team: 'Team',
    sharedTeam: '{n} working at more than one venue',
    noTeam: 'No team member in the venues you can see.',
    targetVenues: 'Send to',
    targetHint: 'Only venues where you can send. A contact known at two of them still gets one email.',
    thisVenueOnly: 'This venue only',
  },
  empty: {
    experiencesHint: 'An experience is what you sell: its duration, its capacity, its prices. Create one and the calendar fills itself from its schedule.',
    campaignsHint: 'A campaign writes to a segment of your own contact list — and Portage Cloud tells you how many tickets it sold, not how many people opened it.',
    flowsHint: 'A flow is what visitors fill in before playing: a few questions, an NDA if the title is unreleased, and the waiver. One tablet, one QR code, or a link in their confirmation.',
    sitesHint: 'A site gives this experience its own page on its own address, with the booking funnel inside it — not a link out to somebody else’s marketplace.',
    filteredHint: 'Nothing matches this filter. Widen it, or clear the search.',
  },
  eov: {
    title: 'End of visit',
    hint: 'When a card moves to “Done”, its headset goes back to these settings. A headset that is offline at that moment gets them when it comes back, and at the latest before its next pairing.',
    noneHint: 'Pick a studio preset: every headset of this room will go back to it at the end of each visit.',
    noneRoom: 'No end-of-visit settings for this room. They are chosen from Portage Cloud, in Rooms.',
    noPresets: 'The studio has no preset yet. Create one in the Portage console → Studio inventory → Headset presets.',
    none: '— None —',
    fromCloud: 'Chosen from Portage Cloud.',
    on: 'on',
    off: 'off',
    errors: {
      preset_not_found: 'This preset no longer exists.',
      venue_without_studio: 'This venue is not attached to a studio: it has no presets.',
      cloud_only: 'End-of-visit settings are chosen from Portage Cloud.',
    } as Record<string, string>,
  },
  presets: {
    hint: 'A preset is a set of headset values — volume, brightness, kiosk… A venue picks one as its end-of-visit settings: every headset goes back to it when a card moves to “Done”. A preset only touches the settings it carries.',
    create: 'New preset',
    none: 'No preset yet.',
    name: 'Name',
    namePlaceholder: 'End of visit — standard',
    note: 'Note',
    values: 'Values',
    edit: 'Edit',
    untouched: 'not touched',
    save: 'Save the preset',
  },
  xcfg: {
    tab: 'Configuration',
    none: 'No remote configuration for this experience.',
    noneHint: 'The studio has not declared any setting for its title, or has not published anything yet.',
    hint: 'What this venue plays, from the studio’s publication #{n}. Only the studio changes it: ask it for a change.',
    editInConsole: 'Edit in the Portage console',
    key: 'Setting',
    value: 'Value played',
    origin: 'Origin',
    set: 'Set by the studio',
    default: 'Default',
    on: 'on',
    off: 'off',
  },
  rooms: {
    logs: {
      title: 'Experience server log',
      subtitleRoom: 'Read on this machine (Unity folder mounted into Portage Room). Keys, tokens and emails are masked.',
      subtitleCloud: 'Asked of the room: it answers at its next sync, within seconds when it is online. Keys, tokens and emails are masked.',
      file: 'Log',
      player: 'Unity, current launch',
      playerPrev: 'Unity, previous launch',
      sdk: 'Portage SDK',
      lines: 'Lines',
      lastLines: 'Last {n} lines',
      filter: 'Filter…',
      problemsOnly: 'Problems only',
      refresh: 'Refresh',
      download: 'Download',
      waiting: 'Request sent to the room, waiting for its answer…',
      truncated: 'Beginning cut: only the end of the file is shown.',
      empty: 'No log in the mounted folder yet (Player.log, Player-prev.log, portageroom_*.jsonl).',
      notMounted: 'The experience server’s Unity folder is not mounted into Portage Room. Add this volume to the “docker run” of portage-salle (read-only), then recreate the container:',
      errors: { room_silent: 'The room did not answer within a minute: is it online?', not_found: 'This log no longer exists on the machine.', studio_only: 'Studio accounts only.', not_a_room: 'Only Portage Room reads the log directly.' } as Record<string, string>,
    },
    link: {
      title: 'Link activity',
      outboxTitle: 'Sent by the room to the cloud',
      outboxInRoom: 'What the room sends is read in Portage Room (its send queue lives there).',
      commandsTitle: 'Commands sent to the experience server',
      none: 'Nothing yet.',
      when: 'When',
      what: 'What',
      state: 'State',
      error: 'Error',
      target: 'Target',
      sent: 'Sent',
      refused: 'Refused',
      retrying: 'Failed, retrying',
      pending: 'Waiting',
      acked: 'Acknowledged',
      failed: 'Refused',
      delivered: 'Delivered',
    },
    showLog: 'View the log',
    showLink: 'View the activity',
    none: 'No room declared yet.',
    noneHint: 'A room is a space where an experience runs, with its own experience server. Name it here, then attach its server.',
    headsets: '{n} headset{s}',
    server: 'Experience server',
    noServer: 'No server serves this room yet: attach it below, once it has reported.',
    network: 'Network and engine',
    networkOnRoom: 'Set on Portage Room: the headset token never leaves it.',
    map: 'Space map',
    mapOnCloud: 'Pushed from Portage Cloud, which holds the studio’s map library.',
    noMaps: 'The studio’s library has no map yet (Portage console → Studio inventory → Space maps).',
    pickMap: 'Map from the studio’s library',
    unassignedTitle: 'Servers not attached to a room',
    unassignedHint: 'An experience server that reported without a room: choose the room it serves.',
    fromCloud: 'Rooms are declared in Portage Cloud and come down to this Portage Room. Here you attach servers and set each room’s network.',
    ready: '✓ Ready to play',
    notReady: 'Not ready yet',
    reportedByRoom: 'reported by Portage Room at {date}',
    computedByCloud: 'computed by Portage Cloud — the network and the token are only known to Portage Room',
    refusedClear: 'Reset',
    refusedCleared: '{n} refusal(s) cleared.',
    outboxKinds: {
      'visit.create': 'Visit card created',
      'visit.move': 'Card moved',
      'visit.pair': 'Headset paired',
      'visit.group': 'Group',
      'visit.help': 'Call for help',
      'visit.personal': 'Personal settings',
      'command.ack': 'Command acknowledged',
      'run.upsert': 'Session',
      'events.relay': 'Telemetry',
      'assign.relay': 'Headset assignment',
      'fleet.add.relay': 'Headset added',
      'message.trace': 'Message to headsets',
      'alert.trace': 'Alert',
      'diagnostics.relay': 'Diagnostic',
    } as Record<string, string>,
    refusedStatus: {
      '400': 'refused, invalid content',
      '401': 'refused, key not recognised',
      '403': 'refused, not allowed',
      '404': 'refused, unknown to the cloud',
      '409': 'refused, conflict',
      '413': 'refused, too large',
      '422': 'refused, incomplete',
    } as Record<string, string>,
    checks: {
      server: 'Experience server',
      agent: 'Headset control',
      xroam: 'XROAM server',
      cloud: 'Link with Portage Cloud',
      battery: 'Batteries',
      thermal: 'Heat',
      asleep: 'Asleep',
      remote_file: 'Portage file on headsets',
      network: 'Network settings',
      token: 'Headset token',
      headsets: 'Headsets',
      versions: 'Versions',
      map: 'Space map',
      software: 'Portage version',
      schema: 'Room database',
    } as Record<string, string>,
    notReadyCount: 'Not ready yet — {summary}',
    countFail: '{n} to fix',
    countWarn: '{n} to watch',
    countUnknown: '{n} to check',
    andMoreOne: 'and 1 more',
    fromCloudShort: 'Rooms come from Portage Cloud.',
    unplacedShow: 'Show the list',
    unplacedHide: 'Hide',
    moreDetails: 'Details',
    lessDetails: 'Less',
    noTargetLine: 'No APK is published for this venue: the State column appears once one is.',
    goto: 'Go there',
    andMore: 'and {n} more',
    families: { network: 'Network', security: 'Security', headsets: 'Headsets', content: 'Content' } as Record<string, string>,
    boardTitle: 'Controls',
    boardHint: 'One light per room. Red: it cannot play. Orange: it plays, keep an eye on it. Green: ready.',
    boardCounts: '{fail} to fix · {warn} to watch',
    boardSee: 'See',
    serverSettings: 'Server settings',
    noHeadsetToMeasure: 'No headset answers: nothing to measure.',
    toFix: '{n} to fix',
    serverOnRoom: 'Its name, address and room are set on Portage Room (the gear next to the server).',
    serverRoom: 'Room it serves',
    serverRoomHint: 'The room whose headsets it runs. Moving it moves its readiness checklist and its headsets.',
    serverState: 'State',
    serverSeen: 'last heard {when}',
    serverIdentity: 'Identity',
    serverVersion: 'Version',
    serverToken: 'Headset token held',
    serverTokenMatch: '✓ same as the room',
    serverTokenMismatch: 'differs from the room: headsets won’t appear in the fleet',
    serverForget: 'Forget this server',
    serverForgetTitle: 'Forget {name}?',
    serverForgetBody: 'For a replaced machine. Its record goes; its sessions and history stay. If it comes back with the room key, it reappears on its own.',
    serverForgetOnline: 'Only a server that no longer checks in can be forgotten.',
    serverForgotten: 'Server forgotten.',
    serverSettingsOf: 'Settings — {name}',
    serverName: 'Name',
    serverIp: 'Address on the room network',
    serverIpHint: 'The IP headsets use to reach this machine on the room Wi‑Fi. It pre-fills the experience server’s “Network” tab.',
    serverAppears: 'An experience server appears on its own at its first heartbeat, with the room key (Rooms → Keys). You name it and say where it is here.',
    lights: { fail: 'Cannot play', warn: 'To watch', ok: 'Ready', unknown: 'Unknown' } as Record<string, string>,
    fields: {
      agentUrl: 'the headset control address (7791)',
      token: 'Headset token',
      networkServer: 'the Portage Room game server',
      xroamIp: 'XROAM server',
    } as Record<string, string>,
    help: {
      'agent.fail.unreachable': {
        why: 'The headset control address does not answer at {items}: headsets would not receive commands or settings.',
        fix: 'Check the experience server (or the Atlantis server in hybrid) is running, and that the address under the experience server’s “Network” tab is right.',
        gotoRoom: 'network',
      },
      'agent.unknown.not_set': { why: 'No headset control address yet.', fix: 'Fill it under the experience server’s “Network” tab.', gotoRoom: 'network' },
      'agent.unknown.room_only': {
        why: 'Only Portage Room can reach it, and it has not reported in the last 10 minutes.',
        fix: 'Check Portage Room is on and connected to the Internet.',
      },
      'xroam.fail.not_set': { why: 'No XROAM server address for this room.', fix: 'Fill it in the experience server block, “XROAM” tab.', gotoRoom: 'xroam' },
      'xroam.fail.unreachable': {
        why: 'The XROAM machine does not answer at {items}, nor its backup: hybrid experiences cannot start.',
        fix: 'Turn the XROAM server on, check it is on the room network, and check the address and port. On Windows, the firewall silently drops the check (TCP) when no rule opens the port: add a TCP rule on 7777 as well as the UDP one.',
        gotoRoom: 'network',
      },
      'xroam.warn.backup_only': {
        why: 'The main XROAM machine does not answer ({items}); only the backup does.',
        fix: 'It plays on the backup. Check the main server before the next show.',
        gotoRoom: 'network',
      },
      'xroam.warn.no_account': {
        why: 'The venue’s XROAM account is not set up: it plays, but visitors are not registered with XROAM.',
        fix: 'Studio Rabaska sets it up (console → Engines → Venue accounts).',
      },
      'xroam.warn.account_blocked': {
        why: 'XROAM’s form changed: registrations are held until the studio checks the matching.',
        fix: 'Studio Rabaska releases them from the console → Engines. Nothing is lost.',
      },
      'xroam.unknown.room_only': {
        why: 'Only Portage Room can reach the XROAM machine, and it has not reported in the last 10 minutes.',
        fix: 'Check Portage Room is on and connected to the Internet.',
      },
      'cloud.fail.error': {
        why: 'Portage Room cannot sync with Portage Cloud: {items}.',
        fix: 'Check Portage Room’s Internet access. It keeps working locally and catches up when the link is back.',
      },
      'cloud.fail.no_sync': {
        why: 'No sync with Portage Cloud since {since}.',
        fix: 'Check Portage Room’s Internet access. It keeps working locally and catches up when the link is back.',
      },
      'cloud.fail.no_report': {
        why: 'Portage Room has not reported since {since}: it is off, offline, or not reaching this cloud.',
        fix: 'Check Portage Room and its Internet access.',
      },
      'cloud.warn.refused': {
        why: '{items} message(s) from the room were refused by Portage Cloud in the last 24 hours.',
        fix: 'Tell Studio Rabaska: something the room did did not reach the office.',
      },
      'cloud.warn.pending': {
        why: '{items} message(s) are waiting to go up to Portage Cloud.',
        fix: 'Usually a slow or interrupted connection; it catches up on its own.',
      },
      'battery.warn': { why: '{n} under 30 % and not charging: {items}.', fix: 'Charge them between two shows.' },
      'thermal.warn': { why: '{n} throttled by heat: {items}.', fix: 'Let them cool down (out of the sun, fan) before the next show: the image may stutter.' },
      'asleep.warn': {
        why: '{n} handed to a visitor but asleep: {items}. They answer on the network but would refuse every command.',
        fix: 'Put them on, or press their power button, to wake them.',
      },
      'remote_file.warn': {
        why: 'On {n} headset{s}, portageroom-remote.json is not what Portage wrote (missing, unreadable, or another address, token or Portage switch): {items}.',
        fixRoom: 'Under “Headsets and versions”, tick them, then “Update…” → “Reconfigure only”. A file gone missing usually means the app was uninstalled (Build and Run from a dev machine).',
        fixCloud: 'Reconfigure them from Portage Room (Rooms → Versions).',
        gotoRoom: 'versions',
      },
      'server.fail.none': {
        why: 'No experience server is attached to this room.',
        fix: 'Start the Unity server build on the room machine. It shows up under “Servers without a room”: pick this room there.',
      },
      'server.fail.running_mute': {
        why: 'The experience server is running — it answers headset control ({items}) — but sends nothing to Portage.',
        fix: 'On its machine, check its key (C:\\ProgramData\\Portage\\portageroom.json, field “ingestKey”: the server key of THIS room, not another venue’s; and no “disabled”: true) and the Portage address it targets (127.0.0.1:8080 when Portage runs on the same machine). Its log says “key refused” or “unreachable”.',
      },
      'server.warn.device_rejected': {
        why: 'The server is reporting, but Portage refuses part of what it sends: {items}. Those devices are missing from the board.',
        fix: 'A field Portage does not accept: send this line to Portage Room (SDK) or the Portage team.',
      },
      'server.fail.rejected': {
        why: 'The experience server is sending, but Portage Room refuses what it sends (400 invalid_body): {items}.',
        fix: 'The SDK sends a field Portage does not accept: send this line to Portage Room (SDK) or the Portage team. Restarting will not fix it.',
      },
      'server.fail.offline': {
        why: 'The experience server has not been heard from since {since}.',
        fix: 'Check the machine is on and the server build is running; it reconnects on its own.',
      },
      'network.fail.missing': {
        why: 'Missing: {items}.',
        fixRoom: 'Fill them in under the experience server’s “Network” tab below. Headsets receive them at install time.',
        fixCloud: 'They are entered in Portage Room (Rooms → this room → experience server): the headset token never leaves the room.',
        gotoRoom: 'network',
      },
      'network.fail.port_clash': {
        why: 'The experience server (Portage network) and the XROAM server are both on {items}: only one of them can listen on that port.',
        fixRoom: 'Change one of the two ports under the experience server’s “Network” tab (Portage network: 7780 by default, and FISHNET_PORT on the experience server), then “Reconfigure” the headsets.',
        fixCloud: 'To change on Portage Room (Rooms → this room → experience server), along with FISHNET_PORT on the experience server.',
        gotoRoom: 'network',
      },
      'network.unknown.room_only': {
        why: 'Only Portage Room can check this, and it has not reported in the last 10 minutes.',
        fix: 'Check Portage Room is on and connected to the Internet.',
      },
      'token.unknown.no_server': {
        why: 'No experience server has reported in this room yet, so nobody can confirm the headset token.',
        fix: 'Start the experience server (Atlantis in hybrid) and check it reaches this instance: its log must say “agent started towards …” and no “heartbeat refused”. If it shows up under “Servers without a room”, place it here.',
      },
      'token.fail.missing': {
        why: 'No headset token: headsets would have nothing to present to the experience server (ours, or Atlantis in hybrid), and would not show up. It is not the ingest key.',
        fix: 'Since SDK 0.17.27 it is derived from the ingest key: give this Portage Room its portageroom.json (PORTAGEROOM_FILE, field “ingestKey”), the same file as the experience server’s. For a server still on an older SDK: paste its remote-token.txt in the “Headset token” tab, under Troubleshooting.',
        gotoRoom: 'network',
      },
      'token.fail.file_missing': {
        why: 'The token file is declared but not found: {items}.',
        fix: 'A server built under another product name writes it elsewhere: fix ROOM_TOKEN_FILE, or paste the token in the “Headset token” tab.',
        gotoRoom: 'network',
      },
      'token.warn.file_missing': {
        why: 'The token file is not found ({items}); the pasted token is used and it matches.',
        fix: 'Works as is. Fix ROOM_TOKEN_FILE so that a new token on the server is picked up without pasting.',
      },
      'token.fail.mismatch': {
        why: 'The saved token is not the experience server’s (different fingerprints): headsets would be refused.',
        fix: 'The experience server and this Portage Room must read the SAME portageroom.json: same ingestKey, same roomTokenVersion. A server on an SDK older than 0.17.27 keeps its remote-token.txt: update it, or paste that token under Troubleshooting. Then “Reconfigure” the headsets.',
        gotoRoom: 'network',
      },
      'token.unknown.not_announced': {
        why: 'The experience server does not announce its token fingerprint, so it cannot be checked.',
        fix: 'Update the Portage Room SDK of the server build to 0.13.1 or later.',
      },
      'token.unknown.room_only': {
        why: 'Only Portage Room can check this, and it has not reported in the last 10 minutes.',
        fix: 'Check Portage Room is on and connected to the Internet.',
      },
      'headsets.fail.0': {
        why: 'The experience server has not seen any headset.',
        fix: 'Turn the headsets on and start the experience. For a new headset: Rooms → the room → Add headsets.',
      },
      'headsets.warn': {
        why: 'In use but silent: {items}. Someone is wearing a headset Portage no longer follows.',
        fix: 'Check the headset: on, on the room Wi‑Fi, experience running. A headset at rest in the rack is not a problem and is not listed here.',
      },
      'versions.warn': {
        why: '{n} not on {target}: {items}.',
        fixRoom: 'Use “Update” under Versions.',
        fixCloud: 'The update is started from Portage Room (Rooms → Versions).',
        gotoRoom: 'versions',
        gotoCloud: 'versions',
      },
      'versions.unknown.nothing_published': {
        why: 'The studio has not published any APK for this venue, so there is no version to expect.',
        fix: 'The studio publishes from the Portage console → Studio inventory → APKs → “Published for”.',
      },
      'schema.fail.behind': {
        why: 'The room database has {room} migration(s), its code expects {cloud}: everything that touches the new tables fails (500 errors), the experience server cannot report.',
        fix: 'Run the migrations with the same image (docker run … server/scripts/migrate.ts, see docs/SALLE-SEULE.md), then restart the app.',
      },
      'server.warn.outbox_dropped_visits': {
        why: 'The server had to drop events from its outbox after a long time without reaching Portage, including visit starts or ends (events / visits): {items}. Some visits are missing from Analytics for that period.',
        fix: 'Nothing to redo: it is already caught up. Check why it could not reach Portage (instance stopped, network) so it does not happen again.',
      },
      'server.warn.outbox_dropped_unknown': {
        why: 'The server had to drop events from its outbox after a long time without reaching Portage: {items}. Details are missing from Analytics for that period, and possibly visits: its SDK (before 0.17.8) does not tell the two apart.',
        fix: 'Nothing to redo. Check why it could not reach Portage, and update its SDK to 0.17.8 or later to know whether visits were lost.',
      },
      'server.warn.outbox_dropped': {
        why: 'The server had to drop events from its outbox after a long time without reaching Portage: {items}. Scene, metric and performance details are missing from Analytics for that period; visits themselves are complete.',
        fix: 'Nothing to redo: it is already caught up. Check why it could not reach Portage (instance stopped, network) so it does not happen again.',
      },
      'software.warn.behind_schema': {
        why: 'This room runs Portage {room}, the cloud {cloud}: {lag} migration(s) behind. It still plays, but silently ignores what the cloud sends that is new.',
        fixRoom: 'Update this machine: the image at the cloud’s tag ({cloud}), its migrations, then restart the app.',
        fixCloud: 'The update happens on the venue’s machine: the image at the cloud’s tag ({cloud}), its migrations, then a restart.',
      },
      'software.warn.behind': {
        why: 'This room runs Portage {room}, older than the cloud ({cloud}). It still plays; some recent changes are not there.',
        fixRoom: 'Update this machine: the image at the cloud’s tag ({cloud}), its migrations, then restart the app.',
        fixCloud: 'The update happens on the venue’s machine: the image at the cloud’s tag ({cloud}), its migrations, then a restart.',
      },
      'software.warn.untracked': {
        why: 'This room runs a Portage version older than version tracking, so older than the cloud ({cloud}).',
        fixRoom: 'Update this machine: the image at the cloud’s tag ({cloud}), its migrations, then restart the app.',
        fixCloud: 'The update happens on the venue’s machine: the image at the cloud’s tag ({cloud}), its migrations, then a restart.',
      },
      'map.unknown.never_pushed': {
        why: 'No space map has been pushed to this room’s headsets.',
        fix: 'Store the room survey under “Space map” below, then install it on the headsets.',
        gotoCloud: 'map',
        gotoRoom: 'map',
      },
      'map.warn': {
        why: '{n} have not confirmed the install yet: {items}.',
        fix: 'They install it the next time the experience starts: turn them on.',
      },
      'map.fail': {
        why: 'The install failed on {n}: {items}.',
        fix: 'Push the map again; if it fails again, open its settings (Rooms, or “··· › Headset settings” on its card).',
        gotoCloud: 'map',
      },
    } as Record<string, { why: string; fix?: string; fixRoom?: string; fixCloud?: string; gotoRoom?: string; gotoCloud?: string }>,
    mapStatus: '{name}: {ok} installed, {pending} pending, {failed} failed — pushed {date}',
    versions: 'Headsets and versions',
    target: 'Published for this venue: {label} {version}',
    installed: 'Installed',
    behind: 'Behind',
    ahead: 'Newer than the published APK',
    upToDate: 'Up to date',
    installWhat: 'APK to install',
    unplaced: '{n} headsets are in no room.',
    placeHere: 'Put them in {room}',
    placed: '{n} headsets put in {room}.',
    installOn: 'Install {version} on {n} headset{s}',
    notInstalled: 'Not installed',
    awaitingHeadset: '{version} installed, waiting for the headset',
    noHeadsetsHere: 'No headset in this room. Add some from Headsets (+) and pick this room.',
    checking: 'Checking the rooms: server, headset control, XROAM, headsets… it takes a few seconds.',
    headsetsLoading: 'Loading this room’s headsets and their versions…',
    ipColumn: 'Wi-Fi IP',
    liveColumn: 'Live',
    headsetSearch: 'Find a headset: name, serial, IP…',
    picked: '{n} selected',
    pickAllHeadsets: 'Select all {n} headsets',
    pickAllFiltered: 'Select the {n} headsets shown by the filter',
    unpickAll: 'Clear the selection',
    noHeadsetMatch: 'No headset matches.',
    pageOf: '{from}–{to} of {n}',
    pagePrev: 'Previous page',
    pageNext: 'Next page',
    ipUnknown: 'Unknown',
    ipSet: 'Enter',
    ipEdit: 'Change',
    noIpLate: 'Not included, no IP address: {names}. Installation goes over Wi-Fi ADB (headset: Settings → Wi-Fi → the network → IP address).',
  },
  install: {
    cloudOnlyRoom: 'Installation runs from Portage Room.',
    cloudHint: 'It is the one on the headsets’ network, and the headset token never leaves it. Open Portage Room to install.',
    room: 'Room',
    settings: 'What {room} puts on each headset',
    settingsHint: 'Filled in once, and editable at any time. The headsets receive these addresses and the headset token at every installation; « Reconfigure » pushes a change without reinstalling.',
    settingsReady: '{room}: network settings complete.',
    settingsMissing: '{room}: network settings incomplete (device channel or headset token) — complete them in',
    portageBlock: 'Portage Room experiences',
    xroamBlock: 'XROAM experiences (hybrid)',
    agentUrl: 'Headset control address (7791)',
    hostLabel: 'Experience server machine (IP)',
    hostHintPortage: 'Its address on the room Wi‑Fi, as the headsets see it. They are controlled there (7791) and play there (Portage Room).',
    hostHintXroam: 'The Atlantis server’s address on the room Wi‑Fi, as the headsets see it. They are controlled there (7791) and play there (XROAM).',
    splitAddresses: 'Separate addresses',
    joinAddresses: 'One machine',
    thisMachine: 'This computer’s address',
    hostDetected: 'Filled in from the address Portage knows for this machine (announced by the experience server, or set at launch). Check it before saving.',
    thisMachineUnknown: 'This machine’s network address is not known yet: the experience server has not announced it and the launcher did not set it. Type it once (on a Mac: System Settings → Network).',
    networkServer: 'Portage Room game server',
    networkPort: 'Port',
    xroamIp: 'XROAM server',
    xroamSecondaryIp: 'XROAM backup',
    xroamPort: 'XROAM port',
    portClash: 'The experience server (Portage network) and the XROAM server are both on {at}: only one of them can listen on that port. The Portage network listens on 7780 by default — change one of the two ports (and FISHNET_PORT on the experience server), then “Reconfigure” the headsets.',
    token: 'Headset token',
    tokenFromFile: 'Read from the experience server (fingerprint {fp}).',
    tokenDerived: 'Derived from the ingest key in portageroom.json, version {v} (fingerprint {fp}). Nothing to paste: the experience server derives the same one (SDK 0.17.27).',
    tokenOverride: 'Forced by PORTAGEROOM_REMOTE_TOKEN on this Portage Room (fingerprint {fp}) — troubleshooting.',
    tokenTroubleshoot: 'Troubleshooting: paste a token by hand',
    tokenTroubleshootHint: 'Only for an experience server still on an SDK older than 0.17.27 (its remote-token.txt). Used only if it is the token the server announces.',
    tokenStored: 'Entered (fingerprint {fp}). Type a new one to replace it.',
    tokenMissing: 'The password headsets present to the experience server (the remote-token.txt file the experience server created in its data folder). Paste it once; it is never shown again.',
    tokenMatches: '✓ The experience server holds the same token.',
    tokenMismatch: 'The experience server announces another token (fingerprint {fp}): the headsets would not appear in the fleet. Check that it reads the same portageroom.json (ingestKey, roomTokenVersion).',
    tokenMismatchShort: 'The headset token does not match the experience server’s: installation refused.',
    save: 'Save',
    what: 'Experience to install',
    noBuilds: 'No APK published for this venue yet (Portage console → Titles → the title’s APKs).',
    where: 'Headsets',
    noHeadsets: 'No headset with a known address. Scan the network with “Add headsets” in Rooms.',
    headset: 'Headset',
    state: 'State',
    authHint: 'The first time, each headset asks “Allow USB debugging?”: accept it headset on, ticking “Always allow”.',
    launch: 'Install on {n} headsets',
    reconfigure: 'Reconfigure {n} headset{s}',
    apply: 'Update {n} headset{s}…',
    tabNetwork: 'Network',
    tabXroam: 'XROAM (hybrid)',
    tabToken: 'Headset token',
    tabMissing: 'to complete',
    xroamSameHost: 'XROAM server: {ip}, the machine of the Network tab (“Separate addresses” there for another one).',
    applyTitle: 'Update {n} headset{s}',
    andMore: 'and {n} more',
    applyInstall: 'Install an APK',
    applyInstallHint: 'Installs the APK, rewrites the room’s files and restarts the app.',
    /* Les APK de test de la salle (15/10) : studio seulement, jamais au nuage. */
    publishedGroup: 'Published',
    testGroup: 'Test APKs (this room)',
    testBadge: 'TEST',
    testFollowsRoom: 'room’s type',
    testNote: 'Test APK of this room: never published nor sent to the cloud. An older version is accepted by default.',
    testCloudDown: 'The cloud is not answering: only this room’s test APKs are offered.',
    testAdd: 'Add a test APK',
    testAddHint: 'It stays in this room until removed, never sent to the cloud.',
    testFolder: 'You can also drop it in this folder on the room server:',
    testStored: 'Test APK {version} added.',
    testRemoved: 'Test APK removed.',
    testRemove: 'Remove this test APK',
    testRemoveTitle: 'Remove this test APK?',
    testRemoveBody: '{name} leaves this room’s list and its file is deleted. Headsets that have it keep it.',
    testRemoveFolder: 'The file is also deleted from the watched folder.',
    applyReconfigure: 'Reconfigure only',
    applyWarn: 'The app restarts on each headset: whoever is wearing one will be interrupted.',
    applyHint: '“Update” installs an APK or reconfigures the ticked headsets, after a confirmation. “Check” only reads their files, without changing anything.',
    helpLabel: 'Help',
    disablePortage: 'Disable Portage on these headsets',
    disablePortageHint: 'Emergency switch: the app runs without Portage on these headsets (no tracking, no control). Untick and reconfigure to bring it back.',
    reconfigureHint: 'Rewrites the room’s files (agent, token, servers) and restarts the app, without reinstalling — for example when the XROAM server’s address changes.',
    reconfigureStarted: 'Reconfiguration started on {n} headset{s}.',
    cancel: 'Cancel',
    cancelAll: 'Cancel the {n} in progress',
    cancelled: '{n} install(s) cancelled.',
    retry: 'Retry',
    retryAll: 'Retry the {n} failure{s}',
    retryStarted: 'Retry started on {n} headset{s}.',
    retryDowngrade: 'Retry, accepting the older version',
    replaceApp: 'Replace the app…',
    details: 'Details',
    queued: 'Queued, #{pos} ({width} at a time)',
    summary: { running: '{n} in progress', queued: '{n} queued', failed: '{n} failed', done: '{n} done' },
    summaryAll: 'Show all',
    options: 'Install options',
    allowDowngrade: 'Accept an older version',
    allowDowngradeHint: 'Android only allows it when the installed app is a debug build; otherwise publish the newest APK.',
    replaceAppOption: 'Replace the app (uninstall, then install)',
    replaceAppHint: 'For an app signed with another key.',
    replaceAppWarn: 'The app’s own data is erased on these headsets (saves, logs, preferences). Portage then puts back the headset identity, the headset token and the XROAM configuration.',
    replaceTitle: 'Replace the app on {n} headset{s}',
    replaceConfirm: 'Uninstall and reinstall',
    newerInstalled: 'Newer than {version} already installed on: {names}. Android will refuse the install unless “Accept an older version” is ticked.',
    adbVersion: 'Read over ADB on {when} (the headset is not reporting)',
    check: 'Check ({n})',
    checkHint: 'Reads portageroom-remote.json on these headsets over ADB and compares it with what Portage wrote — even when Portage is switched off on them. Changes nothing.',
    checkDone: '{read} read, {missed} not reachable over ADB.',
    portageColumn: 'Portage',
    remoteFile: {
      ok: 'In line',
      disabled: 'Switched off',
      drift: 'Differs',
      missing: 'File missing',
      unreadable: 'File unreadable',
      not_installed: 'App absent',
    } as Record<string, string>,
    remoteFileDiff: { agent: 'address', token: 'token', disabled: 'Portage switch', lbeMap: 'expected LBE map', keepAwake: 'keep awake' } as Record<string, string>,
    remoteFileRead: 'Read {when}',
    remoteFileDiffers: 'Differs: {what}',
    remoteFileNever: 'not read',
    started: 'Installation started on {n} headset{s}.',
    progress: 'Installations',
    last: 'Last step',
    states: {
      queued: 'Waiting',
      awaiting_authorization: 'Accept in the headset',
      downloading: 'Downloading',
      installing: 'Installing',
      configuring: 'Configuring',
      restarting: 'Restarting',
      done: 'Installed',
      failed: 'Failed',
    } as Record<string, string>,
    codes: {
      adb_unreachable: 'ADB does not answer on this headset (Wi‑Fi debugging off?)',
      adb_unauthorized: 'The headset did not accept the room’s key',
      INSTALL_FAILED_VERSION_DOWNGRADE: 'A newer version is already installed',
      INSTALL_FAILED_UPDATE_INCOMPATIBLE: 'Different signature: only “Replace the app” installs it',
      INSTALL_FAILED_INSUFFICIENT_STORAGE: 'Headset storage full',
      version_mismatch: 'Installed, but not the expected version',
      provisioning_incomplete: 'Room settings incomplete',
      checksum_mismatch: 'The downloaded file is corrupted',
      cloud_unreachable: 'Portage Cloud unreachable',
      push_failed: 'A configuration file could not be written',
      files_dir_missing: 'The app did not create its folder after being launched: is the headset asleep?',
      downgrade_refused: 'Android refuses the older version: the installed app is not a debug build. Publish a newer APK, or replace the app',
      no_identity: 'No readable identity on this headset',
      not_installed: 'The app is not installed on this headset: install it first',
      interrupted: 'Interrupted by a restart of the experience server: start this headset again',
      remote_file_mismatch: 'portageroom-remote.json, read back after writing, is not what was written',
      install_failed: 'The install failed: what adb said is below',
      cancelled: 'Cancelled',
      timeout: 'The install took more than 15 minutes: headset asleep or Wi‑Fi too slow?',
      internal_error: 'Unexpected error on the experience server: the detail is below',
      build_not_found: 'This APK no longer exists in Portage Cloud',
      INSTALL_FAILED_INVALID_APK: 'The APK file is invalid for this headset',
      INSTALL_PARSE_FAILED_NO_CERTIFICATES: 'The APK is not signed',
      uninstall_failed: 'Uninstalling failed: what adb answered is below',
    } as Record<string, string>,
    errors: {
      zone_layout_unavailable: 'The zone layout could not be fetched from Portage Cloud.',
      zone_layout_mismatch: 'ZoneLayout.json read back on the headset does not match: push it again.',
      provisioning_incomplete: 'Room settings are incomplete: agent, token, and server address.',
      cloud_unreachable: 'Portage Cloud cannot be reached: the APK list is unavailable.',
      no_cloud: 'This Portage Room is not linked to Portage Cloud.',
      build_not_found: 'This APK no longer exists.',
      probe_build: 'This APK carries no Portage package: there is no Portage file to read on the headsets.',
      build_required: 'Choose an APK to install. Reconfiguring and checking work without one.',
    } as Record<string, string>,
  },
  builds: {
    title: 'Experiences to install (APK)',
    declination: 'Declination',
    declinations: { portage: 'Portage (full)', hybrid: 'Hybrid (XROAM + Portage)', probe: 'Probe (XROAM, observed)' } as Record<string, string>,
    declinationHints: {
      portage: 'Built with Portage Room: Portage runs everything (network, headsets, show).',
      hybrid: 'Built on XROAM with our packages inside: XROAM runs the show, Portage steers the headsets.',
      probe: 'A partner’s build without our packages: installed with its XROAM settings only, observed but not steered.',
    } as Record<string, string>,
    playsAs: 'plays it as {declination}',
    hint: 'Uploading installs nothing: publish the APK for a venue, then the venue chooses when to install it, room by room. Each APK is built for one declination (Portage, hybrid, probe), and is only offered to venues that play this experience in that declination. Always the same signing source for a given package.',
    releasedTo: 'Published for',
    released: 'Published: the venue can install it in its rooms.',
    unreleased: 'Publication withdrawn for this venue.',
    noInstances: 'No venue plays this title yet.',
    declinationsShort: { portage: 'Portage', hybrid: 'Hybrid', probe: 'Probe' } as Record<string, string>,
    drop: 'Drop the APK here, or click to choose it',
    dropHint: 'Package, version and version code are read from the file.',
    change: 'Change',
    apk: 'Package',
    versionName: 'Version',
    versionCodeShort: 'code {code}',
    size: 'Size',
    upload: 'Upload',
    uploading: 'Sending… {pct} %',
    stored: 'APK {version} uploaded.',
    none: 'No APK uploaded yet.',
    remove: 'Delete',
    removeTitle: 'Delete APK {version}?',
    removeBody: 'Venues it was published for will no longer be offered it. Headsets that already have it keep it.',
    errors: {
      apk_unreadable: 'This file’s manifest cannot be read: is it really an APK?',
      already_stored: 'This exact APK is already uploaded.',
      too_large: 'File too large (1.9 GB max).',
      unsupported_type: 'Choose an .apk file.',
      content_mismatch: 'This file is not an APK.',
      '401': 'Your session has expired: sign in again.',
    } as Record<string, string>,
  },
  views: {
    emptyTitle: 'No live view right now',
    emptyHint: 'Start a headset’s live view to watch what the person sees. Every headset of the venue is listed below, with what is missing.',
    start: 'Start the view',
    stop: 'Stop the view',
    starting: 'Starting…',
    started: 'Live view requested on {name}.',
    confirmTitle: 'Start the live view during a session?',
    confirmBody: '{name} is in an experience. Its live view costs heat and battery on a headset someone is wearing.',
    why: {
      offline: 'Off or unreachable',
      stopped: 'View turned off',
      no_source: 'Not broadcasting',
      no_address: 'Broadcasting without an address',
      not_on_lan: 'Only visible from the venue network',
      error: 'Broadcast failing',
    } as Record<string, string>,
  },
  pause: {
    pause: 'Pause this person’s experience',
    resume: 'Resume this person’s experience',
    pauseShort: 'Pause',
    resumeShort: 'Resume',
    pauseGroup: 'Pause the group',
    resumeGroup: 'Resume the group',
    xroam: 'This experience is run by XROAM: individual pause is not available.',
    notPaused: 'Not paused',
    notResumed: 'Not resumed',
    states: {
      pending: 'Pause requested…',
      paused: 'Paused',
      held: 'Paused — waiting for the headset',
      resuming: 'Resuming…',
    } as Record<string, string>,
    codes: {
      unsupported: 'the experience cannot pause one person',
      alert_active: 'an alert is in progress',
      not_playing: 'not in an experience',
      room_paused: 'the whole room is paused',
      timeout: 'no answer from the headset',
      expired: 'the experience server did not pick it up',
      partial: 'some headsets did not follow',
    } as Record<string, string>,
    errors: {
      unsupported: 'This experience is run by XROAM: individual pause is not available.',
      not_playing: 'This person is not in an experience right now.',
      not_a_room: 'Pause is sent from Portage Room only.',
      no_server_holds_device: 'No experience server holds this headset.',
      no_server_device: 'No experience server in this venue.',
    } as Record<string, string>,
  },
  msg: {
    toHeadset: 'Message in the headset',
    toPerson: 'Message to {name}',
    write: 'Message',
    writeTo: 'Write to {name}',
    toGroup: 'Message to the group',
    placeholder: 'Shown in the headset — 140 characters at most',
    duration: 'On screen',
    send: 'Send',
    outcomes: {
      pending: 'On its way…',
      delivered: 'Shown',
      failed: 'Not shown',
      expired: 'Not delivered in time',
      timeout: 'No answer from the experience server',
    } as Record<string, string>,
    codes: {
      offline: 'headset offline',
      paused: 'headset not worn',
      alert_active: 'an alert is on screen',
      unsupported: 'this build cannot show messages',
      partial: 'some headsets of the group did not show it',
    } as Record<string, string>,
    errors: {
      not_a_room: 'Messages leave from Portage Room only.',
      no_server_holds_device: 'No experience server holds this headset: start the experience first.',
      no_server_device: 'No experience server in this venue.',
      target_device_not_found: 'This headset is not in this venue.',
      target_group_not_found: 'This group no longer exists.',
    } as Record<string, string>,
  },
  alert: {
    button: 'Alert',
    ongoing: 'Alert in progress',
    drillOngoing: 'Drill in progress',
    raiseTitle: 'Evacuation alert',
    raiseHint: 'Every headset of the venue will show “Remove your headset”, and sessions pause.',
    drill: 'Drill',
    real: 'Real alert',
    hold: 'Hold for 2 seconds',
    confirmReal: 'Raise a REAL alert in the whole venue?',
    confirmDrill: 'Start a drill in the whole venue?',
    confirm: 'Raise the alert',
    title: 'Alert in progress',
    drillTitle: 'Drill in progress',
    raisedBy: 'Raised by {name} at {time}',
    shownOf: '{n} of {total} headsets showed the alert',
    missing: 'Not shown — go and check',
    strangers: 'Unknown headsets that showed it',
    servers: 'Experience servers',
    serverOk: 'All shown',
    serverDelivered: 'Received',
    serverPending: 'Waiting',
    serverFailed: 'Failed',
    clear: 'Lift the alert',
    cleared: 'Lifted',
    codes: {
      offline: 'offline',
      paused: 'not worn',
      unsupported: 'build too old',
      partial: 'partial',
      expired: 'not delivered in time',
      timeout: 'no answer',
    } as Record<string, string>,
    errors: {
      not_a_room: 'The alert leaves from Portage Room only.',
      alert_active: 'An alert is already in progress.',
      no_server_device: 'No experience server in this venue: evacuate by voice.',
    } as Record<string, string>,
  },
  fadd: {
    room: 'Room of these headsets',
    roomHint: 'It lists them, installs them and filters the board. You can change it later on the headset.',
    listOptional: 'Headset list (optional)',
    csvHintWithScan: 'With a scan, a column of addresses is enough to match; serial numbers help, names are the point.',
    listRead: '{n} line(s) read.',
    needSerialOrIp: 'Map at least the serial or the IP column.',
    scanSummary: '{n} device(s) answered, {known} of which said who they are.',
    toMatch: 'Match ({n})',
    matchTitle: 'Match headsets to their names',
    matchHint: 'One line per headset. Portage matched by serial when it read one, by address otherwise — check those. Fix a name or a missing serial right here.',
    exportCsv: 'Export to CSV',
    colSource: 'From',
    colNote: 'Match',
    sources: { scan: 'Scan', list: 'List', both: 'Scan + list' } as Record<string, string>,
    notes: {
      serial: 'by serial',
      ip: 'by IP — check',
      conflict: 'conflict: the list says {serial} at this address',
      not_seen: 'not seen on the network',
      no_serial: 'no serial — fill it in',
      waiting: 'waiting for approval in the headset',
    } as Record<string, string>,
    serialPlaceholder: 'serial number',
    namePlaceholder: 'no name',
    notAddable: '{n} line(s) without a serial number will not be added.',
    keptLabel: 'name kept — the list said “{label}”',
    title: 'Add headsets',
    hint: 'Find them on the network or drop a spreadsheet. Nothing is written before you confirm.',
    confirmTitle: 'Check before adding',
    confirmHint: 'This is exactly what Portage will write. Rows in red will be left out.',
    scan: 'Automatic scan',
    csv: 'CSV upload',
    scanHint: 'Headsets answering on this block are listed — known ones get their address updated, unknown ones that give a serial number are added.',
    scanKnown: '{n} known headsets answered',
    scanNew: '{n} new headsets gave their serial number',
    scanAnonymous: '{n} headsets answered without saying who they are:',
    scanWaiting: '{n} headsets are waiting for your approval — accept in each headset, then scan again:',
    scanAnonymousHint: 'A PICO headset does not publish its serial number on the network. Portage recognises one by its address once the address is known; for the others, the serial number is needed once.',
    toCsv: 'Complete these addresses in a CSV',
    csvLabel: 'Serial numbers, fixed addresses and names',
    csvHint: 'One headset per line. Comma, semicolon or tab — the separator is detected. Columns are recognised by their header (serial, ip, name…) and can be corrected below.',
    csvFile: 'Choose a file',
    columns: 'What each column holds',
    colIgnore: 'Ignore',
    colSerial: 'Serial number',
    colIp: 'Fixed IP',
    colName: 'Name',
    colAction: 'What happens',
    pickAll: 'All / none',
    colGroup: 'Subgroup',
    needSerial: 'Pick the column that holds the serial number: it is the only identity a headset gives.',
    review: 'Review {n} headsets',
    nothingToAdd: 'Nothing to add.',
    back: 'Back',
    commit: 'Add {n} headsets',
    done: '{n} headsets written to the fleet.',
    grouping: 'Grouping',
    groupApart: 'Put this batch in a group of its own',
    groupName: 'Group name',
    defaultGroup: 'Delivery',
    groupExists: 'This group already exists: the headsets will be added to it.',
    groupRoom: 'Groups belong to the studio: Portage Cloud writes them when this room syncs.',
    splitNone: 'No subgroups',
    splitCount: 'Into N subgroups',
    splitSize: 'N headsets per subgroup',
    splitHint: 'In list order — the order the headsets came out of the crate.',
    headsets: '{n} headsets',
    actions: {
      create: 'New',
      claim: 'Arrives',
      update: 'Updated',
      same: 'Unchanged',
      skip: 'Left out',
    } as Record<string, string>,
    why: {
      empty: 'No serial number',
      duplicate: 'Serial listed twice',
      bad_ip: 'Unreadable IP address',
      no_ip: 'No IP address: installation goes over Wi-Fi ADB',
      ip_taken: 'Address already used by another headset here',
      elsewhere: 'Currently at {venue} — move it from the Portage console',
      foreign_studio: 'Belongs to another studio',
    } as Record<string, string>,
    groupProblems: {
      venue_without_studio: 'This venue is not attached to a studio: its headsets cannot be grouped.',
      unknown_serial: 'A subgroup holds a headset that will be left out.',
      grouped_twice: 'A headset is placed in two subgroups.',
      name_taken: 'One of these names is already used by another group of the studio. Rename it.',
    } as Record<string, string>,
  },
  parc: {
    headsets: 'Headsets',
    devices: 'Devices',
    sellable: 'Sellable capacity',
    add: 'Add headsets',
    install: 'Installation',
  },
  res: {
    title: 'Resources',
    catalogue: 'Catalogue',
    newResource: 'Add a resource',
    name: 'Name',
    kind: 'Kind',
    headset: 'Headsets',
    room: 'Room',
    vehicle: 'Vehicle',
    staff: 'Staff',
    other: 'Other',
    quantity: 'How many you own',
    unitCapacity: 'People per unit',
    active: 'Counted',
    inactive: 'Ignored',
    usedBy: 'Used by',
    linkExperience: 'Add an experience',
    perParticipant: 'Per person',
    perSession: 'Per session',
    holdMinutes: 'Unavailable after (min)',
    holdHint: 'A headset needs cleaning and charging before the next group.',
    unlink: 'Remove',
    load: 'Load today',
    saturated: 'Fully used',
    exceeded: 'Exceeded — your schedule needs more than you own',
    noResources: 'No shared resource yet.',
    noResourcesHint: 'A resource is something two experiences compete for: a pool of headsets, a room, an operator. Declare it once and Portage Cloud refuses the sale that would oversell it — even when the room still has seats.',
    unlinked: 'Not used by any experience — it will never block a sale.',
    modeHint: 'Per person for headsets, per session for a room or an operator.',
    sellable: 'Sellable capacity',
    fleetDeclared: 'sold',
    fleetReal: 'in the fleet',
    fleetFault: '(including {n} faulty)',
    fleetMatch: 'Matches the fleet.',
    fleetSpare: '{n} kept in reserve.',
    fleetOver: 'Oversold: {n} seats without a headset.',
  },
  tpl: {
    title: 'Studio templates',
    contentLang: 'Language of the text',
    langMissing: 'missing: {langs}',
    start: 'Start from a template',
    none: 'No template for this jurisdiction yet.',
    noneHint:
      'Studio Rabaska writes them per country and per province, because a release signed in Quebec and one signed in France are not the same text. Ask the studio if you need one where you operate.',
    jurisdiction: 'Written for',
    wholeCountry: 'the whole country',
    use: 'Use this one',
    used: 'Added to the flow',
    notice:
      'A template is a starting point, not legal advice. From the moment you add it, the text is yours: edit it, and have your counsel review it before going live.',
    stale: 'The studio published a new version of {name}',
    staleFrom: 'You copied v{from}, the current one is v{to}.',
    staleWhat: 'What changed',
    staleKeep:
      'Nothing changes on its own — your text stays exactly as it is. Compare and decide.',
    dismiss: 'Understood',
    kindRegistration: 'Registration',
    kindNda: 'NDA',
    kindWaiver: 'Waiver',
    country: 'Country',
    region: 'Province or state',
    regionHint: 'Leave empty when the template applies to the whole country.',
    usage: 'Used by',
    steps: 'flow steps',
    upToDate: 'up to date',
    publish: 'Publish a new version',
    publishHint:
      'Bumps the version and warns every operator who copied it. A typo fix does not need this.',
    changelog: 'What changed in this version',
    retire: 'Retire',
    retireHint: 'Stops offering it. Flows that already copied it keep their text.',
    newTemplate: 'New template',
    saved: 'Saved',
  },
  studio: {
    homeVenue: 'Where the fleet comes home',
    homeVenueHint: '“Bring home” sends this entity’s headsets here. Leave empty to bring them home without a venue, in storage.',
    homeVenueNone: 'None: no venue',
    identity: 'Studio entities',
    administration: 'Administration',
    infoTab: 'General info',
    identityHint:
      'Named in the confidentiality agreements signed at the venues it answers for, through {{studio.nomLegal}} and {{studio.adresse}}. Templates reference these values instead of spelling them out — a move would otherwise mean re-editing every template and having everyone sign again.',
    newStudio: 'New entity',
    none: 'No entity declared.',
    noneHint:
      'An entity is the legal person that answers for a set of venues. It is named in the confidentiality agreements signed there, and it writes the templates those venues are offered.',
    orphans: '{n} venue(s) are not attached to any entity.',
    orphansHint:
      'They are offered no template at all — no NDA, no waiver. Attach them from Venues, on each venue’s sheet.',
    attached: 'Answering entity',
    attachedHint:
      'Named in the agreements signed here, and the source of the templates this venue is offered.',
    attachedNone: 'Not attached — no template will be offered',
    name: 'Name',
    legalName: 'Legal name',
    legalNameHint: 'The one that appears in an agreement. Falls back to the name above if empty.',
    legalHint: 'Those of the entity that answers for the signed documents — not the venue’s, and not the partner’s.',
    address: 'Address',
    city: 'City',
    region: 'Province or state',
    postalCode: 'Postal code',
    country: 'Country',
    email: 'Contact email',
    website: 'Website',
    preview: 'As it will read in a signed document',
  },
  media: {
    title: 'Media',
    intro:
      'The briefing your room screens play. It belongs to the studio’s EXPERIENCE, like its variables: the studio makes it, so it makes its briefing, in every language. A venue can only play the media of the experiences it plays.',
    kind: 'Kind',
    briefing: 'Briefing',
    trailer: 'Trailer',
    locale: 'Language',
    upload: 'Upload a file',
    size: 'Size',
    none: 'No media on this title.',
    noneHint:
      'One row per language — a briefing is dubbed, not subtitled. MP4, WebM or MP3, 500 MB maximum.',
    tooLarge: 'That file is too heavy — 500 MB maximum.',
    uploadFailed: 'The upload failed. Nothing was stored.',
  },
  profile: {
    title: 'My account',
    identity: 'Identity',
    name: 'Name',
    email: 'Email',
    emailHint: 'Identifies your account and signs you in. An owner or manager can change it.',
    changePhoto: 'Change photo',
    removePhoto: 'Remove',
    tooLarge: 'That image is too heavy — 2 MB maximum.',
    badImage: 'That file is not a readable image. JPEG, PNG or WebP.',
    preferences: 'Preferences',
    language: 'Interface language',
    languageHint: 'Follows you from one venue and one device to another. It is not the venue’s language.',
    password: 'Password',
    currentPassword: 'Current password',
    newPassword: 'New password',
    passwordHint: 'Ten characters minimum.',
    confirmPassword: 'Confirm',
    changePassword: 'Change password',
    passwordOnCloud: 'Your password is held by Portage Cloud, not by this Portage Room. Change it from the cloud back office — Portage Room checks it there when you sign in.',
    mismatch: 'The two new passwords do not match.',
    wrongPassword: 'That is not your current password.',
    weak: 'Too short — ten characters minimum.',
  },
  studioTeam: {
    title: 'Team',
    hint: 'Who works for this entity. They are superadmins: they open the console and act in every venue, whatever their entity. Invite by link — the person chooses their own password.',
    leaveTeam: 'Remove from the team',
    addToTeam: 'Add to this team',
    unassigned: 'Superadmins without an entity (they keep full access)',
    placed: 'Added to the team',
    unplaced: 'Removed from the team — still a superadmin',
    add: 'Add someone',
    email: 'Email',
    name: 'Name',
    addHint: 'An existing account is promoted and keeps its password. A new one gets a one-time link, valid for an hour.',
    send: 'Invite',
    invited: 'Invitation ready: send the link below',
    promoted: 'Account promoted to studio superadmin',
    linkFor: 'Link for {name}',
    linkExpires: 'Valid once, until {time}. Send it privately.',
    copy: 'Copy the link',
    hideLink: 'Hide',
    none: 'No studio superadmin.',
    you: 'you',
    remove: 'Revoke studio access',
    removeTitle: 'Remove {name} from the studio team?',
    removeBody: 'They keep their account and their venue memberships, but lose the console and access to every venue.',
    removed: 'Removed from the studio team',
    errors: {
      not_yourself: 'You cannot remove yourself.',
      last_superadmin: 'There must always be at least one studio superadmin.',
    } as Record<string, string>,
  },
  personal: {
    addBlank: 'Blank parameter',
    reuse: 'Reuse “{name}”',
    defaultShort: 'default',
    sharedWith: 'Shared with {names}: a change applies to all of them.',
    hiddenAt: 'Hidden at {venues}',
    languageAtOnboarding: 'Ask for the language at registration',
    languageAskedHint: 'The visitor picks the experience language at the end of registration, among the venue’s languages; it is sent to the headset. The console can still change it during the visit.',
    languageNotAskedHint: 'Not asked at registration: the experience lets the visitor choose (Atlantis: the intercom). The console can change it during the visit; Atlantis returns to the venue default between visitors.',
    languageOnboardingLabel: 'Experience language',
    languagePreviewHint: 'The venue’s languages, the registration language preselected.',
    preview: 'Preview · visitor screen',
    previewTitle: 'Your preferences',
    title: 'Personal settings',
    variables: 'Variables',
    untitled: 'Untitled setting',
    defaultIs: 'default: {value}',
    appliesCount: '{n} experience(s)',
    edit: 'Edit',
    done: 'Done',
    optionValue: 'Value read by the headset',
    appliesTo: 'Applies to',
    hiddenForHint: 'Per experience in a venue: offered everywhere else.',
    noInstance: 'No venue plays these experiences yet.',
    hint: 'Switches and choices the VISITOR makes at registration (e.g. guided or explorer mode). The operator can change them on their card; the headset they wear reads them. Language is always offered.',
    none: 'No personal setting for this experience',
    noneHint: 'Add one to offer it in the registration form.',
    add: 'Add a setting',
    addAssistance: 'Template: assistance mode (Atlantis)',
    key: 'Key read by the headset',
    keyHint: 'Lowercase, dots allowed: what the experience reads with PlayerParams.',
    labelFr: 'Label (French)',
    labelEn: 'Label (English)',
    type: 'Type',
    toggle: 'Switch (on / off)',
    choice: 'Choice',
    options: 'Choices',
    addOption: 'Add a choice',
    optionIcon: 'Pictogram',
    descriptionFr: 'Description shown under the choice (FR)',
    descriptionEn: 'Description shown under the choice (EN)',
    iconNone: 'None',
    icons: { map: 'Map', compass: 'Compass', search: 'Magnifier', eye: 'Eye', people: 'People', clock: 'Clock', help: 'Helping hand', globe: 'Globe' },
    default: 'Default',
    hiddenFor: 'Hidden for',
    duplicateKey: 'Two settings share a key, or one is called “language” (reserved).',
    person: 'Personal settings',
    personOf: 'Settings — {name}',
    language: 'Language',
    groupDefault: 'group language ({language})',
    headsetDefault: 'the experience’s language',
    applied: 'Sent to the headset they wear.',
    noParams: 'This experience has no personal setting yet: only the language.',
    formTitle: 'Your preferences',
  },
  checkReport: {
    title: 'Check report',
    summary: '{read} read, {missed} unreachable over ADB, {problems} to look at.',
    portageOk: 'Portage: {n} in line',
    xroamOk: 'XROAM: {n} in line',
    duplicates: 'Duplicate XROAM numbers: {numbers}',
    duplicatesHint: 'Two headsets with the same number: the XROAM server keeps the first and ejects the second, which stays offline until its app is relaunched.',
    unreachable: '{n} unreachable',
    fix: 'Fix the duplicates ({n})',
    fixHint: 'Each headset takes the number in its name (Casper-07 → 7), or a free one; Portage rewrites its XROAM file and relaunches the app. Refused on a headset paired with a visitor.',
    duplicate: 'Duplicate',
    expandHint: 'Click a line for the full XROAM file.',
    change: 'Change',
    apply: 'Apply',
    applyHint: 'Rewrites the XROAM file with this number and relaunches the app. The XROAM visitor pairing follows the number: refused on a headset paired with a visitor.',
    renumbered: 'Renumbered: {list}.',
    outcome: { unauthorized: 'ADB not authorised', refused: 'ADB refused', unreachable: 'Unreachable' } as Record<string, string>,
    xroamState: { ok: 'In line', drift: 'Differs', missing: 'File missing', unreadable: 'File unreadable' } as Record<string, string>,
    xroamDriftNumber: 'Number differs',
    xroamDriftOther: 'Settings differ',
    xroamValues: '{what}: {read} on the headset, {expected} expected',
    xroamField: { deviceId: 'number', ip: 'XROAM server', secondaryIp: 'backup address', port: 'port', displayName: 'Name shown' } as Record<string, string>,
    errors: {
      device_in_visit: 'A headset is paired with a visitor: renumber it between two visits',
      number_taken: 'This number is already used in the room',
      number_invalid: 'A number from 1 to 9999',
      no_ip: 'Headset without a known IP address',
      not_found: 'Unknown headset',
    } as Record<string, string>,
  },
  ipChanged: {
    badge: 'New',
    hint: 'The headset announced a new address (it was {previous}, changed {when}). Portage follows it on its own.',
  },
  linkQr: {
    show: 'QR code',
    hint: 'Scan it with the device that should open this address.',
    copy: 'Copy the address',
    print: 'Print',
  },
  pages: {
    title: 'Dynamic pages',
    hint: 'A page made by the studio (an exit leaderboard…) that Portage fills with the room’s visits of the day and rotates through the venue’s languages. Show its address on the screen’s computer: Portage Room’s address (it keeps running without internet), or this one.',
    hintRoom: 'Pages are declared in Portage Cloud and come down here. On the screen’s computer, open the address below on THIS Portage Room: it keeps running without internet.',
    create: 'New page',
    defaultName: 'Exit leaderboard',
    experience: 'Experience',
    room: 'Room',
    template: 'Template',
    name: 'Name',
    empty: 'No dynamic page',
    emptyHint: 'Create one per room: it shows that room’s visits of the day.',
    nothingOffered: 'No template available: the studio drops one on the experience (Console › Experience › Pages).',
    disabled: 'Off',
    shown: 'Displayed',
    notShown: 'No screen',
    enabled: 'Page on',
    copy: 'Copy',
    open: 'Open',
    demo: 'Preview with examples',
    addressHint: 'On a Portage Room: http://<its address>:8080{path}. The token is the authorization: one screen, one address.',
    rotate: 'New address',
    rotateHint: 'Replaces the token: the old address stops working.',
    rotateTitle: 'Change this page’s address?',
    rotateBody: 'The screens showing it will have to open the new address.',
    remove: 'Remove',
    removePageTitle: 'Remove “{name}”?',
    removePageBody: 'The screens showing it will display “page not found”. The visits stay.',
    templatesTitle: 'Dynamic pages',
    templatesHint: 'Page templates for this experience: a .zip or .tar.gz with the Handlebars page, its portage.json (languages, venue settings, fields computed from metrics) and its files. Venues then create their page per room, and only change the settings the template opens to them.',
    templatesEmpty: 'No template yet',
    templatesEmptyHint: 'Drop an archive, or ask Claude to install an official one (MCP install_page_template).',
    fields: 'Fields',
    sorting: 'Ranking',
    sortingValue: 'by {field}, the last {n} visits of the day',
    venueParams: 'Venue settings',
    files: 'Files',
    pick: 'Choose an archive',
    pickEmpty: 'No archive chosen',
    upload: 'Drop',
    uploaded: 'Template saved.',
    inUse: 'A venue page still uses this template.',
    removeTitle: 'Remove template “{name}”?',
    removeBody: 'Only possible when no venue page uses it any more.',
    errors: {
      bundle_unreadable: 'Unreadable archive',
      bundle_too_large: 'Archive too large (20 MB max)',
      manifest_missing: 'portage.json not found',
      manifest_invalid: 'portage.json is invalid',
      entry_missing: 'The page named in portage.json is missing',
      template_invalid: 'The Handlebars page does not compile',
    } as Record<string, string>,
  },
  timings: {
    showUnreported: 'Show the {n} scenes no headset has reported',
    hideUnreported: 'Hide the scenes never reported',
    title: 'Scene timing',
    hint: 'The planned duration of each scene, imported from Reed (base timing). In the room, a card blinks red when someone stays in their scene longer than planned plus the venue’s percentage.',
    empty: 'No timing yet',
    emptyHint: 'Ask Claude to import it from Reed (MCP tool set_scene_timings). The Portage cloud cannot reach Reed directly.',
    importedFrom: 'From {source}, imported {at}.',
    manual: 'entered by hand',
    scene: 'Scene',
    sceneId: 'Scene id reported by the headset',
    noScene: 'none: no alert',
    planned: 'Planned',
  },
  game: {
    heatTitle: 'Heat map',
    heatHint: 'Time spent per 50 cm cell over one day, measured on the real headsets, over the floor plan imported from Creator. Same palette as Creator’s simulation; red = the busiest 5 % of cells, so a few very busy spots do not wash out the rest.',
    heatDay: 'Day',
    heatEmpty: 'No heat map for this day',
    heatEmptyHint: 'It fills in once the experience server sends positions (SDK 0.17.30 and later).',
    heatNoPlan: 'This venue has no floor plan yet: it is imported from Creator (MCP tool set_floor_plan).',
    heatTotal: '{h} h of presence in total',
    heatUpdated: 'updated at {time}',
    heatPlan: 'plan {plan}',
    lossTitle: 'Incomplete figures.',
    lossBody: 'Servers had to drop events from their outbox after a long time offline: {items}. Scene, metric and performance details are missing for the period; visits themselves are complete.',
    lossBodyVisits: 'Servers had to drop events from their outbox after a long time offline: {items}. Some VISITS are missing for the period, not only details: counts and durations are understated.',
    lossVisits: 'including {n} visit starts or ends',
    lossBodyUnknown: 'Servers had to drop events from their outbox after a long time offline: {items}. Details are missing for the period, and possibly visits: their SDK (before 0.17.8) does not tell the two apart.',
    title: 'Analytics',
    periods: { '7': '7 days', '30': '30 days', '90': '90 days', '365': '1 year' } as Record<string, string>,
    experience: 'Experience',
    allExperiences: 'All experiences',
    venue: 'Venue',
    allVenues: 'All venues',
    version: 'Version',
    allVersions: 'All versions',
    compareBy: 'Compare by',
    compareNone: 'Nothing',
    compare: { ageBand: 'Age band', language: 'Language', appVersion: 'APK version', variantId: 'A/B variant', model: 'Headset model', venue: 'Venue' } as Record<string, string>,
    perf: {
      title: 'Headset performance',
      hint: 'What the headsets held during the visits, scene by scene (Portage Room SDK 0.17.3+). Frames slower than the headset’s refresh rate count as “below target”.',
      meanFps: 'Mean FPS',
      belowTarget: 'Time below target',
      p95: 'Frame time p95',
      drain: 'Battery used',
      thermalMax: 'Max temperature',
      hot: 'Time overheating',
      memMin: 'Min free memory',
      scene: 'Scene',
      basis: 'Measured on {n} visit(s).',
    },
    livePlaying: '{n} in play now',
    liveRecent: '{aborted} dropped in 30 min · {finished} finished today',
    empty: 'No visit recorded over this period',
    emptyHint: 'Visits come from the headsets through the Portage Room SDK (visit start, chapters, end). A build without the SDK sends nothing.',
    visits: 'Visits',
    medianDuration: 'Median duration',
    finished: 'Finished',
    perDay: 'Visits per day',
    journey: 'Journey',
    journeyHint: 'The share of visits that reach each chapter, and the average time spent in it. Click a chapter for its visits.',
    openVisits: 'See the visits',
    allSessions: 'See all sessions',
    sessionsTitle: 'Sessions',
    sessionsHint: 'One per person and headset, last 90 days — no name. Orange: beyond the time planned in Portage Creator; red: beyond it by the venue’s overrun margin.',
    chapterTime: 'In {chapter}',
    plannedShort: 'planned {t}',
    overrunOnly: 'Overruns only',
    sortRecent: 'Most recent',
    sortLongest: 'Longest',
    outcomeAll: 'All',
    groupColumn: 'Group',
    groupOf: '{label} · {n}',
    groupUnnamed: 'Group',
    groupFilter: 'Group {label}',
    clearFilter: 'Clear',
    alone: 'Alone',
    chaptersColumn: 'Chapters',
    overruns: '{n} overrun{s}',
    profile: 'Profile',
    wholeVisit: 'Whole visit',
    wholeVisitLine: 'average {avg} · median {median}',
    plannedTotal: 'planned {planned} in Portage Creator',
    detailChapters: 'Chapters',
    detailLog: 'Log',
    aborted: 'Drop-outs:',
    reasons: { quit: 'Quit', sleep: 'Headset asleep', silent: 'Silent', left_experience: 'Left the experience', unknown: 'Unknown' } as Record<string, string>,
    metrics: 'Experience metrics',
    metricsHint: 'What each experience measures, as the studio declares it: the key the APK sends, and how to count it.',
    storagePerVisit: 'Disk used per visit',
    walkPerVisit: 'Distance walked per visit',
    bandAttendance: 'Attendance',
    bandInWork: 'In the experience',
    bandInTitle: 'In {title}',
    walkSub: 'max {max} · {total} in total · {n} visits',
    objects: {
      title: 'Objects handled',
      hint: 'How many visitors touched, grabbed, collected or placed each object. One row per object and gesture; click a number column to sort one way, then the other.',
      empty: 'No object handled over this period',
      emptyHint: 'The headset sends Metrics.Record("<interact|collect|place>.<gesture>.<scene>.<object>", 1) once per visit, object and gesture (interact.language.<language> has no scene). An object missing from the list was not handled by anyone.',
      scene: 'Scene',
      allScenes: 'All scenes',
      allActions: 'All',
      action: 'Gesture',
      actions: {
        'interact.touch': 'touched',
        'interact.grab': 'grabbed',
        'interact.assisted': 'assisted',
        'interact.language': 'language chosen',
        'collect.scanned': 'collected · scanned',
        'collect.grabbed': 'collected · grabbed',
        'collect.assisted': 'collected · assisted',
        'collect.attempted': 'collection attempted',
        'place.manual': 'placed',
        'place.assisted': 'placed · assisted',
      } as Record<string, string>,
      count: '{n} objects',
      object: 'Object',
      visitors: 'Visitors',
      visitShare: 'Share of visits',
      sceneShare: 'Share of the scene',
      legacy: 'old format',
      legacyHint: 'Part of these visitors comes from builds up to 0.5.1215, which sent milliseconds held and counted contact bounces.',
      legacyNote: 'Rows marked « old format » include visits from builds up to 0.5.1215: a visit that had the object counts, but those builds also sent contact bounces. Pick a version in the filter at the top to separate them.',
      showAll: 'Show all {n}',
      showLess: 'Show fewer',
      filter: 'Filter by name (e.g. collect)',
      filterLabel: 'Filter objects by name',
      noMatch: 'No object matches this filter.',
      sortBy: 'Sort by {column}',
    },
    declare: 'Declare the metrics',
    noMetrics: 'No metric received.',
    undeclared: 'undeclared',
    venueVisible: 'Visible to the venue',
    pickToDeclare: 'Pick an experience to declare its metrics.',
    sandbox: 'sandbox',
    sandboxExcluded: '« All venues » leaves out the sandbox venues ({venues}): pick one in the Venue filter to see its playtests.',
    aggregates: { sum: 'total per visit', max: 'maximum', avg: 'average', last: 'last value of the visit', flag: 'reached or not', duration: 'duration' } as Record<string, string>,
    comparison: 'Compared by {by}',
    segment: 'Segment',
    unknown: 'Unknown',
    visitsHint: 'Last 90 days of detail — no name, no card.',
    noVisits: 'No visit here.',
    when: 'Start',
    outcome: 'Outcome',
    outcomes: { finished: 'Finished', aborted: 'Dropped', open: 'In progress' } as Record<string, string>,
    duration: 'Duration',
    lastScene: 'Last chapter',
    timeline: 'Timeline of the visit',
    declareTitle: 'Experience metrics',
    declareHint: 'The APK sends a metric event { key, value }; here you say how to count it. An undeclared key is kept and flagged.',
    declareEmptyHint: 'Add the keys this experience emits.',
    addMetric: 'Add a metric',
    atlantisPreset: 'Prefill Atlantis’s 4 measures',
    metricKey: 'Key',
    metricLabelFr: 'Label (French)',
    metricLabelEn: 'Label (English)',
    aggregate: 'Count as',
    unit: 'Unit',
    duplicateKey: 'Two metrics have the same key.',
    venueSection: 'Play',
    venueSectionHint: 'How visits go in your venue: duration, finished visits, chapters, and the metrics the studio shares with you.',
  },
  incidents: {
    title: 'Operations incidents',
    hint: 'Every “we had to help” logged in a venue, with its category, the note as written, the chapter the person was in and the headset’s state at that moment. Never the visitor’s name.',
    empty: 'No incident over this period.',
    all: 'All categories',
    allVenues: 'All venues',
    when: 'When',
    venue: 'Venue',
    category: 'Category',
    headset: 'Headset',
    chapter: 'Chapter',
    note: 'Note',
    by: 'By',
    noHeadset: 'no headset paired',
    state: 'Headset at that moment',
    worn: 'worn',
    notWorn: 'not worn',
    charging: 'charging',
    asleep: 'asleep',
    thermal: 'Android thermal {level}/6',
    crashLink: '↯ A crash of this headset at the same moment',
    showAll: 'See all ({n})',
    showLess: 'Show fewer',
    stage: { arrived: 'arrived', registered: 'registered', paired: 'paired', in_room: 'in the room', done: 'done' } as Record<string, string>,
  },
  diag: {
    lastCommand: 'Last Portage command',
    outcomes: { acked: 'done', failed: 'refused', pending: 'waiting' } as Record<string, string>,
    session: 'Session at the time',
    logTail: 'End of the Unity log ({n} lines)',
    yes: 'yes',
    no: 'no',
    sessionKeys: {
      groupSize: 'Group (visitor included)',
      serverConnected: 'Connected to its session server',
      visibleAvatars: 'Other avatars shown',
      personal: 'Personal settings (values)',
      config: 'Remote config (hash, origin, variant)',
      scene: 'Scene',
      sceneSeconds: 'In the scene for (s)',
      sessionSeconds: 'In the experience for (s)',
      role: 'Role',
      memoryFreeMB: 'Free memory (MB)',
      fps: 'FPS',
      uptimeSeconds: 'App up for (s)',
    } as Record<string, string>,
    room: 'Room',
    temperature: 'Temperature',
    battery: 'Battery',
    thermalLevel: 'level {n}',
    charging: 'charging',
    unknownDevice: 'Not in the fleet',
    resolve: 'Dismiss',
    originLabel: 'Where it comes from',
    origins: { app: 'App', sdk: 'Portage SDK', engine: 'Third-party engines', library: 'Unity and libraries' } as Record<string, string>,
    originHints: {
      app: 'The experience’s own code — and problems without a stack (hang, abrupt end), which cannot be attributed.',
      sdk: 'The Portage Room SDK embedded in the experience.',
      engine: 'A third-party engine (XROAM…), recognised by its stack prefixes in the catalogue. Nothing to fix on our side.',
      library: 'Unity, Pico, Meta, XR Interaction Toolkit… or a stack that only contains Unity and .NET.',
    } as Record<string, string>,
    noIssuesFiltered: 'Nothing with these origins',
    noIssuesFilteredHint: 'Problems exist from other origins: turn their filter on above.',
    resolveHint: 'Takes it off the list. It comes back if it happens again.',
    resolvedToast: 'Dismissed. It will come back if it happens again.',
    resolveConfirmTitle: 'Dismiss this problem?',
    resolveConfirmBody: '« {message} » leaves the list. It comes back on its own if it happens again, and stays findable under « Dismissed ».',
    unresolve: 'Bring back',
    unresolvedToast: 'Back in the list.',
    viewOpen: 'Open',
    viewResolved: 'Dismissed',
    noResolved: 'Nothing dismissed on this window',
    agoNow: 'just now',
    agoMinutes: '{n} min ago',
    agoHours: '{n} h ago',
    agoDays: '{n} days ago',
    pageOf: '{from}–{to} of {n}',
    previous: 'Previous',
    next: 'Next',
    times: 'Times',
    timesHint: 'How many times this error occurred on this headset',
    noSnapshot: 'Report received before headset states were recorded: no name or temperature.',
    title: 'Diagnostics',
    allWorks: 'All experiences',
    days: '{n} days',
    health: 'Crash-free sessions',
    healthHint: 'Per experience and per version: the question to ask before deploying — is it better than before?',
    noSessions: 'No sessions reported yet',
    noSessionsHint: 'The experience server sends session counts per version once the Portage Room SDK diagnostics are enabled in the experience.',
    work: 'Experience',
    version: 'Version',
    sessions: 'Sessions',
    crashed: 'Crashed',
    crashFree: 'Crash-free',
    issues: 'Problems',
    issuesHint: 'Grouped by signature, most frequent first. One headset is a headset to check; forty is a bug to fix.',
    noIssues: 'Nothing reported on this window',
    noIssuesHint: 'Exceptions, hangs and abrupt exits arrive here from the experience servers, never from the headsets directly.',
    problem: 'Problem',
    occurrences: 'Occurrences',
    devices: 'Devices',
    venues: 'Venues',
    versions: 'Versions',
    lastSeen: 'Last seen',
    kept: 'Kept',
    keep: 'Keep',
    unkeep: 'Stop keeping',
    retentionHint: 'Reports and their screenshots are deleted 30 days after they arrive. “Keep” exempts this problem.',
    keptHint: 'Kept since {date}: its reports and screenshots are not deleted after 30 days.',
    reportLine: '{n} occurrence(s), {from} → {to}',
    breadcrumbs: 'Path before the problem ({n} steps)',
    screenshot: 'Screenshot at the time of the problem',
    showScreenshot: 'Show the screenshot',
    natures: {
      exception: 'Exception',
      unhandled: 'Unhandled',
      hang: 'Hang',
      abnormal_exit: 'Abrupt exit',
      assert: 'Assertion',
      error: 'Error',
      warning: 'Warning',
    } as Record<string, string>,
  },
  eng: {
    title: 'Experience engine',
    catalogue: 'Engines',
    integrations: 'Integrations',
    intro:
      'Which platform runs this experience in the room. Portage is the default; a licensed title may run on a partner’s stack, and Portage Cloud then registers each visitor there at the end of onboarding.',
    engine: 'Engine',
    nativeHint:
      'Runs on Portage’s own ingest. Nothing else to configure — see docs/INTEGRATION-UNITY.md for what the Unity build sends.',
    credentials: 'Your account with them',
    required: 'required',
    consentNote:
      'Their terms and privacy policy are shown to the visitor at the end of your onboarding, and the box they tick is what Portage Cloud sends on. Nobody is registered without having accepted.',
    mapping: 'Field rules',
    mappingHint:
      'Their form on the left, where the value comes from on the right. This is where our separate first and last name become their single Name, and our date of birth becomes their age.',
    enable: 'Register visitors with this partner at the end of onboarding',
    cannotEnable: 'Cannot be enabled yet — still missing:',
    blocked: 'Sending held',
    driftTitle: 'Their form has changed',
    driftWhat:
      'Nothing is being sent, and nothing is lost: what was queued is waiting. Read their form, check the rules still hold, then release.',
    probe: 'Read their form now',
    probeResult: 'What their form declares today',
    probeChanged: 'differs from what we knew',
    probeSame: 'unchanged',
    release: 'Release and replay',
    log: 'Deliveries',
    noLog: 'Nothing sent yet.',
    noLogHint:
      'A delivery is queued at the end of each onboarding and leaves within the minute. The visitor never waits for it.',
    when: 'When',
    status: 'Status',
    who: 'Registered',
    externalId: 'Their id',
    detail: 'Detail',
    attempts: 'attempts',
    statuses: { pending: 'Queued', held: 'Held', sent: 'Sent', failed: 'Failed' },
    noBridge: 'No third-party engine is available.',
    noBridgeHint:
      'Studio Rabaska declares them. An engine is a partner platform that runs an experience in the room; once declared, any venue can point an experience at it and supply its own credentials.',
    accountHint: 'One account for the whole venue. Every experience you point at {engine} uses it.',
    accountSet: 'This venue has an account with {engine}.',
    accountNone: 'This venue has no account with this partner yet — nothing can be sent.',
    accountMissing: 'Set up the account in the venue settings first: nothing could be sent.',
    goToSettings: 'Venue settings',
    studioSetsUp: 'As the studio, you set up this venue’s engines in the console.',
    declination: 'Declination',
    declinations: { hybrid: 'Hybrid — our packages inside', probe: 'Probe — observed only' } as Record<string, string>,
    declinationHints: {
      hybrid: 'The partner runs the show with our packages inside: Portage steers the headsets. Hybrid APKs are offered.',
      probe: 'A partner build without our packages: Portage installs it and observes, without steering. Probe APKs are offered.',
    } as Record<string, string>,
    openInConsole: 'Open in the console → Engines',
    noPartner: 'No partner engine for this venue',
    noPartnerHint: 'Have an XROAM account (or another partner)? Tell Studio Rabaska: the studio sets up the technical side, nothing to enter here.',
    inPlace: 'In place',
    inPlaceHint: 'Set up by the studio. Visitors are registered with {engine} at the end of onboarding.',
    blockedVenueHint: 'The partner’s form has changed: registrations are held until the studio checks the matching. Nothing is lost — they go out once released.',
    signupOn: 'visitors registered there',
    signupOff: 'registration off',
    runsOn: 'Runs on {engine}',
    setByStudio: 'Set up by Studio Rabaska.',
    venueAccounts: 'Venue accounts',
    venueAccountsHint: 'Each venue tells us whether it has XROAM; we set up its technical identifiers and field matching here. The venue only sees the state and the registration log.',
    incomplete: 'Not usable yet — still missing:',
    usedBy: 'Used by',
    usedByNone: 'No experience points at this engine yet.',
    enginePairing: 'Read the pairing from the engine',
    enginePairingHint: 'In hybrid mode, the engine says which of its users is on which headset, and the room pairs the card by itself: by the engine id, otherwise by first and last name. A user unknown to Portage gets a card of their own. In room and done keep following the headset. Off: Portage only, as before.',
    overrides: 'This experience has its own credentials',
    overridesHint:
      'Leave empty to use the venue account. Useful when a room has its own registration point with the partner — the placeholder shows what the venue account holds.',
    checkNow: 'Check now',
    checkNowHint: 'Reads each partner’s signup form (XROAM…) to spot a changed field. Done on its own twice a day (6 am and 6 pm, Montréal time); this button forces it now.',
    autoCheck: 'Partner forms are checked automatically at 6 am and 6 pm',
    lastRead: 'last read {at}',
    newEngine: 'New engine',
    noEngine: 'No engine declared.',
    catalogueHint:
      'An engine is a platform that runs an experience in the room. Declaring one here lets any venue point an experience at it, supply its own credentials, and map the fields — without a line of code.',
    kind: 'Kind',
    native: 'Portage',
    bridge: 'Bridge',
    usage: 'Venues',
    active: 'enabled',
    fingerprint: 'Form fingerprint',
    vendor: 'Vendor',
    definition: 'Definition',
    definitionHint:
      'Their fields, the calls to replay, and where to read their live form. This is a copy of what you read on their side — keeping it as JSON avoids a translation layer between the two.',
    badJson: 'Not valid JSON:',
    nothingToProbe: 'No engine declares a probe.',
    probeUnread: 'could not be read — check the credentials and the probe',
    sources: {
      ignore: 'Do not send',
      fullName: 'First and last name',
      firstName: 'First name',
      lastName: 'Last name',
      email: 'Email',
      age: 'Age, from date of birth',
      birthdate: 'Date of birth',
      gender: 'Gender',
      civility: 'Title',
      locale: 'Language',
      engineConsent: 'Their terms, accepted',
      hasGuardian: 'Has a guardian',
      yes: 'Always yes',
      no: 'Always no',
    },
  },
  notify: {
    title: 'Notifications',
    discord: 'Discord',
    url: 'Webhook address',
    urlHint:
      'From Channel settings › Integrations in Discord. Leave empty to post nothing. Only a discord.com address is accepted: this field sends visitor names out of Portage Cloud.',
    events: 'What to announce',
    eventsHint: 'One line in the channel each time. Nothing is announced twice.',
    noneChosen: 'Nothing ticked — the channel is set but will stay silent.',
    notDiscord: 'That is not a Discord webhook address.',
    test: 'Test',
    testOk: 'Sent — check the channel.',
    testTimeout: 'No answer in 5 s. Outbound network filtered?',
    testGone: 'Discord no longer knows this webhook — recreate it.',
    testFailed: 'Failed ({code}).',
    names: {
      arrival: 'Ticket scanned',
      onboarding: 'Onboarding signed',
      delivery_sent: 'Registered with a partner',
      delivery_failed: 'Registration failed',
      retention_due: 'Records to review',
      crash: 'Headset crash',
      app_error: 'Headset error',
    },
    hints: {
      arrival: 'Someone walked in and their ticket was accepted at the door.',
      onboarding: 'A visitor finished signing — waiver, NDA, whatever your flow asks.',
      delivery_sent: 'Their account exists on the partner platform; the room will know them.',
      delivery_failed: 'Every attempt failed. Somebody has to look — the room will not know them.',
      retention_due:
        'Weekly. Lists what is past its retention period. Nothing is ever deleted automatically — someone approves.',
      crash: 'The experience crashed, froze or quit on a headset. Once per problem: when new, back after being filed, or after a quiet day.',
      app_error: 'An error without a crash. Same rhythm — can be talkative on an experience in development.',
    },
  },
  imp: {
    title: 'Import contacts',
    subtitle: 'From a CSV file',
    drop: 'Drop a CSV file here',
    dropHint: 'or click to choose one',
    explain:
      'Export your attendee list from Eventbrite (or any other tool) as CSV, then drop it here. The next step lets you say which column is which — no need to rename anything beforehand. The file is read on this computer; nothing is sent until you confirm.',
    emptyFile: 'This file has no readable rows.',
    badEncoding:
      'The accents in this file are broken — it is probably not UTF-8. Re-export it, or open it in Excel and save as “CSV UTF-8”.',
    failed: 'The import could not run. Nothing was written.',
    lines: 'rows',
    delimiter: 'separator',
    ragged: 'irregular rows',
    needEmail: 'Point one column at Email — it is what identifies a contact.',
    ignore: 'Ignore this column',
    emptyColumn: 'empty in every row',
    fields: {
      email: 'Email',
      firstName: 'First name',
      lastName: 'Last name',
      phone: 'Phone',
      locale: 'Language',
      marketingOptIn: 'Subscribed',
      tags: 'Tags',
      notes: 'Notes',
    },
    tagAll: 'Tag every imported contact',
    tagHint: 'Makes them findable later, and lets you undo a bad import by filtering on it.',
    tagPlaceholder: 'Eventbrite — March 2026',
    consent: 'I hold marketing consent for the people in this file.',
    consentHint:
      'Without this, the subscription column is read but ignored, and everyone comes in unsubscribed. Buying a ticket is not subscribing to a newsletter.',
    noConsentNote:
      'Nobody will be marked as subscribed: you did not declare holding consent. You can still write to them once they opt in.',
    back: 'Back',
    checkFile: 'Check the file',
    willCreate: 'New',
    willComplete: 'Completed',
    unchanged: 'Unchanged',
    invalid: 'Skipped',
    duplicate: 'Duplicates',
    dedup: '{n} rows repeat an address already in the file — a ticketing export repeats the buyer on every ticket. {unique} people will be written.',
    line: 'Row',
    outcome: 'What happens',
    noEmail: 'no address',
    badEmail: 'unreadable address',
    doImport: 'Import {n} contacts',
    importing: 'Importing…',
    finished: 'Import done.',
    created: 'Created',
    touched: 'Completed',
    skipped: 'Skipped',
  },
  analytics: {
    title: 'Analytics',
    tabSales: 'Sales',
    tabAudience: 'Audience',
    tabExperience: 'In the room',
    allVenues: 'All venues',
    venues: 'Venues',
    period: 'Period',
    last30: '30 days',
    last90: '90 days',
    last365: '12 months',
    custom: 'Custom',
    from: 'From',
    to: 'To',
    exportCsv: 'CSV',
    exportWorkbook: 'Full workbook',
    exporting: 'Preparing…',
    revenue: 'Revenue',
    refunded: 'refunded',
    bookings: 'Bookings',
    seats: 'Seats',
    fillRate: 'Fill rate',
    seatsSold: 'Seats sold',
    seatsAttended: 'Seats attended',
    perDay: 'per day',
    perWeek: 'per week',
    perMonth: 'per month',
    partialLast: 'The dotted segment is the current period, which is not over.',
    byExperience: 'By experience',
    byTicketType: 'By ticket type',
    byChannel: 'Sales channel',
    leadTime: 'How far ahead people book',
    leadMedian: 'Median',
    days: 'days',
    leadPast: 'after the fact',
    leadSameDay: 'same day',
    leadEve: 'day before',
    leadWeek: 'within a week',
    leadMonth: 'within a month',
    leadMore: 'over a month',
    newVsReturning: 'New and returning',
    fresh: 'First visit',
    returning: 'Been before',
    ageBands: 'Age at visit',
    ageCoverage: 'Real birthdate known for {known} of {total} attended seats ({percent}%).',
    ageFallback: 'The rest is only known by ticket type, below.',
    ageMasked: 'Bands under {min} people are hidden rather than shown — on a quiet slot, one cell names a person.',
    masked: 'too few',
    minorsAdults: 'Adults and minors',
    adults: 'Adults',
    minors: 'Minors',
    visitHours: 'When people come',
    hoursHint: 'Seats by weekday and hour, in each venue’s own local time.',
    colourBy: 'Break down by',
    everyone: 'Everyone',
    sceneTimes: 'Time spent per scene',
    sceneHint: 'Median seconds inside each scene, crossed with the visitor’s age. This is the number a remote-config slider answers to.',
    roomDuration: 'Time in the room',
    stages: 'Where the queue forms',
    stagesHint: 'Median time between each step of a visitor’s path. Removed cards are excluded.',
    visitors: 'Visitors',
    toRegister: 'Arrival → registration',
    toPair: 'Registration → headset',
    toStart: 'Headset → start',
    nominal: 'Nominal',
    median: 'Median',
    runs: 'Sessions run',
    visits: 'passes',
    noData: 'Nothing in this window.',
    noDataHint: 'Widen the period, or pick another venue.',
    noTelemetry: 'No experience has reported yet.',
    noTelemetryHint: 'Scene times arrive from the Unity server build. See docs/INTEGRATION-UNITY.md for the contract.',
    unknownAge: 'age unknown',
  },
  ops: {
    portageDisabled: 'Portage disabled (reconfiguration)',
    storageFree: 'disk {free} free',
    storageOf: 'of {total}',
    title: 'Live ops',
    allRooms: 'All rooms',
    roomFree: 'Free',
    roomsMoved: 'Rooms, their servers, keys and network settings are managed in',
    nothingReadYet: 'This headset has not reported its settings yet. Blocks marked “Not read” show no value: they are waiting for the headset’s own.',
    tagUnread: 'Not read',
    tagFailed: 'Read failed',
    stateOn: 'On',
    stateOff: 'Off',
    ipdModeAuto: 'Automatic',
    ipdModeManual: 'Manual',
    settingGroups: {
      sound: 'Sound and image',
      vision: 'Vision',
      control: 'Control',
      actions: 'Actions',
    } as Record<string, string>,
    ipdAutomatic: 'Measured by the headset: turn off “Automatic IPD” to set it by hand.',
    settingHelp: {
      volume: 'The sound volume in the headset.',
      brightness: 'The brightness of the headset screen.',
      passthrough: 'Shows the real room through the headset cameras, instead of the experience.',
      recenter: 'Realigns the visitor’s position on the room origin.',
      capture: 'Takes a screenshot of what the visitor sees.',
      kiosk: 'Locks the system buttons, so the visitor cannot leave the experience.',
      ipd: 'The distance between the two lenses, to match the visitor’s eyes for a sharp image.',
      ipd_auto: 'The headset measures the distance between the eyes itself when it is put on.',
      cast: 'Streams what the visitor sees live, to follow them from the console.',
      language: 'The language of the experience: subtitles, voices and menus.',
    } as Record<string, string>,
    fleet: 'Fleet',
    devices: 'Devices',
    gear: 'Gear',
    dense: 'Dense',
    cosy: 'Comfortable',
    large: 'Large',
    fold: 'Collapse the header — one more lane of cards',
    unfold: 'Show the header again',
    declared: 'Declared',
    seen: 'Reporting',
    usable: 'Usable',
    charging: 'Charging',
    offline: 'Silent',
    fault: 'Fault',
    lowBattery: 'Low battery',
    battery: 'Battery',
    lastSeen: 'Last heard',
    never: 'never',
    label: 'Label',
    pool: 'Pool',
    noDevices: 'No device has reported yet.',
    sdkIdentity: 'SDK identity: {id} (the number on the box is the one shown)',
    serialUnknown: 'Identity made up by the SDK: the serial on the box has not been read yet. “Check” or a scan reads it over ADB.',
    noDevicesHint: 'The Unity build announces its headsets and its server as soon as it holds an ingest key. The studio mints it per room, in Distribution.',
    gearSearch: 'Name, serial or IP',
    gearSortLabel: 'Sort by',
    gearSort: { state: 'Needs attention first', name: 'Name', battery: 'Battery (lowest first)', ip: 'IP address' },
    gearFilter: { all: 'All', usable: 'Usable', in_experience: 'In session', charging: 'Charging', offline: 'Offline', fault: 'Fault', low: 'Low battery' },
    gearNoMatch: 'No device matches this search.',
    openRun: 'Open a session',
    start: 'Start',
    pause: 'Pause',
    resume: 'Resume',
    end: 'End',
    abort: 'Abort',
    pending: 'Waiting',
    briefing: 'Briefing',
    running: 'Running',
    paused: 'Paused',
    ended: 'Ended',
    aborted: 'Aborted',
    delivered: 'Room acknowledged',
    unlinked: 'not linked',
    config: 'Remote config',
    scenes: 'Scenes',
    schemaMissing: 'The studio has not published any tunable parameter for this experience yet.',
    venueDefault: 'Default for this venue',
    studioDefault: 'Studio default',
    forSegment: 'For this ticket',
    reset: 'Back to default',
    savedReload: 'Saved — the room has been asked to reload.',
    sceneReport: 'Time spent per scene',
    sceneHint: 'Median time in each scene, crossed with the ticket the visitor holds. That is what tells you which scene to give more time to, and to whom.',
    visits: 'visits',
    median: 'median',
    noScenes: 'No scene telemetry yet.',
    actInRoom: 'In the experience',
    actWorn: 'On a head',
    actOffHead: 'Put down',
    rooms: 'Rooms',
    roomOnline: 'Server online',
    roomOffline: 'Server silent',
    roomUnassigned: 'No server',
    roomHeadsets: 'headsets',
    roomName: 'Room name',
    newRoom: 'Add a room',
    roomOf: 'Room',
    noRoom: 'No room',
    studioOnly: 'Studio only',
    settings: 'Settings',
    settingsOf: 'Settings — {name}',
    temperature: 'Temperature',
    temperatureNone: 'Not reported',
    headroomOf: '{n} % of the throttling threshold',
    throttlesAt: 'throttles at {t}',
    shutsDownAt: 'shuts down at {t}',
    /* « Jamais annoncé » et « rien annoncé » sont deux textes, parce que ce
       sont deux constats : l'un envoie allumer le casque, l'autre non. */
    capsUnknown: 'Not announced yet',
    capsUnknownHint: 'This headset has never reported in. Its settings appear once the experience server has seen it.',
    capsNone: 'No remote settings',
    capsNoneHint: 'The headset reported in and announces no setting it can apply. Its build may predate the device channel.',
    noServerHolds: 'No server holds this headset',
    noServerHoldsHint: 'It is on, but no experience server reports it. Start the experience on its room, or put it back on its shelf charger.',
    sending: 'Sending…',
    readAt: 'read on the headset at {time}',
    ago: '{age} ago',
    ageMin: 'min',
    ageHour: 'h',
    ageDay: 'd',
    /* « Pas encore lu » et « jamais lisible » sont DEUX phrases, parce que
       l'une envoie attendre le prochain battement et l'autre non. */
    notReadYet: 'not read yet',
    neverReadable: 'the room never reports this one back',
    requestedValue: 'last asked for: {value}, at {time}',
    setValue: 'Set',
    /* Le flux d'un casque. Trois refus DIFFÉRENTS, parce qu'ils appellent
       trois gestes différents — et un cadre noir n'en appelle aucun. */
    stream: 'Live view',
    streamOffline: 'The headset is off or unreachable. Its live view comes back when it reports in.',
    streamNotOnLan: 'A live view only plays from inside the venue network. You are connected from outside it.',
    streamNoSource: 'This headset does not publish a live view. Its room build does not offer one yet.',
    streamStopped: 'The live view is off on this headset.',
    streamNoAddress: 'The headset is broadcasting but sent no address. Its room may be part-way through an update — or the headset itself is failing to hand one over.',
    streamError: 'The broadcasting service has failed on this headset: {reason}. Restarting it will not help until the service is back.',
    streamUnverified: 'Reached at its recorded address. The headset does not report whether it is broadcasting, so this frame may be its waiting screen.',
    settingBroken: 'could not be read: {reason}',
    streamReload: 'Reload',
    asleepTitle: 'Asleep since {time}',
    asleepHint: 'The headset answers on the network, but its application is suspended (taken off, screen off): it would refuse any command. Put it on, or press its power button, to wake it.',
    asleepRetry: 'Try anyway',
    asleep: 'Asleep',
    asleepShort: 'Asleep: wake the headset first',
    captureTitle: 'Screenshot — {time}',
    captureDownload: 'Download',
    captureBack: 'Back to live',
    captureKept: 'Kept on Portage Room only, deleted after a week.',
    captureInRoom: 'Screenshot taken. It stays in the room: view it on Portage Room.',
    captureMissing: 'The headset took the screenshot, but the image did not reach Portage Room.',
    streams: 'Live views',
    unverified: 'unconfirmed',
    streamsOnline: '{n} of {total} broadcasting',
    streamsHidden: 'show {n} hidden',
    streamCrop: 'Crop',
    streamMaximise: 'Fill',
    streamRestore: 'Back',
    streamHide: 'Hide',
    wallEnter: 'Wall mode',
    wallLeave: 'Leave wall',
    discover: 'Scan the network',
    discoverHint: 'Looks for headsets answering on the venue network and writes the address of each one it can name. Only from Portage Room — the cloud is not on your network.',
    discoverCidr: 'Address block to scan',
    discoverRun: 'Scan',
    discoverMatched: '{n} headsets named and updated',
    discoverUnknown: '{n} answered without saying who they are',
    discoverNothing: 'Nothing answered on that block.',
    discoverNotRoom: 'Only Portage Room can scan: the cloud is not on the venue network.',
    discoverTooWide: 'That block is too large. A venue network is a /24 — /22 is the widest accepted.',
    discoverBadCidr: 'Write a block such as 10.30.30.0/24.',
    streamsNoHeadset: 'No headset in this venue yet.',
    applyFailed: 'Refused: {reason}',
    duringRun: 'Not while a group is in the experience',
    confirmTitle: 'Apply now?',
    confirmBody: 'Someone is in the experience with this headset. {name} takes effect immediately.',
    allowed: 'allowed',
    blocked: 'blocked',
    apply: 'Apply',
    run: 'Run',
    setVolume: 'Volume',
    setBrightness: 'Brightness',
    setSleep: 'Allow sleep',
    setIpdAuto: 'Automatic IPD',
    setIpd: 'IPD',
    setCapture: 'Screen capture',
    setRecenter: 'Recenter view',
    setPassthrough: 'Passthrough',
    setKiosk: 'Kiosk mode',
    setCast: 'Live view',
    setLanguage: 'Language',
    turnOn: 'Turn on',
    turnOff: 'Turn off',
    /* Les codes d'échec que la salle renvoie. Traduits ICI plutôt qu'affichés
       bruts : le motif humain arrive du réseau et échapperait au typage. */
    failUnsupported: 'the headset does not support it',
    failNotBound: 'enterprise service not linked',
    failPermission: 'permission missing on the headset',
    failInvalidValue: 'value not understood',
    failOutOfRange: 'value out of range',
    failOffline: 'headset unreachable',
    failTimeout: 'no answer from the headset',
    failNoDevice: 'headset unknown to the room',
    failDeviceError: 'the headset refused',
    failPartial: 'applied to some headsets only',
  },
  customers: {
    title: 'Customers',
    signatures: 'Registrations signed',
    noSignatures: 'Nothing signed yet',
    noSignaturesHint: 'Waivers and NDAs signed by this household will show up here, with the flow and the date.',
    invalidate: 'Invalidate',
    invalidateTitle: 'Invalidate this registration?',
    invalidateBody: '{name} signed {flow} on {date}. Deleting it removes the signed text and asks them to fill the flow again on their next visit.',
    invalidateKeeps: 'The booking, the ticket and the customer record are untouched.',
    erase: 'Erase this visitor',
    eraseTitle: 'Erase this visitor’s data?',
    eraseBody:
      'Clears {name}’s name, email and phone from the customer record, the household, the bookings and the room history. Counters, amounts and dates are kept, so the figures do not move.',
    eraseKeeps:
      'Signed waivers are kept whole — they are binding documents — but unlinked from everything else. They are the only place the name remains. This cannot be undone.',
    erased: 'Visitor anonymised. Waivers kept, unlinked.',
    segment: 'Segment',
    allContacts: 'All contacts',
    bookings: 'Bookings',
    visits: 'Visits',
    lifetimeValue: 'Lifetime value',
    lastVisit: 'Last visit',
    tags: 'Tags',
    notes: 'Internal note',
    notesHint: 'Only your team sees this. Saved when you click away.',
    history: 'Booking history',
    since: 'since',
    noHistory: 'No booking yet.',
    unsubscribeAction: 'Unsubscribe them',
    subscribed: 'Subscribed',
    unsubscribed: 'Unsubscribed',
    emailColumn: 'Emails',
    pendingWaivers: 'Pending waivers',
    pendingWaiversHint: 'Signed, but tied to no booking yet — usually from the wall QR, before booking or under another email. They attach on their own as soon as a booking matches.',
    minor: 'Minor',
    channelKiosk: 'Tablet',
    channelLink: 'Link',
    channelQr: 'Wall QR',
    channelCounter: 'Counter',
    noConsent: 'No consent',
    export: 'Export CSV',
    exportNote: 'Your contacts, yours to take. Full file, no limit.',
    searchPlaceholder: 'Name, email, phone',
    noResults: 'No contacts match.',
    newContact: 'New contact',
    newContactHint: 'A contact does not need a booking. If they book later, it lands on this same record.',
    createContact: 'Create the contact',
    email: 'Email',
    firstName: 'First name',
    lastName: 'Last name',
    optInLabel: 'They agreed to receive email',
    optInHint: 'Only tick this if they said so. Without it the contact exists but no campaign reaches them.',
    knownAt: 'Known at',
    sourceBooking: 'Booked',
    sourceOnboarding: 'Registered on site',
    sourceLead: 'Left their email',
    sourceManual: 'Added by the team',
    sourceImport: 'Imported',
    notKnownHere: 'Not one of this venue’s contacts — campaigns here will not reach them.',
  },
    retention: {
      title: 'Retention review',
      backlog: '{n} records · oldest {days} day(s) late',
      hint: 'Nothing is ever deleted automatically. These records are past their retention period and need a decision.',
      waivers: 'Signed waivers past retention',
      customers: 'Customer records with no activity',
      overdue: '{days} day(s) late',
      deleteWaivers: 'Delete {n} waiver(s)',
      anonymiseCustomers: 'Anonymise {n} record(s)',
      confirmWaivers: 'Delete these waivers?',
      confirmWaiversBody:
        'A waiver is a binding document and the only place a name remains after anonymisation. Deleting it is final.',
      confirmCustomers: 'Anonymise these records?',
      confirmCustomersBody:
        'Names, emails and phone numbers are cleared. Counters, amounts and dates are kept, so the figures do not move.',
      done: '{waivers} waiver(s) deleted, {customers} record(s) anonymised.',
    },
  experiences: {
    title: 'Experiences',
    duration: 'Duration',
    capacity: 'Capacity',
    published: 'On sale',
    draft: 'Not on sale',
    upcomingSessions: 'upcoming sessions',
    ticketTypes: 'Ticket types',
    schedules: 'Schedules',
    noExperiences: 'No experiences yet.',
  },
} as const

/** Élargit les littéraux de `en` pour que `fr` n'ait pas à les répéter. */
type Widen<T> = T extends string ? string : { [K in keyof T]: Widen<T[K]> }

export const fr: Widen<typeof en> = {
  app: {
    name: 'Portage',
    loading: 'Chargement…',
    error: 'Quelque chose a échoué.',
    retry: 'Réessayer',
    cancel: 'Annuler',
    chooseFile: 'Choisir un fichier',
    noFile: 'Aucun fichier choisi',
    save: 'Enregistrer',
    delete: 'Supprimer',
    refresh: 'Rafraîchir',
    close: 'Fermer',
    confirm: 'Confirmer',
    search: 'Rechercher',
    today: "Aujourd'hui",
    all: 'Tout',
    none: 'Aucun',
    filters: 'Filtres',
    results: 'résultats',
    of: 'sur',
    forbidden: "Votre rôle ne vous donne pas accès à ceci. Demandez à un propriétaire ou à un gestionnaire.",
  },
  theme: {
    label: 'Thème',
    system: 'Selon le système',
    light: 'Clair',
    dark: 'Sombre',
  },
  auth: {
    signIn: 'Se connecter',
    email: 'Courriel',
    password: 'Mot de passe',
    signOut: 'Se déconnecter',
    invalidCredentials: 'Ce courriel et ce mot de passe ne concordent pas.',
    notInVenueTeam: 'Votre mot de passe est bon, mais votre compte n’est pas dans l’équipe de ce lieu. Demandez à quelqu’un qui gère l’équipe de vous ajouter (Équipe → Postes), puis réessayez quelques secondes plus tard.',
    sessionRevoked: 'Le mot de passe de ce compte a changé. Reconnectez-vous.',
    forgot: 'Mot de passe oublié ?',
    forgotHint: 'Nous vous envoyons un lien pour en choisir un nouveau.',
    forgotSend: 'Envoyer le lien',
    forgotSent: 'Regardez votre boîte',
    forgotSentHint:
      'Si cette adresse a un compte, un lien est parti. Il expire dans une heure. Rien ne change tant que vous ne l’utilisez pas.',
    backToSignIn: 'Revenir à la connexion',
    choosePassword: 'Choisir un nouveau mot de passe',
    newPassword: 'Nouveau mot de passe',
    newPasswordHint: 'Dix caractères au minimum.',
    tooShort: 'Dix caractères au minimum.',
    linkExpired: 'Ce lien a expiré ou a déjà servi. Demandez-en un nouveau.',
    tagline: 'Le back-office qui porte la charge.',
    madeWith: 'Fait avec',
    by: 'par',
  },
  platform: {
    wrong: 'Ce qui demande une décision',
    allGood: 'Rien à régler',
    allGoodHint: 'Tous les casques donnent signe de vie, tous les écrans ont appelé, toute expérience publiée est achetable.',
    fleet: 'Le parc',
    devices: 'Appareils',
    deployed: 'Déployés',
    inCase: 'En caisse',
    silent: 'Muets',
    silentShort: 'muets',
    byVenue: 'Où ils sont',
    catalogue: 'Ce qui tourne où',
    media: 'briefings',
    noMedia: 'Aucun briefing',
    upcoming: 'à venir',
    noPrice: 'sans tarif',
    noSession: 'sans créneau',
    draft: 'En préparation',
    statesTitle: 'Ce que veulent dire ces trois états',
    stateLive: 'Vendable',
    stateLiveHint: 'Publiée, avec un tarif et un départ à venir. Sa page publique est en ligne et le tunnel de réservation fonctionne.',
    stateStuck: 'En ligne mais bloquée',
    stateStuckHint: 'Publiée sans tarif, ou sans créneau à venir : la page est visible et rien ne peut y être acheté. À régler tout de suite.',
    stateDraftHint: 'Pas publiée. Sa page publique est hors ligne — et rien d’autre n’est touché : la vente au comptoir, le scan, l’enregistrement et l’exploitation fonctionnent, et les billets vendus ailleurs restent valables.',
    statesFoot: 'Une instance naît non publiée volontairement : sans tarif ni créneau, la publier mettrait en ligne une page visible et inachetable.',
    noTitles: 'Aucune expérience déployée',
    noTitlesHint: 'Glissez une expérience sur un lieu, dans Distribution, pour l’y ajouter.',
    alerts: {
      devices_offline: 'casques ne donnent plus signe de vie',
      devices_unclaimed: 'appareils n’appartiennent à personne',
      screens_never_seen: 'écrans n’ont jamais appelé',
      deliveries_failed: 'inscriptions chez un partenaire ont échoué',
      venues_orphan: 'lieux n’ont pas d’entité du studio',
      instances_unsellable: 'expériences publiées ne sont pas achetables',
      titles_without_media: 'expériences n’ont pas de briefing',
    },
  },
  planning: {
    title: 'Planning',
    autofill: 'Remplir la semaine',
    publish: 'Publier',
    publishTitle: 'Publier cette semaine ?',
    publishBody: '{n} quarts en brouillon deviennent visibles, et chaque personne concernée reçoit le sien par courriel. Ce qui est déjà publié ne bouge pas.',
    hours: 'Heures',
    clashOne: 'chevauchement',
    clashes: 'chevauchements',
    clashWith: 'Également au planning de {venue}, {from} – {to}. On n’est pas à deux endroits à la fois — sauf à passer de l’un à l’autre.',
    addShift: 'Ajouter un quart',
    removeShift: 'Retirer ce quart',
    from: 'De',
    to: 'À',
    shiftHint: 'La personne est affectée à tous les créneaux qui commencent dans cette plage. Un créneau ajouté plus tard y est rattrapé.',
    noTeam: 'Personne ne travaille encore dans ce lieu',
    noTeamHint: 'Ajoutez quelqu’un dans l’onglet Postes, juste à côté — un planning a besoin de gens avant d’avoir besoin d’heures.',
    availability: 'Disponibilités',
    addRule: 'Ajouter une fenêtre',
    ruleKind: 'Type',
    kindAvailable: 'Disponible',
    kindUnavailable: 'Absent',
    ruleMode: 'Répétition',
    modeWeekly: 'Chaque semaine',
    modeDates: 'Dates précises',
    weekday: 'Jour',
    ruleNote: 'Motif',
    noRules: 'Aucune disponibilité déclarée',
    noRulesHint: 'Déclarez qui travaille quels jours, et « Remplir la semaine » proposera un planning que vous ajusterez. L’absence l’emporte sur la disponibilité — des vacances gagnent toujours sur un jour habituel.',
    mine: 'Mon planning',
    mineEmpty: 'Aucun quart à venir',
    mineEmptyHint: 'Votre planning apparaît ici dès que le lieu le publie.',
    sessionsCovered: 'séances',
  },
  board: {
    roomCut: 'Aucun contact avec le nuage',
    roomNeverSynced: 'jamais synchronisé',
    roomStillWorks: 'Le tableau, les casques et la séance en cours continuent. Tout remontera au retour du lien.',
    roomRefused: '{n} refusés par le nuage',
    roomPending: '{n} geste(s) en attente de remontée',
    xroamDown: 'Cette expérience tourne sur XROAM et ne peut pas démarrer sans le lien.',
    xroamNext: 'Notez la personne et replacez-la, ou proposez une expérience qui tourne encore.',
    pilotedFromRoom: 'Piloté depuis la salle',
    lastContact: 'dernier contact il y a {age}',
    takeOver: 'Reprendre la main',
    groupWord: 'Groupe',
    totalTime: 'Temps passé dans l’expérience, de l’entrée en salle à la fin',
    totalTimeShort: 'Total',
    householdWord: 'Foyer {name}',
    changeHeadset: 'Changer',
    changeHeadsetHint: 'Donner un autre casque à cette personne. L’ancien est libéré chez le moteur, le nouveau lié.',
    changeHeadsetLong: 'Changer de casque',
    changeHeadsetFor: 'Un autre casque pour {name}',
    slotHint: 'Créneau du billet',
    experienceBand: 'Expérience : {name}',
    householdPlain: 'Venus ensemble',
    selectStart: 'Grouper',
    selectDone: 'Terminé',
    selectHint: 'Touchez les cartes qui voyagent ensemble.',
    selectCount: '{n} sélectionnée(s)',
    groupMixed: 'Ces cartes appartiennent à deux expériences différentes.',
    groupNotSupported: 'Cette expérience ne se joue pas en groupe — chacun entre quand son casque est prêt.',
    groupTooLarge: 'Cette expérience admet au plus {n} personnes à la fois.',
    groupThem: 'Les grouper',
    ungroupThem: 'Dégrouper',
    grouped: 'Cartes groupées.',
    ungrouped: 'Cartes dégroupées.',
    groupWaitingTitle: 'Un membre attend encore',
    groupWaitingBody: 'Quelqu’un de ce groupe n’a pas encore de casque. Le faire entrer seul le laisse de l’autre côté sans les siens.',
    groupWaitingGo: 'Faire entrer quand même',
    title: 'Tableau',
    languageSent: 'Langue demandée : {language}.',
    groupLanguage: 'Langue du groupe',
    empty: 'Personne sur place',
    emptyHint: 'Une carte apparaît ici dès qu’un billet est scanné — une par personne, pas une par réservation.',
    here: 'ici depuis {n} min',
    pair: 'Appairer un casque',
    unpair: 'relâcher',
    start: 'Démarré',
    finish: 'Terminé ✓',
    noDevice: 'Aucun casque libre — ils sont tous dehors ou au nettoyage.',
    otherDevice: 'un autre ({n})',
    silent: 'muet',
    overtime: 'dépassé',
    tuneHeadset: 'Réglages du casque',
    headsetSettings: 'Paramètres du casque',
    volume: 'Volume',
    ipd: 'Écart pupillaire',
    ipdAsked: 'Dernier écart pupillaire demandé — le casque ne le rapporte pas',
    notWorn: 'Le casque n’est pas porté',
    silentClosing: 'Casque muet depuis {n} min · fin automatique dans {left} min',
    enginePairedHint: 'Appairée par le moteur (son kiosque a lié cette personne au casque), et non par un geste.',
    engineLinked: 'Liée au casque {n} chez {engine} par Portage — rien à faire dans son app.',
    engineWaiting: 'Sera liée chez {engine} dès qu’il aura inscrit la personne.',
    engineErr: 'Pas liée chez {engine} :',
    engineErrNumber: 'Portage ne connaît pas le numéro {engine} de ce casque.',
    engineErrDevice: 'le casque {n} n’existe pas chez {engine}.',
    engineErrSetup: 'l’Operator App {engine} n’est pas réglée pour ce lieu.',
    engineErrRefused: '{engine} a refusé ou n’a pas répondu ; Portage réessaie chaque minute.',
    engineCreatedHint: 'Créée par le moteur : Portage ne connaît pas cet utilisateur — ni enregistrement, ni décharge, ni consentement chez nous.',
    engineOutside: 'hors Portage',
    unregistered: 'Sans enregistrement',
    lobby: 'Lobby',
    unregisteredWorn: 'porté sans enregistrement',
    attach: 'Rattacher à une carte…',
    attachTitle: 'Rattacher {name} à une carte',
    attachHint: 'La personne était enregistrée, mais son casque n’avait pas été appairé. Sa carte reprend le casque et entre en salle à l’heure où la visite a vraiment commencé ; la carte « {name} » disparaît.',
    attachNone: 'Aucune carte n’attend de casque pour cette expérience.',
    attachDo: 'Rattacher',
    attachRegistered: 'enregistrée',
    attachArrived: 'arrivée',
    unregisteredHint: 'Ce casque a été lancé sans que personne passe par l’enregistrement : ni décharge ni consentement au dossier. La carte porte le nom du casque, comme dans XROAM, et se termine d’elle-même à la fin de la visite. Le casque n’est pas proposé à l’appairage entre-temps.',
    noImage: 'Pas d’image',
    imageYes: 'Image autorisée',
    imageYesHint: 'Le lieu tourne et cette personne a autorisé l’utilisation de son image.',
    menu: 'Plus d’actions',
    devIgnoredOne: '{headset} annonce un visiteur de dev ({name}) : ignoré, ce lieu n’est pas un bac à sable.',
    devIgnoredMany: '{n} casques annoncent un visiteur de dev, ignorés — ce lieu n’est pas un bac à sable : {list}.',
    devIgnoredHint: 'Pour tester ici, passez le lieu en bac à sable (console du studio), ou créez la carte à la main.',
    noImageHint: 'Le lieu tourne et cette personne n’a pas autorisé l’utilisation de son image : ne pas la filmer, ou la flouter.',
    silentClosed: 'Terminée automatiquement : le casque s’est tu',
    finishing: 'Fin de l’expérience : casque retiré, la carte passe en Terminé dans la minute',
    finishingManual: 'Fin de l’expérience : casque retiré, à passer en Terminé',
    storageLowHint: 'Espace disque libre du casque, sous le seuil du lieu. Libérer de la place avant qu’il ne manque.',
    sceneOverrun: '{scene} : {elapsed} min, {expected} prévues',
    deviceQuiet: 'Le casque ne répond plus',
    deviceLost: 'Casque injoignable',
    tooHot: 'Casque sur le point de s’éteindre — trop chaud',
    throttling: 'Casque bridé par la chaleur — l’image va saccader',
    thermalAndroid: 'puce : état Android {level}/6, {headroom} % du seuil de bridage',
    unplayable: 'Trop chaud pour tenir la cadence VR — sortez la personne avant la nausée',
    tempUnknown: 'Aucun casque ne rapporte encore de température — rien n’est caché.',
    headroomHint:
      'Où en est le casque sur le chemin du bridage thermique, 100 % étant le seuil. Ce casque ne rapporte pas de degrés — l’API Android qui en donne demande une permission système.',
    batteryFlat: 'Batterie sur le point de lâcher',
    autoStarts: 'Démarre seule',
    autoStartsHint: 'Cette expérience démarre quand le groupe est au complet.',
    helpTitle: 'Signaler qu’on a dû aider',
    helpHeading: 'Aidé {name}',
    helpBody:
      'Laisse une marque sur la carte pour celui qui prend le relais, et part au studio avec l’état du casque et le chapitre où en est la personne.',
    helpCategory: 'Ce qui est arrivé',
    helpCategories: {
      comfort: 'Malaise ou inconfort',
      lost: 'Bloqué ou perdu dans l’expérience',
      fitting: 'Ajustement du casque',
      tracking: 'Suivi, position, recentrage',
      audio_visual: 'Son ou image',
      freeze: 'Gel ou écran noir (redémarré)',
      app_crash: 'L’application a planté',
      session_restart: 'Session relancée',
      headset_swap: 'Casque remplacé',
      battery: 'Batterie ou charge',
      avatars: 'Avatars désynchronisés',
      network: 'Réseau ou serveur',
      other: 'Autre',
    } as Record<string, string>,
    helpHistory: 'Déjà aidée sur cette carte',
    helpReason: 'Ce qui s’est passé (facultatif)',
    helpReasonHint: 'Casque mal ajusté, visiteur apeuré, scène bloquée…',
    helpConfirm: 'Enregistrer',
    helpDone: 'Intervention enregistrée',
    remove: 'Sortir du parcours',
    removeTitle: 'Sortir {name} ?',
    removeBody:
      'La carte quitte le tableau et le casque retourne au bac. Rien n’est supprimé : les temps déjà mesurés sont conservés, et rescanner le billet ne la fera pas revenir.',
    removeReason: 'Pourquoi (facultatif)',
    removeReasonHint: 'Casque en panne, visiteur qui renonce, carte ouverte par erreur…',
    removeConfirm: 'Sortir',
    allExperiences: 'Toutes les expériences',
    stages: {
      arrived: 'Arrivé',
      registered: 'Enregistré',
      paired: 'Appairé',
      in_room: 'En salle',
      done: 'Terminé',
    },
  },
  toast: {
    saved: 'Enregistré.',
    deleted: 'Supprimé.',
    sent: 'Envoyé.',
    failed: 'L’action n’a pas abouti.',
    visitRemoved: '{name} est sorti du parcours.',
    visitAttached: 'La visite de {device} est rattachée à {name}.',
    visitRemovedHeadset: 'Le casque est retourné au bac.',
    published: 'Planning publié — l’équipe a reçu son courriel.',
    filled: '{n} garde(s) proposée(s). Rien n’est encore publié.',
    revoked: 'Accès révoqué.',
    activated: 'Activé.',
    deactivated: 'Désactivé.',
    duplicated: 'Copie créée.',
    imported: '{n} contact(s) importé(s).',
    screenSent: 'Envoyé à l’écran.',
    assigned: 'Affecté.',
    unassigned: 'Retiré.',
  },
  orphans: {
    title: 'Fichiers que plus rien ne réclame',
    banner: '{n} fichier(s) que plus rien ne réclame ({size}) — supprimé(s) automatiquement le {when}.',
    bannerBlocked: '{n} fichier(s) que plus rien ne réclame ({size}) — trop pour la purge de nuit, qui n’y touche pas : vérifiez-les, puis supprimez-les à la main.',
    show: 'Voir le détail',
    hide: 'Masquer',
    purgeOn: 'Supprimé le',
    purgeNow: 'Supprimer maintenant',
    hint: 'Supprimer une expérience emporte ses lignes de médias et laisse les fichiers. Ceux-ci sont sur le volume et plus rien ne les désigne ; la purge de nuit les efface après trois jours d’affilée sans être réclamés.',
    weight: '{size} embarqués par chaque sauvegarde — {copies} archives dans la fenêtre de rétention, soit {total} de stockage et de téléversement.',
    file: 'Fichier',
    size: 'Poids',
    age: 'Sur le disque',
    days: '{n} j',
    purge: 'Les supprimer',
    purged: '{n} fichier(s) supprimé(s).',
    confirmTitle: 'Supprimer {n} fichier(s) ?',
    confirmBody:
      'Ils quittent le disque définitivement — {size} récupérés. Rien en base ne les désigne, et le serveur le revérifie avant d’effacer. Une sauvegarde antérieure à aujourd’hui les contient encore.',
  },
  legal: {
    title: 'Conditions et confidentialité',
    hint: 'Affichés à la fin de l’enregistrement, à côté de ceux du lieu et du partenaire. Une adresse par langue — un consentement donné sur un texte qu’on ne lit pas n’en est pas un.',
    terms: 'Conditions',
    privacy: 'Confidentialité',
  },
  maps: {
    title: 'Cartes d’espace',
    hint: 'Relevez la salle avec UN casque, puis poussez le fichier sur les autres. Un relevé par casque donne des cartes légèrement différentes, donc des visiteurs qui ne se voient pas au même endroit.',
    empty: 'Aucune carte déposée.',
    file: 'Fichier (.zip)',
    name: 'Nom',
    platform: 'Relevée sur',
    anyPlatform: 'Toutes plateformes',
    store: 'Déposer',
    storing: 'Dépôt…',
    stored: 'Carte déposée',
    target: 'Pousser vers',
    pickGroup: 'Choisir un groupe…',
    push: 'Pousser',
    pushing: 'Envoi…',
    queued: 'Déposée sur {n} casques — l’installation prend quelques minutes',
    noneInRoom: 'Aucune carte pour cette salle. Relevez la salle avec un casque, exportez la carte, et déposez-la ici.',
    history: '{n} version(s) précédente(s)',
    newVersion: 'Déposer un nouveau relevé',
    firstMap: 'Déposer le relevé de la salle',
    noneQueued: 'Aucun casque de cette salle ne peut prendre cette carte (autre marque, ou ne sait pas poser de carte).',
    skipped: '{n} écarté(s)',
    status: 'en service sur {ok} · en attente {pending} · refusée {failed}',
    notInCloudYet: 'pas encore dans Portage Cloud',
    pushRoom: 'Poser sur les casques de la salle',
    pushAgain: 'Reposer cette version',
    fileHint: 'Le .zip qu’exporte le casque (Pico : carte, marqueurs, limites). Un fichier par marque.',
    studioHint: 'Les cartes appartiennent aux salles : elles se déposent et se posent depuis la salle (Portage Room, ou Salles dans Portage Cloud). En lecture ici, pour le support.',
    where: 'Lieu · salle',
    date: 'Déposée',
    download: 'Télécharger',
    why: {
      platform_mismatch: 'autre plateforme',
      device_not_placed: 'pas encore dans un lieu',
      no_server: 'aucun serveur de l’expérience pour la porter',
    },
  },
  map: {
    title: 'Carte',
    heatNone: 'Personnes seules',
    heatLive: 'Chaleur en direct',
    heatDay: 'Journée',
    heatLiveHint: 'Combien de personnes par case de 50 cm en ce moment, lissé — comme la simulation de Creator.',
    heatDayHint: 'Temps passé par case de 50 cm depuis le début de la journée ({day}) — rouge = les cases les plus fréquentées.',
    heatDayTotal: '{h} h de présence cumulées.',
    heatShow: 'Afficher la chaleur',
    heatHide: 'Masquer la chaleur',
    heatOpacity: 'Opacité',
    noPlan: 'Pas encore de plan',
    noPlanHint:
      'Le plan s’importe depuis Creator, par le studio (outil MCP set_floor_plan). Il arrive ici à la synchronisation suivante.',
    feedNever: 'Aucune position reçue',
    feedNeverHint: 'Le serveur d’expérience n’envoie pas de positions sur ce réseau.',
    feedStopped: 'Flux de positions interrompu',
    feedStoppedHint:
      'Les points sont retirés volontairement : montrer où les gens étaient il y a une minute vous enverrait au mauvais endroit.',
    onFloor: 'au sol',
    scale: '2 m',
    count: '{n} sur le plan',
    adrift: '{n} hors du plan',
    adriftHint:
      'Leur position tombe en dehors du plan — presque toujours un casque qui n’a pas été recalé sur la station de référence, pas un visiteur dans un mur.',
    unaligned: '{n} recalage perdu',
    unalignedHint:
      'Ces casques ne tiennent plus le repère — soit la salle le signale, soit leur position a sauté plus vite qu’un corps humain ne se déplace. Presque toujours un visiteur qui a tenu le bouton système enfoncé. La position paraît plausible et elle est fausse, donc on ne les dessine pas. À recentrer.',
  },
  nav: {
    platform: 'Plateforme',
    backTo: 'Revenir à {name}',
    backToVenue: 'Revenir au lieu',
    more: 'Plus d’entrées plus bas',
    venueLabel: 'Lieu',
    collapse: 'Replier le menu',
    expand: 'Déplier le menu',
    dashboard: 'Tableau de bord',
    today: "Aujourd'hui",
    calendar: 'Calendrier',
    bookings: 'Réservations',
    customers: 'Clients',
    experiences: 'Expériences',
    fleet: 'Casques',
    rooms: 'Salles',
    welcome: 'Accueil',
    staff: 'Équipe',
    settings: 'Réglages',
    groupToday: 'La journée',
    analytics: 'Analytique',
    groupSell: 'Vendre',
    groupSetup: 'Configurer',
  },
  dashboard: {
    title: "Aujourd'hui",
    occupancy: 'Remplissage',
    trend: '30 derniers jours',
    vsPrevious: 'contre les 15 jours précédents',
    seatsSold: 'Places vendues',
    revenue: 'Recettes du jour',
    upcomingWeek: 'Sept prochains jours',
    needsAttention: 'À traiter',
    allClear: "Rien ne vous attend pour l'instant.",
    onDuty: 'En poste',
    noSessions: "Aucun créneau aujourd'hui.",
    sessionsToday: 'Créneaux du jour',
    alertStaffMissing: 'Aucun opérateur affecté',
    alertBelowMinimum: 'Sous le minimum, départ imminent',
    alertCheckInIncomplete: 'Pointage incomplet',
  },
  calendar: {
    title: 'Calendrier',
    day: 'Jour',
    week: 'Semaine',
    month: 'Mois',
    list: 'Liste',
    allExperiences: 'Toutes les expériences',
    seatsLeft: 'libres',
    full: 'Complet',
    empty: 'Vide',
    cancelled: 'Annulé',
    closed: 'Fermé',
    private: 'Privé',
    noSessions: 'Aucun créneau sur cette période.',
    previous: 'Précédent',
    next: 'Suivant',
  },
  session: {
    time: 'Heure',
    unassign: 'Retirer de ce créneau',
    staffMissing: 'il en manque {count}',
    roster: 'Liste des visiteurs',
    capacity: 'Capacité',
    whereWeAre: 'Où on en est',
    group: 'Le groupe',
    settings: 'Configuration',
    arrived: 'Arrivées',
    inExperience: 'En salle',
    notStarted: 'Pas démarré',
    serverUpFor: 'Serveur en service depuis',
    inferredVisit: 'visite déduite de l’état de la salle',
    runStates: {
      pending: 'En attente',
      briefing: 'Briefing',
      running: 'En cours',
      paused: 'En pause',
      ended: 'Terminée',
      aborted: 'Interrompue',
    },
    sold: 'Vendues',
    held: 'Retenues',
    available: 'Libres',
    walkIn: 'Sans billet',
    walkInToggle: 'On entre sans réservation',
    walkInHint:
      'Pour un playtest, une visite de presse ou un rodage. L’enregistrement cesse de réclamer une référence de réservation, et l’expérience reçoit quand même ses inscriptions.',
    guests: 'Visiteurs',
    staff: 'Opérateurs',
    assignGuide: 'Affecter un opérateur',
    noTeamHint: 'Personne ne travaille encore dans ce lieu. Ajoutez quelqu’un dans Équipe › Postes, puis affectez-le ici.',
    noGuests: 'Personne pour le moment.',
    checkIn: 'Pointer',
    checkedIn: 'Arrivé',
    noShow: 'Absent',
    internalNote: 'Note interne',
    addBooking: 'Ajouter une réservation',
    cancelSession: 'Annuler le créneau',
    changeCapacity: 'Changer la capacité',
  },
  booking: {
    reference: 'Référence',
    status: 'Statut',
    source: 'Origine',
    seats: 'Places',
    total: 'Total',
    contact: 'Contact',
    bookedOn: 'Réservé le',
    pending: 'En attente',
    confirmed: 'Confirmée',
    cancelled: 'Annulée',
    expired: 'Expirée',
    checkedIn: 'Arrivé',
    noShow: 'Absent',
    online: 'En ligne',
    counter: 'Comptoir',
    phone: 'Téléphone',
    import: 'Importée',
    partner: 'Partenaire',
    cancelBooking: 'Annuler la réservation',
    departure: 'Départ',
    searchPlaceholder: 'Référence, nom, courriel, téléphone',
    noResults: 'Aucune réservation ne correspond.',
  },
  counter: {
    title: 'Vendre au comptoir',
    /* Le libellé COURT de la barre du haut : deux gestes côte à côte, le titre
       complet y prendrait la moitié de la largeur. */
    action: 'Vendre',
    chooseSession: 'Choisir un créneau',
    tickets: 'Billets',
    customer: 'Client',
    payment: 'Paiement',
    method: 'Mode',
    card: 'Carte',
    cash: 'Comptant',
    check: 'Chèque',
    invoice: 'Facture',
    other: 'Autre',
    collectNow: 'Encaisser maintenant',
    payLater: 'Retenir sans encaisser',
    marketingOptIn: 'Accepte de recevoir les prochaines annonces',
    complete: 'Conclure la vente',
    sold: 'Vendu.',
  },
  campaigns: {
    title: 'Campagnes',
    status: 'Statut',
    journeys: 'Parcours',
    newCampaign: 'Nouvelle campagne',
    name: 'Nom de la campagne',
    draft: 'Brouillon',
    scheduled: 'Programmée',
    sending: 'Envoi en cours',
    sent: 'Envoyée',
    failed: 'Échec',
    audience: 'Destinataires',
    audienceAll: 'Tous les contacts abonnés',
    exclude: 'Ne pas envoyer à',
    subject: 'Objet',
    content: 'Contenu',
    review: 'Vérification',
    recipients: 'Destinataires',
    opened: 'Ouvertures',
    clicked: 'Clics',
    revenue: 'Revenu',
    attributedBookings: 'Réservations',
    send: 'Envoyer',
    sendConfirm: 'Envoyer à {count} contacts ?',
    locked: 'Une campagne envoyée ne se modifie plus.',
    noCampaigns: 'Aucune campagne pour l’instant.',
    addBlock: 'Ajouter',
    blockHeading: 'Titre',
    blockText: 'Paragraphe',
    blockButton: 'Bouton',
    blockImage: 'Image',
    blockDivider: 'Séparateur',
    blockExperience: 'Expérience',
    blockExperienceHint: 'Rempli depuis la fiche de l’expérience à l’envoi',
    preview: 'Aperçu',
    remove: 'Retirer',
    attributionNote: 'Revenu des réservations faites dans les 7 jours suivant l’envoi.',
    journeyReminder: 'Rappel de la veille',
    journeyPostVisit: 'Après la visite',
    journeyAbandonedCart: 'Panier abandonné',
    journeyWinback: 'Réactivation',
    journeyReminderDesc: 'Les infos pratiques la veille. Fait baisser les absences.',
    journeyPostVisitDesc: 'Merci, demande d’avis, et la prochaine expérience.',
    journeyAbandonedCartDesc: 'Une réservation laissée en plan reçoit une relance.',
    journeyWinbackDesc: 'Les visiteurs qui ne sont pas revenus depuis six mois.',
    enabled: 'Actif',
    disabled: 'Inactif',
    timing: 'Déclenchement',
    hoursBefore: 'heures avant',
    hoursAfter: 'heures après',
    daysBefore: 'jours avant',
    daysAfter: 'jours après',
    runNow: 'Exécuter',
  },
  unsubscribe: {
    title: 'Préférences de courriel',
    subscribed: 'Vous êtes abonné aux nouvelles de {venue}.',
    already: 'Vous êtes désabonné. Vous ne recevrez plus de courriels d’annonce.',
    confirm: 'Se désabonner',
    resubscribe: 'Se réabonner',
    done: 'C’est fait. Vous êtes désabonné.',
    resubscribed: 'Vous êtes réabonné.',
    invalid: 'Ce lien n’est pas valide. Votre logiciel de courriel l’a peut-être coupé.',
    note: 'Cela ne concerne que les annonces. Les confirmations et les rappels des billets que vous avez déjà achetés continueront de vous parvenir.',
  },
  modify: {
    title: 'Modifier',
    reschedule: 'Déplacer',
    uncheck: 'Remettre en non pointé',
    uncheckHint: 'Retire ses cartes du tableau. Refusé si quelqu’un porte un casque.',
    uncheckBlocked: 'Quelqu’un est déjà en route — traitez-le sur le tableau d’abord.',
    refund: 'Rembourser',
    quantity: 'Changer les places',
    cancel: 'Annuler la réservation',
    balanceDue: 'Reste à payer',
    refundDue: 'À rembourser',
    settled: 'Soldé',
    paid: 'Payé',
    amount: 'Montant',
    reason: 'Motif',
    chooseTime: 'Choisir un nouveau créneau',
    sameExperience: 'Seuls les créneaux de la même expérience sont proposés.',
    checkInCleared: 'Déplacer une réservation efface son check-in.',
    refundHint: 'On ne peut pas rendre plus que ce qui a été encaissé.',
    apply: 'Appliquer',
    payments: 'Paiements',
    tickets: 'Codes des billets',
  },
  onboarding: {
    title: 'Enregistrement',
    welcome: 'Bienvenue',
    findBooking: 'Retrouver votre réservation',
    emailLabel: 'Le courriel utilisé pour réserver',
    or: 'ou',
    referenceLabel: 'Votre référence de réservation',
    findIt: 'Continuer',
    notFound: 'Aucune réservation trouvée. Vérifiez l’orthographe, ou utilisez votre référence.',
    noneHere: 'Ce lieu n’a pas encore d’enregistrement en ligne. Rien à remplir — on vous accueille à la porte.',
    notServedHere: 'Ce formulaire n’est pas celui utilisé ici. Demandez le bon lien à l’accueil.',
    ambiguous: 'Plusieurs réservations correspondent. Utilisez votre référence.',
    experienceRequired: 'Nous ne savons pas à quelle expérience vous inscrire. Prévenez l’accueil — rien n’a été enregistré.',
    /* Chaque défaut NOMMÉ, à l'étape où il se corrige (14/10). */
    invalid: {
      email: 'L’adresse courriel n’est pas valide. Vérifiez-la (par exemple nom@exemple.com).',
      emailRequired: 'Une adresse courriel est requise pour un adulte.',
      date: 'Cette date n’est pas valide.',
      birthdateFuture: 'La date de naissance ne peut pas être dans le futur.',
      phone: 'Ce numéro de téléphone n’est pas valide.',
      number: 'Entrez un nombre.',
      signature: 'La signature n’a pas pu être lue. Signez à nouveau.',
      guardian: 'Indiquez le nom du parent ou du tuteur.',
      required: 'Ce champ est obligatoire.',
      field: 'Cette réponse a été refusée. Vérifiez-la.',
      preferences: 'Une de vos préférences a été refusée. Choisissez à nouveau.',
      emailShared: 'Chaque adulte a sa propre adresse courriel.',
    },
    serverRefused: 'Votre enregistrement n’est pas parti : une réponse est à corriger. Vous voici à l’étape où elle se corrige — rien de ce que vous avez saisi n’est perdu.',
    claimTitle: 'L’une de celles-ci est-elle la vôtre ?',
    claimHint: 'Nous avons trouvé une réservation à cette adresse. La choisir vous garde avec votre groupe à l’accueil.',
    claimNone: 'Aucune de celles-ci — continuer sans réservation',
    whichTicket: 'Pour quel billet vous enregistrez-vous ?',
    ticketDone: 'Fait',
    ticketLeft: 'à remplir',
    whoFor: 'Pour qui vous enregistrez-vous ?',
    whoForHint: 'Tout le groupe doit être enregistré. Vous pouvez le faire l’un après l’autre sur cet appareil.',
    adult: 'Adulte',
    minor: 'Enfant',
    done: 'Fait',
    alreadyOnFile: 'Déjà au dossier',
    remaining: 'Reste à faire',
    startFor: 'Enregistrer',
    venueTerms: '{venue} — j’accepte ses conditions et sa politique de confidentialité.',
    imageRightsTitle: 'Droit à l’image · facultatif',
    imageRights: 'J’autorise {studio} et {operator} à utiliser les photos et vidéos où j’apparais, prises pendant ma visite, pour faire connaître l’expérience (réseaux sociaux, sites web, salons, publicités), sans rémunération, pendant cinq ans. Je peux retirer cette autorisation en écrivant au lieu ; ce qui est déjà publié reste en ligne.',
    imageRightsSingle: 'J’autorise {studio} à utiliser les photos et vidéos où j’apparais, prises pendant ma visite, pour faire connaître l’expérience (réseaux sociaux, sites web, salons, publicités), sans rémunération, pendant cinq ans. Je peux retirer cette autorisation en écrivant au lieu ; ce qui est déjà publié reste en ligne.',
    engineTerms: 'Conditions d’utilisation',
    enginePrivacy: 'Politique de confidentialité',
    minorNotice: 'Un enfant ne signe pas. Un adulte présent signe pour lui.',
    guardianLabel: 'Nom de l’adulte qui signe',
    signHere: 'Signez ici',
    clear: 'Effacer',
    next: 'Suivant',
    back: 'Retour',
    finish: 'Terminer',
    allDone: 'Tout le groupe est enregistré.',
    allDoneHint: 'Montrez cet écran à l’entrée, ou entrez directement — le comptoir le voit.',
    oneDone: 'Enregistré.',
    another: 'Enregistrer quelqu’un d’autre',
    required: 'Obligatoire',
    yourEmail: 'Votre courriel',
    step: 'Étape',
    of: 'sur',
    walkIn: 'Je n’ai pas encore de réservation',
    walkInHint: 'Remplissez maintenant, on le rattachera à votre billet à l’achat.',
    whichExperience: 'Pour quelle expérience êtes-vous ici ?',
    emailLabelPlain: 'Votre courriel',
    findYou: 'Votre courriel, pour vous retrouver si vous êtes déjà venu.',
    householdTitle: 'Qui est là ce soir ?',
    householdHint:
      'Nous connaissons déjà ces personnes. Cochez qui vous accompagne — celles qui ne le sont pas ne comptent pas pour ce soir.',
    householdEmpty: 'Première visite',
    householdEmptyHint: 'Nous n’avons rien à cette adresse. Inscrivez-vous ci-dessous, c’est l’affaire d’une minute.',
    householdCovered: 'Rien à signer',
    householdAdd: 'Quelqu’un d’autre m’accompagne',
    /* Venus ensemble sans réservation (14/10). */
    householdAddAdult: 'Ajouter un adulte',
    householdAddChild: 'Ajouter un enfant',
    householdChildAfterAdult: 'Enregistrez d’abord l’adulte qui signe pour l’enfant : son nom et son courriel seront repris.',
    householdToday: 'Déjà enregistrés ce soir',
    householdTogetherTitle: 'Qui vient avec vous ?',
    householdTogetherHint: 'Enregistrez chaque personne à la suite — les adultes d’abord, puis les enfants pour qui ils signent.',
    householdFinish: 'Non, c’est tout',
    walkInEmailHint: 'Vous vous enregistrez d’abord ; vous pourrez ensuite ajouter les personnes qui vous accompagnent.',
    householdNextTitle: 'Quelqu’un vient avec vous ?',
    householdNextHint: 'Ajoutez chaque personne qui vous accompagne ce soir. Un adulte remplit ses propres informations ; un enfant est enregistré par l’adulte qui en est responsable.',
    householdStartSelf: 'Commencer mon enregistrement',
    whoSelf: 'Votre enregistrement',
    whoSelfHint: 'Commencez par vous. Vous pourrez ajouter tout de suite après les personnes qui vous accompagnent.',
    whoCompanion: 'Un adulte qui vous accompagne',
    whoCompanionHint: 'Les informations de cette personne, avec sa propre adresse courriel.',
    whoChild: 'Un enfant dont vous êtes responsable',
    whoChildHint: '{name} signe pour cet enfant.',
    whoKnown: 'Enregistrement de {name}',
    whoSeat: 'Place {n} sur {total}',
    whoSeatNamed: '{name} — place {n} sur {total}',
    whoSeatHint: 'Les informations de la personne qui occupe cette place.',
    whoSeatChildHint: 'Un enfant : c’est l’adulte qui en est responsable qui signe.',
    householdSign: 'Continuer',
    householdReady: 'Nous sommes prêts',
    householdBack: 'Retour',
    whichExperienceHint:
      'Nous en présentons plusieurs. Choisissez la vôtre pour que la salle vous reconnaisse à l’entrée — et pour lire la bonne décharge.',
  },
  forms: {
    title: 'Formulaires',
    pick: 'Choisissez un formulaire pour afficher son code.',
    hint: 'À faire flasher avec le téléphone de la personne. Il ouvre CE formulaire, pas le défaut du lieu.',
    none: 'Aucun formulaire d’enregistrement n’est publié ici. Ils s’écrivent et se publient dans Onboarding.',
  },
  ob: {
    title: 'Enregistrement',
    newFlow: 'Nouveau parcours',
    appliesTo: 'S’applique à',
    wholeVenue: 'Ce lieu',
    useHere: 'L’utiliser ici',
    notUsedHere: 'Pas utilisé ici',
    detachHint: 'Ne plus l’utiliser ici. Le parcours lui-même reste.',
    alsoAt: 'Aussi à',
    crossBorder: 'Affecté dans plus d’un pays — vérifiez que la décharge tient sous les deux droits.',
    printSheet: 'Feuille à imprimer',
    wallQr: 'QR-code de mur',
    printTitle: 'Enregistrez-vous avant d’entrer',
    wallQrHint: 'À imprimer et coller là où la file se forme. Il ouvre CE parcours, pas le défaut du lieu.',
    forThisFlow: 'Ce parcours',
    followsVenue: 'Suit le lieu',
    tabletFlow: 'Ouvre',
    flows: 'Parcours',
    kiosks: 'Tablettes',
    submissions: 'Signés',
    active: 'Actif',
    inactive: 'Brouillon',
    activate: 'Activer',
    deactivate: 'Désactiver',
    duplicate: 'Dupliquer',
    steps: 'Étapes',
    registration: 'Inscription',
    nda: 'NDA',
    waiver: 'Décharge',
    privacy: 'Politique de confidentialité',
    addStep: 'Ajouter une étape',
    needsAStep:
      'Ajoutez au moins une étape. Le NDA, la décharge et la politique de confidentialité restent optionnels.',
    publish: 'Publier',
    published: 'Publié',
    version: 'Version',
    languages: 'Langues',
    links: 'Où le remplir',
    kioskUrl: 'Tablette à l’entrée',
    wallUrl: 'QR-code pour la file',
    venueQrHint: 'Celui-ci suit le parcours actif du lieu. Pour un parcours précis — un par expérience — ouvrez ce parcours et imprimez son propre code.',
    emailLink: 'Lien du courriel de confirmation',
    copy: 'Copier',
    copied: 'Copié',
    newKiosk: 'Ajouter une tablette',
    kioskName: 'Où est-elle ?',
    revoke: 'Révoquer',
    revoked: 'Révoquée',
    lastSeen: 'Vue',
    never: 'Jamais',
    signedAt: 'Signé',
    channel: 'Rempli depuis',
    noFlows: 'Aucun parcours. Créez-en un pour recueillir une inscription, un NDA ou une décharge.',
    inUse: 'Ce parcours a été signé, il ne peut plus être supprimé.',
    blockField: 'Question',
    blockConsent: 'Case à cocher',
    blockSignature: 'Signature',
    blockHeadingHint: 'Un titre',
    blockTextHint: 'Un paragraphe',
    blockFieldHint: 'Nom, courriel, n’importe quoi',
    blockConsentHint: 'Doit être cochée',
    blockSignatureHint: 'Tracée au doigt',
    blockImageHint: 'Une image',
    blockDividerHint: 'Une ligne de séparation',
    fieldKey: 'Clé',
    fieldKeyHint: 'Nom stable utilisé à l’export. Minuscules, sans espaces.',
    fieldType: 'Type',
    label: 'Libellé',
    help: 'Texte d’aide',
    required: 'Obligatoire',
    preview: 'Ce que voient les visiteurs',
  },
  viewAs: {
    label: 'Voir comme',
    full: 'Studio (tout)',
    hint: 'Descendre vos capacités à celles d\u2019un poste, pour voir ce qu\u2019il voit.',
    active: 'Vue du poste {role}',
    readOnly: 'Lecture seule le temps de la vérification.',
    exit: 'Revenir à l\u2019accès complet',
    refused: 'Pas tant que vous regardez depuis un autre poste. Quittez ce mode d\u2019abord.',
  },
  scan: {
    title: 'Scan',
    action: 'Scan',
    hint: 'Scannez un billet, ou tapez son code et validez.',
    placeholder: 'Code du billet',
    valid: 'Laissez entrer',
    onboardingMissing: 'Laissez entrer — orientez vers une tablette',
    scanToFill: 'Scannez avec votre téléphone',
    scanToFillHint: 'Ouvre son enregistrement, déjà rattaché à cette réservation — rien à retaper.',
    onboardingMissingHint:
      'L’enregistrement n’est pas terminé — ce peut être le formulaire, le NDA ou la décharge, selon ce que demande cette expérience. Orientez la personne vers une tablette ou le QR-code du mur.',
    alreadyIn: 'Déjà entré',
    alreadyInAgo: 'Scanné il y a {n} min',
    alreadyInJust: 'Scanné à l’instant',
    wrongDay: 'Pas le billet du jour',
    wrongDayHint: 'Ce billet est pour le {date}. Cherchez la bonne réservation, ou laissez entrer quand même.',
    wrongTime: 'Mauvais créneau',
    wrongTimeHint: 'Ce billet part à {time}. Demandez à la personne de revenir, ou laissez-la entrer quand même.',
    letInAnyway: 'Laisser entrer quand même',
    overridden: 'Entré sur dérogation',
    void: 'Billet annulé',
    notFound: 'Code inconnu',
    readAs: 'Lu comme ',
    registrations: 'Enregistrés',
    pushed: '{n} transmis à l’expérience',
    departure: 'Départ',
    scanAnother: 'Suivant',
  },
  desk: {
    title: 'Mode terrain',
    screens: 'Écrans',
    leave: 'Sortir',
    leaveTitle: 'Quitter le mode terrain ?',
    leaveBody: 'Vous revenez au back-office complet. Rien n’est perdu — on y retourne d’une touche.',
    enterTitle: 'Passer en mode terrain ?',
    enterBody: 'Plein écran : la caméra, la journée, le formulaire et les écrans, en cibles de doigt. Le reste du back-office est masqué jusqu’à ce que vous sortiez.',
    noScreensHint: 'Déclarez les téléviseurs du lieu dans Parc → Appareils, puis pilotez-les d’ici.',
    screenSoundHint: 'Un téléviseur ne joue le son qu’après avoir été touché une fois. Touchez l’écran après l’avoir allumé.',
    enter: 'Mode terrain',
    enterHint: 'Plein écran, caméra allumée, une main libre. Le scan, la journée, le formulaire, les écrans — et rien d’autre.',
    scan: 'Scan',
    form: 'Formulaire d’enregistrement',
    previousDay: 'Jour précédent',
    nextDay: 'Jour suivant',
    today: 'La journée',
    startCamera: 'Utiliser la caméra',
    cameraInsecure:
      'La caméra exige une page sécurisée. Celle-ci est servie en HTTP simple, et aucun navigateur n’y ouvre de caméra — sur aucun téléphone.',
    cameraUnsupported: 'Ce navigateur ne sait pas lire les QR. Tapez le code ci-dessous, ou utilisez une douchette.',
    cameraDenied: 'Accès à la caméra refusé. Autorisez-le dans les réglages du navigateur, ou tapez le code ci-dessous.',
    aim: 'Visez le billet',
    scanNext: 'Billet suivant',
    household: 'Qui l’accompagne',
    minor: 'Mineur',
    adult: 'Adulte',
    seatOne: 'place',
    seatMany: 'places',
    visits: 'visites',
    signed: 'signées',
    showWaiver: 'À montrer au visiteur',
    showWaiverHint: 'Il le scanne avec son propre téléphone et signe sur place. Pas besoin de l’envoyer à une tablette.',
    findByName: 'Retrouver par nom',
    arrived: 'arrivés',
    arrivedOne: 'arrivé',
    expected: 'attendus',
    walkInHint: 'Quelqu’un sans réservation s’encaisse au comptoir, sur l’ordinateur de l’accueil — pas d’ici.',
    headset: 'Casque',
    headsetNfc: 'Scanner le tag d’un casque (NFC)',
    headsetNfcListening: 'Approchez le téléphone du tag du casque…',
    headsetNfcUnavailable: 'Le NFC ne se lit que dans Chrome sous Android, depuis l’app du nuage. Prenez le QR du casque ou son numéro.',
    headsetNfcDenied: 'NFC refusé ou éteint. Activez-le dans les réglages du téléphone, ou prenez le QR.',
    headsetQr: 'Scanner le QR du casque',
    headsetAim: 'Visez le QR du casque',
    headsetNumber: 'Numéro ou nom du casque',
    headsetFind: 'Trouver',
    headsetUnknown: 'Casque inconnu dans ce lieu.',
    headsetFor: 'Ce casque est pour',
    headsetFree: 'Casque libre',
    headsetSilent: 'silencieux',
    headsetEngineOk: 'Lié chez {engine} aussi.',
    headsetEngineFree: 'Personne dessus chez {engine}.',
    headsetEngineOther: '{engine} a {name} sur ce casque, pas cette personne.',
    headsetEngineStale: '{engine} y a encore {name} — le prochain lien le remplace.',
    headsetRelink: 'Refaire le lien chez {engine}',
    headsetRelinked: 'Lien refait.',
    headsetEngineNobody: 'personne',
    headsetGiveTo: 'Le donner à…',
    headsetNobodyWaiting: 'Personne d’enregistré n’attend de casque.',
    headsetGiven: 'Casque donné à {name}.',
    headsetNext: 'Scanner un autre casque',
  },
  fleet: {
    dupTitle: 'Même adresse {ip} : probablement un seul casque sous deux fiches',
    dupSeen: 'vu le {when}',
    dupNever: 'jamais vu',
    dupMerge: 'Fusionner',
    dupConfirmTitle: 'Fusionner ces deux fiches ?',
    dupConfirmBody: '« {keep} » est gardée, c’est celle qui bat. Elle prend de « {drop} » ce qu’elle ignore (nom, série, salle, rattachement au studio), puis « {drop} » disparaît. Ses visites, ses commandes et son historique suivent. À ne faire que si c’est bien le même casque : une adresse peut avoir été redonnée à un autre.',
    dupMerged: 'Fiches fusionnées.',
    dupTitleLabel: 'Deux fiches « {name} » : probablement un seul casque',
    rowMenu: 'Actions',
    mergeWith: 'Fusionner avec…',
    mergeSelected: 'Fusionner les 2…',
    mergeTitle: 'Fusionner deux fiches',
    mergeHint: 'Le même casque sous deux identités. La fiche gardée reprend la série, le nom, le groupe d’inventaire et le numéro XROAM de l’autre ; les visites, incidents et installations des deux y sont réunis.',
    mergeKeep: 'Garder',
    mergeKeepHint: 'Gardez celle qui bat : c’est elle que le casque vise.',
    mergeOther: 'Autre fiche',
    mergePick: 'Choisir un casque',
    mergeDo: 'Fusionner',
    mergeReports: 'bat',
    mergeNever: 'ne bat pas',
    unplace: 'Sortir de la salle',
    unplaced: '{name} est sorti de la salle.',
    remove: 'Supprimer la fiche…',
    removeTitle: 'Supprimer {name} ?',
    removeBody: 'La fiche quitte le parc, ici et au nuage. Seule une fiche qui ne bat pas, hors inventaire et sans historique peut l’être ; un doublon se fusionne.',
    removeDo: 'Supprimer',
    removed: 'Fiche supprimée.',
    removeErrors: {
      online: 'Ce casque bat : sa fiche reviendrait au prochain battement.',
      in_inventory: 'Ce casque fait partie de l’inventaire : sortez-le de la salle plutôt.',
      has_history: 'Cette fiche a un historique (visites, incidents) : fusionnez-la plutôt avec l’autre fiche du casque.',
    } as Record<string, string>,
    openGroup: 'Ouvrir',
    sameAs: 'Même machine que',
    sameAsPick: 'Choisir une fiche…',
    groupDevicesCount: '{n} casque(s) dans ce groupe',
    groupNote: 'Note',
    groupParent: 'Rangé dans',
    groupMembers: 'Ses casques ({n})',
    groupEmpty: 'Aucun casque dans ce groupe pour l’instant.',
    removeFromGroupN: 'Retirer {n} du groupe',
    showInDevices: 'Voir dans Appareils',
    groupAdd: 'Ajouter des casques',
    searchDevices: 'Nom ou série',
    addToGroupN: 'Ajouter {n} au groupe',
    subgroups: 'Sous-groupes',
    deleteGroup: 'Supprimer le groupe',
    deleteGroupTitle: 'Supprimer « {name} » ?',
    groupCycle: 'Un groupe ne peut pas se ranger dans lui-même ni dans un de ses sous-groupes.',
    seenTitle: '{n} appareils vus dans vos lieux, pas encore rattachés',
    seenHint: 'Ils ont donné signe de vie dans un lieu du studio sans avoir été enrôlés : ils n’appartiennent à personne et n’apparaissent pas au parc. Rattachez ceux que le studio possède ; laissez de côté les casques propres à un exploitant.',
    seenVenue: 'Vu à',
    tabDevices: 'Appareils',
    tabGroups: 'Groupes',
    tabPresets: 'Préréglages de casque',
    rename: 'Renommer',
    deleteGroupHint: 'Supprime le groupe, pas ses casques : un groupe est une étiquette.',
    removeFromGroup: 'Retirer de ce groupe',
    inventory: 'Inventaire du studio',
    groupsTitle: 'Les groupes du studio',
    groupName: 'Groupe',
    groupCount: 'Appareils',
    claimPicked: 'Rattacher {n} au studio',
    claimAll: 'Tout rattacher',
    claimed: '{n} appareils rattachés au studio.',
    ignorePicked: 'Ignorer {n}',
    ignored: '{n} appareil(s) ignoré(s) : ils ne se proposent plus.',
    showIgnored: 'Voir les ignorés ({n})',
    hideIgnored: 'Masquer les ignorés',
    ignoredTitle: 'Ignorés',
    unignore: 'Ne plus ignorer',
    unignored: 'Proposé à nouveau.',
    kindOne: { headset: 'Casque', server: 'Serveur', tracker: 'Balise', router: 'Routeur', other: 'Autre' } as Record<string, string>,
    renameDevice: 'Renommer',
    renamedSync: 'Renommé. La salle reprend le nouveau nom à sa prochaine synchronisation.',
    title: 'Parc',
    wholeFleet: 'Tout le parc',
    device: 'Appareil',
    serial: 'Numéro de série',
    whereIsIt: 'Où il est',
    lastSeen: 'Dernier signe',
    neverSeen: 'Jamais',
    atStudio: 'Au studio',
    none: 'Aucun appareil enrôlé.',
    noneHint:
      'Enrôlez-les depuis le bon de livraison du fabricant — un numéro de série par ligne. Ils pourront ensuite partir dans un lieu par paquets.',
    enrol: 'Enrôler des appareils',
    enrolAction: 'Enrôler',
    kind: 'Type',
    headset: 'Casques',
    server: 'Serveurs',
    tracker: 'Balises',
    router: 'Routeurs',
    other: 'Autre',
    paste: 'Numéros de série',
    pasteHint:
      'Un par ligne : le numéro de série, puis l’étiquette écrite sur la caisse, et si vous l’avez l’adresse du casque sur le réseau du lieu. Tabulations, points-virgules et virgules fonctionnent — collez directement depuis le tableur. L’adresse est reconnue à sa forme, l’ordre des colonnes n’a donc pas d’importance.',
    willEnrol: '{n} appareils prêts',
    selected: '{n} sélectionnés',
    moveTo: 'Déplacer vers…',
    bringHome: 'Rapatrier au studio',
    bringHomeTo: 'Rapatrier au studio ({venue})',
    addToGroup: 'Ajouter au groupe…',
    newGroup: 'Nouveau groupe',
    newGroupHint:
      'Un groupe est une étiquette, pas un contenant : un casque peut être dans « flotte Casper » et dans « massive test #1 » en même temps. Déplacer un groupe emporte ses sous-groupes.',
    groupPlaceholder: 'Massive test #1',
    topLevel: 'Groupe de tête',
    under: 'Dans {name}',
    importTitle: 'Importer un relevé de parc',
    importHint:
      'L’autre moitié du balayage réseau : le balayage trouve ce qui répond, l’import pose ce qu’on sait. Le rapprochement se fait sur l’adresse MAC — une IP change avec le bail DHCP, une MAC désigne l’objet.',
    importFile: 'Fichier CSV',
    importPaste: 'Ou collez les lignes',
    importIgnore: '— ignorer —',
    importFields: {
      hardwareId: 'Numéro de série',
      macAddress: 'Adresse MAC',
      ipAddress: 'Adresse IP',
      label: 'Nom',
    },
    importCount: '{n} lignes lues',
    importBadMac: '{n} lignes portent dans la colonne MAC autre chose qu’une adresse. Elles seront refusées et non devinées — vérifiez la colonne avant d’importer.',
    importGo: 'Importer',
    importing: 'Import…',
    importDone: '{c} créés · {u} mis à jour · {s} refusés',
    importWhy: {
      bad_mac: 'adresse MAC illisible',
      no_match: 'pas de numéro de série, et aucun casque avec cette MAC',
      mac_taken: 'cette MAC appartient à un autre casque',
      empty: 'ni numéro de série ni MAC',
    },
    createGroup: 'Créer',
  },
  remote: {
    title: 'Configuration distante',
    lede: 'Ce que l’expérience joue dans chaque salle, décidé par le studio. Tout ce qui s’écrit ici est un brouillon : rien n’arrive dans une salle avant d’être publié.',
    steps: 'Étapes',
    stepKeys: 'Déclarer',
    stepValues: 'Valeurs par défaut',
    stepVariants: 'Variantes et réglages par âge',
    stepPublish: 'Publier et historique',
    useInUnity: 'Utiliser ces variables dans Unity',
    useInUnityHint: 'Un jeton du studio permet à l’éditeur Unity de les lire et de générer sa classe C#.',
    pickTitle: 'Choisir une expérience',
    noTitles: 'Aucune expérience ne déclare encore de variables.',
    instances: 'Lieux qui la jouent (leurs réglages propres)',
    noInstances: 'Aucun lieu ne joue encore cette expérience.',
    keys: 'Variables',
    template: 'Valeurs par défaut',
    environments: 'Variantes',
    audiences: 'Réglages par âge',
    publications: 'Historique',
    venueValues: 'Ce lieu',
    perTicket: 'Par tarif',
    activeEnvironment: 'Variante active',
    tests: 'Tests A/B',
    devices: 'Appareils',
    back: 'Retour à l’expérience',
    inherit: 'Hériter',
    inherited: 'Hérité : {source}',
    fromSchema: 'défaut du schéma',
    fromTemplate: 'valeurs par défaut',
    fromVenue: 'lieu',
    fromEnvironment: 'variante',
    save: 'Enregistrer le brouillon',
    saved: 'Brouillon enregistré — publiez pour l’envoyer aux salles.',
    publish: 'Publier',
    publishNote: 'Ce qui change, en une ligne (facultatif)',
    publishedAs: 'Publié sous le n° {n}.',
    republish: 'Revenir à celle-ci',
    noPublication: 'Rien de publié : les salles jouent les défauts du schéma.',
    publication: 'Publication {n}',
    schemaVersion: 'schéma {n}',
    live: 'En service',
    warnings: 'Écarté à la construction du paquet',
    key: 'Clé',
    labelEn: 'Libellé (anglais)',
    labelFr: 'Libellé (français)',
    type: 'Type',
    typeNumber: 'Nombre',
    typeBoolean: 'Oui / non',
    typeEnum: 'Choix',
    typeText: 'Texte',
    typeList: 'Liste d’objets',
    open: 'Ouvrir',
    search: 'Chercher une variable',
    onlySet: 'Modifiées seulement',
    counts: '{n} variables · {set} modifiée(s)',
    sectionCount: '{n} variable(s)',
    listCount: '{n} élément(s)',
    listFields: 'Champs d’un élément',
    listFieldsHint: 'Chaque élément de la liste a ces champs — une colonne chacun dans les valeurs. Un champ laissé vide garde le défaut propre à l’expérience.',
    addField: 'Ajouter un champ',
    addRow: 'Ajouter un élément',
    removeRow: 'Retirer',
    yes: 'Oui',
    no: 'Non',
    defaultValue: 'Défaut',
    min: 'Min',
    max: 'Max',
    step: 'Pas',
    unit: 'Unité',
    options: 'Choix (valeur = libellé, un par ligne)',
    scope: 'Portée',
    scopeRoom: 'Salle — la scène commune',
    scopeHeadset: 'Casque — ce seul visiteur',
    scopeHint: 'Une clé de salle ne peut pas différer dans un groupe : elle se teste par séance et ne suit jamais l’âge. Une clé de casque peut différer d’un visiteur à l’autre.',
    studioOnly: 'Studio seulement',
    addKey: 'Ajouter une variable',
    removeKey: 'Retirer',
    publishSchema: 'Publier les variables (version {n})',
    schemaHint: 'Le projet Unity lit ces variables par une classe générée. En renommer une en crée une nouvelle : les réglages faits sur l’ancienne sont écartés et signalés.',
    none: 'Aucun',
    newEnvironment: 'Nouvelle variante',
    environmentName: 'Nom',
    deleteEnvironment: 'Supprimer',
    noEnvironments: 'Aucune variante. Une variante est un jeu nommé d’écarts aux valeurs par défaut : « Hiver », « Briefing court ».',
    environmentHint: 'Les variantes appartiennent à l’expérience, partagées par tous les lieux : c’est ce qui rend un test comparable d’un lieu à l’autre.',
    newAudience: 'Nouveau réglage par âge',
    minAge: 'À partir de',
    maxAge: 'Jusqu’à',
    minorOnly: 'Mineurs seulement (case de la décharge)',
    audienceHint: 'Un réglage par âge est une règle de soin, pas un test : les mêmes valeurs pour tous ceux qu’il désigne, d’après l’âge de la décharge au jour de la séance. Variables de casque seulement.',
    noAudiences: 'Aucun réglage par âge.',
    newTest: 'Nouveau test',
    testName: 'Nom',
    arms: 'Branches',
    control: 'Témoin (aucune variante)',
    weight: 'Part %',
    addArm: 'Ajouter une branche',
    eligibility: 'Qui participe (tests de casque seulement)',
    byRoom: 'Tiré par séance',
    byHeadset: 'Tiré par visiteur',
    testHint: 'Un test à la fois. Les branches copient les variantes telles que publiées. Un test qui touche une variable de salle se tire par séance, sans condition sur les personnes.',
    start: 'Lancer le test',
    stop: 'Arrêter',
    stopWith: 'Arrêter — {arm} l’emporte',
    winnerHint: 'Le gagnant devient la variante active du lieu, dans son brouillon. Publiez pour la faire jouer partout.',
    running: 'En cours',
    ended: 'Terminé',
    noTests: 'Aucun test.',
    holds: 'tient la publication {n}',
    holdsStale: 'encore sur la publication {n}',
    holdsNothing: 'ne dit encore rien',
    originDefault: 'défauts compilés',
    originCached: 'dernier paquet reçu',
    originRemote: 'reçu du nuage',
    ticketType: 'Tarif',
    genders: { female: 'Femmes', male: 'Hommes', nonbinary: 'Non binaires', undisclosed: 'Non précisé' },
    tokens: 'Jetons pour Unity',
    tokensHint: 'L’éditeur Unity lit les variables d’une expérience avec l’un d’eux pour générer sa classe C#. À poser dans la variable d’environnement PORTAGEROOM_STUDIO_TOKEN du poste — jamais dans le projet, jamais dans un build. Il lit les variables, et rien d’autre.',
    tokenLabel: 'À quoi il sert (ex. poste Atlantis)',
    newToken: 'Émettre un jeton',
    tokenOnce: 'Copiez-le maintenant : il ne sera plus jamais affiché.',
    revoke: 'Révoquer',
    revoked: 'Révoqué',
    lastUsed: 'dernier usage {when}',
    neverUsed: 'jamais utilisé',
    noTokens: 'Aucun jeton.',
    errors: {
      out_of_range: 'Une valeur sort des bornes de sa clé.',
      unknown_param: 'Cette clé n’est pas dans le schéma.',
      weights_must_sum_to_100: 'Les parts doivent faire 100.',
      test_already_running: 'Un test est déjà en cours pour cette expérience.',
      environment_not_published: 'Publiez d’abord le gabarit : cet environnement n’est pas en service.',
      room_test_is_by_session: 'Un test sur une clé de salle se tire par séance : il ne peut pas porter de condition sur les personnes.',
      audience_room_key: 'Un public ne règle que des clés de casque.',
      environment_name_taken: 'Ce nom est déjà pris dans la bibliothèque.',
      audience_name_taken: 'Ce nom est déjà pris.',
      environment_needs_ticket_type: 'Un environnement se règle au gabarit ; ici, seulement par tarif.',
      one_control_only: 'Un seul témoin.',
      duplicate_arm: 'Le même environnement deux fois dans un test.',
    } as Record<string, string>,
  },
  lbe: {
    column: 'Carte LBE',
    asked: 'Vérification de la carte LBE demandée à {n} casque{s} : la réponse arrive à leur prochain signal.',
    code: 'code PICO {code}',
    expected: 'Carte LBE attendue',
    expectedHint: 'Le .zip déployé par PICO Business Manager, par son nom de fichier. Écrite sur les casques à l’installation et à « Reconfigurer » ; chaque casque vérifie ensuite que c’est bien la carte en service. Vide : Portage n’attend rien.',
    pick: 'Choisir une carte vue sur les casques',
    none: 'Aucune carte attendue',
    state: {
      in_effect: 'Carte attendue en service',
      not_in_effect: 'Une autre carte est en service',
      missing: 'Carte attendue absente du casque',
      unknown: 'Vérification impossible',
      not_configured: 'Aucune carte attendue sur ce casque',
      unread: 'Non remontée (SDK antérieur à 0.17.31, ou app fermée)',
    },
  },
  awake: {
    tab: 'Carte LBE et veille',
    keep: 'Garder les casques éveillés',
    on: 'Pendant la préparation et l’expérience',
    keepHint: 'Casques PICO entreprise seulement (SDK 0.17.32). Débranché et avant la fin de la visite, le casque ne se met jamais en veille : il garde son app et sa carte LBE. Il reprend son propre délai quand on le branche, à la fin de la visite ou à la fermeture de l’app. Écrit sur les casques à l’installation et à « Reconfigurer ».',
    column: 'Veille',
    held: 'Tenu éveillé',
    normal: 'Veille normale',
    normalHint: 'Branché, ou visite finie : le casque a repris son propre délai.',
    unread: 'Non remontée',
  },
  zl: {
    title: 'Disposition des zones',
    hint: 'ZoneLayout.json, exporté d’Unity : la place et l’orientation de chaque scène, les corridors coupés, les passages déplacés. Chaque dépôt est une version ; la plus récente est proposée dans Portage Room, où l’exploitant la pousse par « Mettre à jour ».',
    noExperience: 'Aucune expérience ne tourne encore dans cette salle.',
    none: 'Aucune disposition : l’app garde le placement de sa build.',
    upload: 'Déposer ZoneLayout.json',
    uploadNew: 'Nouvelle version',
    uploading: 'Dépôt…',
    published: 'Version {v} publiée',
    withWarnings: 'vérifiez les scènes ci-dessous',
    version: 'Version {v} · exportée le {saved} · {scenes} scènes',
    by: 'par {who}, {when}',
    olderOne: 'version précédente',
    olderMany: 'versions précédentes',
    unchecked: 'Scènes non vérifiées : l’expérience n’a pas de minutage importé.',
    unknownScenes: 'Inconnues de l’expérience :',
    missingScenes: 'Non placées (gardent le placement de la build) :',
    column: 'Disposition',
    pushOption: 'Disposition des zones',
    pushVersion: '{name} · version {v}, exportée le {saved}',
    pushHint: 'Posée dans files/ZoneLayout.json, puis l’app redémarre. Cochée d’office quand un casque choisi n’est pas à jour.',
    state: {
      ok: 'À jour (v{v})',
      outdated: 'Ancienne version (v{v}, attendue v{expected})',
      modified: 'Modifiée : ne correspond à aucune version publiée',
      missing: 'Absente du casque',
      none_expected: 'Aucune disposition pour cette salle',
    },
    errors: {
      invalid_json: 'Ce fichier n’est pas du JSON valide.',
      not_a_zone_layout: 'Ce n’est pas un ZoneLayout.json (pas de _layout._sceneTransformDataList).',
      too_large: 'Le fichier est trop lourd.',
      experience_not_in_room: 'Cette expérience ne tourne pas dans cette salle.',
      room_not_found: 'Salle introuvable.',
    },
  },
  broom: {
    heading: 'Salles',
    add: 'Salle',
    namePlaceholder: 'Nom de la salle',
    nameTaken: 'Une salle de ce lieu porte déjà ce nom.',
    noExperience: 'Aucune expérience',
    noServer: 'aucun serveur',
    keyWord: 'clé',
    keysWord: 'clés',
    toPlace: 'À ranger',
    toPlaceLine: '{n} à ranger dans une salle',
    late: 'En retard',
    toPlaceHint: 'Des clés ou des serveurs ne sont rangés dans aucune salle.',
    noRoom: 'aucune salle',
    name: 'Nom',
    experiences: 'Ce qui tourne ici',
    experiencesHint: 'Une même expérience peut tourner dans plusieurs salles.',
    venueHasNone: 'Ce lieu n’a encore aucune expérience : déposez-en une depuis le catalogue.',
    server: 'Serveur',
    serverHint: 'Le serveur se range ici tout seul, au premier battement avec une clé de cette salle.',
    noServerYet: 'Aucun serveur ne s’est encore présenté.',
    online: 'en ligne',
    silent: 'muet',
    keys: 'Clés d’ingestion',
    keysHint: 'Ce qui autorise le serveur de cette salle à écrire dans Portage. Une par machine serveur : le build et l’instance de salle la lisent dans le même portageroom.json. Restreinte à l’expérience qui tourne sur cette machine, elle borne ce qu’une fuite permet. Jamais dans un casque.',
    noKeys: 'Aucune clé pour cette salle.',
    keyFor: 'Pour',
    allExperiences: 'Toutes les expériences',
    newKey: 'Nouvelle clé',
    keyOnce: 'Copiez-la maintenant : elle ne s’affichera plus jamais. Elle va dans le fichier portageroom.json de la machine du serveur — C:\\ProgramData\\Portage\\portageroom.json, {"ingestKey": "…"} — que lit aussi l’instance de salle.',
    lastUsed: 'Dernier usage {when}',
    neverUsed: 'Jamais utilisée',
    revoke: 'Révoquer',
    revokeTitle: 'Révoquer « {label} » ?',
    revokeBody: 'Le serveur qui la porte cesse d’écrire dans Portage à son prochain envoi. Ça ne se défait pas : il lui faudra une nouvelle clé.',
    unplacedTitle: 'Sans salle',
    placeIn: 'Ranger dans…',
    copyId: 'Copier l’identifiant',
    dropped: '{n} événements perdus',
    droppedVisits: ', dont {n} débuts ou fins de visite',
    droppedHint: 'Sa boîte d’envoi était pleine après une longue absence : des détails manquent à l’Analytique.',
    instance: 'Instance de salle {commit}',
    instanceBehind: 'en retard sur le nuage',
    instanceUntracked: 'version antérieure au suivi',
    venueBehind: 'Salle en retard',
    venueBehindHint: 'Une instance de salle de ce lieu tourne sur un Portage plus ancien que le nuage. Elle joue encore ; la mettre à jour quand c’est possible.',
    unplacedServersHint: 'Un serveur sans salle se range depuis la page Salles du lieu, ou tout seul à son prochain battement avec une clé de salle.',
  },
  admin: {
    title: 'Console Portage',
    venues: 'Lieux',
    titles: 'Expériences',
    instances: 'Instances',
    newVenue: 'Nouveau lieu',
    nominalHint:
      'Des valeurs NOMINALES, qui appartiennent à l’expérience du studio. Un lieu qui l’ajoute en hérite ; s’il les change ensuite, il ne change que les siennes.',
    headsetsHint: 'Casques simultanés que l’expérience exige. Sert à dimensionner un parc.',
    titleActive: 'Disponible au licensing',
    editTitle: 'Modifier l’expérience',
    newTitle: 'Nouvelle expérience',
    slug: 'Identifiant',
    name: 'Nom',
    timezone: 'Fuseau',
    currency: 'Devise',
    owner: 'Propriétaire',
    ownerHint: 'Un lieu est créé avec la personne qui l’exploite. Sans elle, personne ne peut entrer.',
    members: 'Personnes',
    upcoming: 'Créneaux à venir',
    duration: 'Durée',
    capacity: 'Capacité nominale',
    headsets: 'Casques requis',
    minAge: 'Âge minimum',
    guides: 'Opérateurs requis',
    bounds: 'Variables distantes',
    boundsHint: 'Déclarées une seule fois par le studio. Chaque lieu qui joue cette expérience en hérite, et le build Unity ne reçoit jamais une valeur en dehors.',
    noBounds: 'Aucune borne publiée. Le build n’a rien à respecter.',
    boundsVersion: 'Version',
    playedIn: 'Jouée dans',
    playedInNone: 'Aucun lieu ne la joue encore. Glissez-la sur un lieu dans Distribution.',
    toDistribution: 'Gérer dans Distribution',
    local: 'Propre au lieu',
    unlink: 'Délier',
    attach: 'Rattacher…',
    attachTo: 'Rattacher « {name} » à une expérience du studio',
    attachHint: 'Créée par le lieu lui-même. Rattachée, elle hérite des bornes et du pilotage de l’expérience du studio ; ses créneaux et ses ventes restent.',
    noEntity: 'Sans entité',
    venueSettings: 'Réglages du lieu',
    venueActive: 'Lieu actif',
    sandbox: 'Bac à sable',
    perfReporting: 'Mesurer les performances des casques',
    sceneOverrun: 'Alerte de scène trop longue',
    storageLow: 'Alerte de disque bas',
    storageLowHint: 'Sous cet espace libre, un casque est signalé en orange sur sa carte du tableau et au parc. Mesuré au début et à la fin de chaque visite (SDK 0.17.24). 0 = jamais.',
    sceneOverrunHint: 'Quand une personne passe dans sa scène plus que la durée prévue (le minutage Reed de l’expérience) majorée de ce pourcentage, sa carte clignote en rouge sur le tableau de la salle, jusqu’à ce qu’elle change de scène. 0 = jamais.',
    perfReportingHint: 'Images par seconde, mémoire, chaleur et batterie par scène, dans l’Analytique. Éteint : les casques du lieu ne mesurent ni n’envoient plus rien. Arrive aux casques avec le prochain paquet de configuration.',
    sandboxHint: 'Pour les essais seulement : le seul lieu où les sessions Claude enregistrent des visiteurs de test (MCP import_visitors). Jamais un vrai lieu.',
    venueActiveHint: 'Inactif, sa billetterie publique ne répond plus, il sort du sélecteur de lieux et ses membres ne peuvent plus l’ouvrir.',
    offer: 'Offre',
    offerHint: 'Les modules que cet exploitant paie, à la carte. Un module éteint est caché partout — menu, API, MCP, tâches de fond — et ses données restent.',
    moduleCore: 'Exploitation',
    moduleCoreHint: 'Toujours incluse : salles et flotte de casques, tableau, carte, écrans, enregistrement, analytique des joueurs, mode terrain.',
    moduleBilletterie: 'Billetterie',
    moduleBilletterieHint: 'Calendrier, journée, réservations, comptoir, scan, tarifs et programmation, ressources, billetterie publique, billets partenaires.',
    moduleMarketing: 'Marketing',
    moduleMarketingHint: 'Clients, campagnes, parcours, sites web, analytique des ventes et du public.',
    moduleEquipe: 'Équipe & planning',
    moduleEquipeHint: 'Planning, disponibilités, mon planning, affectation des guides.',
    operatorActive: 'Exploitant actif',
    operatorActiveHint: 'Inactif, aucun de ses lieux ne s’ouvre plus à ses membres et ses pages publiques ne répondent plus. Le studio y entre toujours.',
    noTitles: 'Aucune expérience au catalogue.',
    slugTaken: 'Cet identifiant est déjà pris.',
    superadminOnly: 'Ceci est la console du studio. Seule l’équipe Rabaska y accède.',
    sheetTab: 'Fiche',
    apkTab: 'APK',
    variablesTab: 'Variables',
    visitorTab: 'Visiteur',
    timingsTab: 'Minutage',
    pagesTab: 'Pages',
    openVariables: 'Ouvrir les variables',
    board: 'Distribution',
    boardHint: 'Glissez une expérience sur un lieu pour l’y rendre disponible. Elle arrive non publiée, sur les valeurs nominales du studio.',
    studioCatalogue: 'Expériences du studio',
    newOperator: 'Nouvel opérateur',
    newVenueHere: 'Nouveau lieu',
    legalName: 'Raison sociale',
    legalNameHint: 'Facultative. Apparaît sur les factures, pas dans le produit.',
    create: 'Créer',
    addTitle: 'Ajouter une expérience',
    allTitlesProvisioned: 'Toutes les expériences du studio sont déjà ici.',
    alreadyProvisioned: 'Cette expérience est déjà dans ce lieu.',
    noTitlesHint: 'Créez-en une dans Expériences : c’est l’expérience du studio, pas l’exemplaire qu’un lieu en fait.',
    noVenuesYet: 'Aucun lieu pour l’instant.',
    noVenuesHint: 'Il naît avec sa propriétaire, pour que quelqu’un puisse y entrer.',
    noBoundsShort: 'Sans bornes',
    removeHint: 'Retirer : rien ne pend encore à cet exemplaire.',
    unlinkHint: 'Délier de l’expérience du studio. L’exemplaire reste, avec ce qu’il a vendu.',
    venueWord: 'lieu',
    venuesWord: 'lieux',
    contactWord: 'contact',
    contactsWord: 'contacts',
    memberWord: 'membre',
    membersWord: 'membres',
    slotWord: 'créneau',
    slotsWord: 'créneaux',
    bookingWord: 'réservation',
    bookingsWord: 'réservations',
    headsetsWord: 'casques',
  },
  team: {
    title: 'Équipe',
    roles: 'Postes',
    invite: 'Inviter quelqu’un',
    byEmail: 'Par courriel',
    existingAccount: 'Compte existant',
    searchPlaceholder: 'Nom ou adresse',
    searchAll: 'Tous les comptes de Portage (studio) : tapez au moins deux caractères.',
    searchOperator: 'Les personnes de vos autres lieux, là où vous gérez l’équipe.',
    noCandidates: 'Aucun compte à ajouter.',
    alsoIn: 'aussi dans {venues}',
    addExisting: 'Ajouter à ce lieu',
    outOfScope: 'Ce compte appartient à une autre organisation : demandez au studio de l’ajouter à ce lieu.',
    inviteHint:
      'Le compte est créé tout de suite. Aucun courriel n’est encore envoyé — vous obtiendrez un lien à transmettre, et la personne choisit son mot de passe.',
    name: 'Nom',
    email: 'Courriel',
    jobTitle: 'Fonction',
    jobTitleHint:
      'Ce qui s’écrit sur une carte de visite. Purement descriptif — les droits sont le poste ci-dessous.',
    jobTitlePlaceholder: 'Directrice de la communication',
    preset: 'Poste',
    presetHint: 'Un poste accorde un ensemble de droits. Ajustez-les ci-dessous pour cette personne seulement.',
    custom: 'Ajusté',
    followsPreset: 'Suit le poste',
    resetToPreset: 'Revenir au poste',
    permissions: 'Droits',
    lastSeen: 'Dernière connexion',
    studioAccount: 'Compte du Studio Rabaska : il a déjà accès à tout, dans tous les lieux, au nuage comme en salle, sans être de l’équipe. Son poste ici ne change rien — vous pouvez le retirer.',
    never: 'Jamais',
    remove: 'Retirer de ce lieu',
    removeHint: 'Le compte reste, et tout ce que la personne a pointé avec. Seul son accès à ce lieu s’arrête.',
    resetLink: 'Lien pour ouvrir le compte',
    resetLinkHint:
      'Valable 48 heures, un seul usage. À transmettre de la main à la main — la personne choisit son mot de passe. Rien ne sera réaffiché.',
    resetLinkCopy: 'Copier le lien',
    cannotDemoteSelf: 'Vous ne pouvez pas vous retirer la gestion de l’équipe.',
    lastTeamManager: 'Quelqu’un doit pouvoir gérer l’équipe. Donnez ce droit à une autre personne d’abord.',
    cannotRemoveSelf: 'Vous ne pouvez pas vous retirer vous-même.',
    noMembers: 'Personne d’autre n’a encore accès.',
    groups: {
      day: 'La journée',
      tickets: 'Billetterie',
      money: 'Argent',
      customers: 'Clients et marketing',
      catalogue: 'Expériences',
      ops: 'Exploitation',
      onboarding: 'Onboarding',
      admin: 'Administration',
    },
  },
  settings: {
    scanBefore: 'Le scan ouvre avant le départ',
    scanBeforeHint: 'Combien de temps avant sa séance un billet est accepté à l’entrée.',
    scanAfter: 'Le scan ferme après le départ',
    scanAfterHint: 'Combien de temps après le départ un billet en retard passe encore.',
    scanRescan: 'Second passage toléré pendant',
    scanRescanHint: 'Un billet scanné deux fois dans ce délai est un double geste, pas une seconde entrée.',
    contents: 'Sommaire',
    title: 'Réglages',
    venue: 'Lieu',
    team: 'Équipe',
    role: 'Rôle',
    emailOutbox: 'Courriels envoyés',
    openMailpit: 'Ouvrir la boîte locale',
    mailpitHint: 'En développement, chaque courriel atterrit dans Mailpit au lieu d’une vraie boîte.',
    provider: 'Fournisseur',
    noWrite: 'En lecture pour l’instant — la modification du lieu viendra avec les écrans de catalogue.',
    language: 'Langue',
    slug: 'Identifiant',
    timezone: 'Fuseau',
    currency: 'Devise',
    name: 'Nom d’affichage',
    phone: 'Téléphone',
    city: 'Ville',
    region: 'Région',
    postalCode: 'Code postal',
    tagline: 'Slogan',
    studioOwned: 'Gardé par le studio',
    legalHint: 'Les vôtres, en tant qu’exploitant. Ceux du studio et du partenaire se déclarent ailleurs et apparaissent sur leur propre ligne.',
    filmingTitle: 'Tournage en cours',
    filmingHint: 'À activer pendant que vous filmez pour la promotion (essais, exploitation). L’inscription propose alors une case facultative, distincte de la décharge, pour autoriser l’utilisation de son image ; au tableau, les cartes de ceux qui ne l’ont pas donnée portent « Pas d’image » — ne pas les filmer, ou les flouter.',
    studioOwnedHint: 'L’identifiant est dans les URL publiques de réservation et dans les courriels déjà partis ; le fuseau et la devise réétiquetteraient chaque créneau et chaque montant déjà encaissés. Demandez à Rabaska.',
    contact: 'Coordonnées',
    address: 'Adresse',
    brand: 'Marque',
    selling: 'Règles de vente',
    legalName: 'Raison sociale',
    website: 'Site web',
    accentColor: 'Couleur d’accent',
    autoProbe: '{names} : en probe, ses casques n’annoncent rien — ses cartes avancent toujours à la main.',
    autoSilent: '{names} : ses casques n’ont encore jamais annoncé un début ni une fin. En automatique, ses cartes ne bougeront pas tant que son APK ne suit pas les visites (SDK Portage Room : room.TrackVisits).',
    startMode: 'Avancement des cartes',
    startModeHint:
      'Qui fait avancer une carte quand l’expérience démarre et se termine. En automatique, la salle le rapporte casque par casque et le tableau suit — c’est ce qu’il faut quand une personne seule tient quarante-cinq casques. À la main, chaque passage reste un geste voulu.',
    silentClose: 'Fin automatique d’une carte muette',
    forgotten: 'Retrait d’une carte oubliée',
    forgottenHint: 'Une carte appairée mais jamais démarrée depuis ce délai est retirée par la salle (visiteur reparti sans jouer, carte de test oubliée). Elle n’entre pas dans l’historique des visites. 0 = jamais.',
    silentCloseHint: 'Une carte en salle dont le casque est en veille ou injoignable sans interruption depuis ce délai est terminée par la salle (app plantée, casque posé). La carte prévient à mi-délai. 0 = jamais.',
    tempUnit: 'Unité de température',
    tempUnitHint:
      'Affichage seulement. Les casques rapportent toujours des degrés Celsius, et leurs propres seuils de bridage et d’extinction aussi.',
    startModeOperator: 'À la main',
    pairMode: 'Appairage des casques',
    pairModeHint:
      'En automatique, la salle donne à chaque personne enregistrée le meilleur casque libre (chargé, pas porté, dans la bonne salle), pour les œuvres qui appairent pendant l’enregistrement. L’équipe peut toujours le changer depuis la carte. À la main, l’équipe choisit chaque casque.',
    startModeAuto: 'Automatique',
    ticketRequired: 'Billet obligatoire à la porte',
    ticketRequiredHint:
      'Activé, c’est le billet qui ouvre la visite — le cas courant. Désactivé, la tablette devient la porte : on enregistre les gens sur place, sans réservation. Les créneaux, la jauge et la vente ne changent pas.',
    onlineCutoff: 'Fermeture de la vente en ligne',
    onlineCutoffHint: 'Minutes avant le départ.',
    cartTtl: 'Durée d’un panier',
    cartTtlHint: 'Minutes avant que les places retenues soient libérées.',
    cancellationWindow: 'Fenêtre d’annulation',
    cancellationWindowHint: 'Heures avant le départ où le visiteur peut encore annuler.',
    reminderLead: 'Rappel envoyé',
    reminderLeadHint: 'Heures avant le départ.',
    minutes: 'min',
    hours: 'h',
    saved: 'Enregistré.',
    capabilities: 'Peut faire',
    you: 'vous',
    roles: {
      owner: 'Propriétaire',
      manager: 'Gérance',
      marketing: 'Marketing',
      finance: 'Finances',
      guide: 'Opérateur',
      desk: 'Accueil',
      scan: 'Borne de scan',
      staff: 'Personnel',
    },
  },
  sites: {
    title: 'Sites web',
    noneForExperience: 'Aucune page pour cette expérience.',
    fromCatalogueHint: 'Une page tire sa durée, son âge, son prix d’appel et son adresse de cette fiche — rien à retaper.',
    createElsewhere: 'Les pages se créent depuis Sites web, où vivent le sous-domaine et l’enregistrement DNS.',
    newSite: 'Nouveau site',
    subdomain: 'Sous-domaine',
    hostname: 'Adresse',
    status: 'État',
    draft: 'Brouillon',
    comingSoon: 'Bientôt',
    open: 'Ouvert',
    ended: 'Terminé',
    dns: 'DNS',
    dnsNone: 'Non créé',
    dnsPending: 'En propagation',
    dnsActive: 'En ligne',
    dnsFailed: 'Échec',
    createDns: 'Créer l’enregistrement DNS',
    platformOnly: 'Seul un admin Rabaska peut créer un sous-domaine — le domaine appartient au studio.',
    sections: 'Sections',
    theme: 'Couleurs',
    seo: 'Moteurs de recherche',
    accent: 'Accent',
    background: 'Fond',
    textColor: 'Texte',
    enable: 'Affichée',
    leads: 'Adresses laissées',
    noSites: 'Aucun site. Créez-en un pour donner sa propre page à cette expérience.',
    visit: 'Ouvrir le site',
    derived: 'Rempli depuis la fiche de l’expérience',
    sectionHero: 'Héros',
    sectionGallery: 'Ce qu’il y a dedans',
    sectionPractical: 'Infos pratiques',
    sectionBooking: 'Réservation',
    sectionReviews: 'Avis',
    sectionGroups: 'Groupes',
    sectionFaq: 'Questions',
    sectionLocation: 'Accès',
    sectionText: 'Texte libre',
    sectionNewsletter: 'Capture d’adresse',
    resetSections: 'Rétablir le gabarit par défaut',
    subdomainHint: 'Imprimé sur des affiches et envoyé par courriel. À choisir une fois — le changer casse tous les liens déjà partis.',
    hostnameTaken: 'Ce sous-domaine est déjà utilisé par une autre page.',
  },
  cat: {
    edit: 'Modifier',
    newExperience: 'Nouvelle expérience',
    sheet: 'Fiche',
    prices: 'Tarifs',
    schedule: 'Programmation',
    slug: 'Identifiant',
    name: 'Nom',
    tagline: 'Accroche',
    description: 'Description',
    knowBefore: 'À savoir avant de venir',
    duration: 'Durée (min)',
    turnover: 'Remise en état (min)',
    capacity: 'Capacité',
    minParticipants: 'Minimum pour partir',
    minAge: 'Âge minimum',
    groupSize: 'Taille de groupe',
    preassign: 'Appairer les casques pendant l’enregistrement',
    preassignHint:
      'Le local technique prépare les casques pendant que les visiteurs signent. À laisser fermé dans un petit lieu qui les donne au fur et à mesure.',
    autoStart: 'Démarre toute seule',
    autoStartHint:
      'L’expérience part dès que le groupe est au complet. Les opérateurs n’appuient plus sur « Démarré » sur chaque carte — quelqu’un ouvre toujours la séance.',
    staffRequired: 'Opérateurs requis',
    published: 'Publiée',
    heroImage: 'Image d’en-tête',
    newPrice: 'Ajouter un tarif',
    priceName: 'Nom',
    priceAmount: 'Prix',
    weight: 'Places consommées',
    audience: 'Public',
    audienceAdult: 'Adulte',
    audienceMinor: 'Enfant',
    audienceAny: 'Non précisé',
    publicPrice: 'Vendu en ligne',
    activePrice: 'Actif',
    newSchedule: 'Ajouter une plage',
    from: 'Du',
    to: 'Au',
    weekdays: 'Jours',
    firstDeparture: 'Premier départ',
    lastDeparture: 'Dernier départ',
    every: 'Toutes les',
    minutes: 'min',
    previewSchedule: 'Prévisualiser',
    departures: 'départs',
    sessionsCreated: 'sessions créées',
    tooTight: 'L’intervalle est plus court que l’expérience — les départs se chevaucheront.',
    deleteSchedule: 'Supprimer',
    noPrices: 'Aucun tarif. Il en faut un pour vendre.',
    noSchedules: 'Aucune plage. Ajoutez-en une pour créer des créneaux.',
  },
  welcomeMirror: {
    hint: 'Les tablettes et les téléviseurs du lieu, tels que Portage Cloud les voit. On les déclare, les règle et les révoque depuis le nuage.',
    notYet: 'La salle n’a pas encore reçu la liste du nuage : elle arrive à la prochaine synchronisation.',
  },
  uploads: {
    sending: 'Envoi de {name} — {pct} %',
    keepOpen: 'Vous pouvez changer d’écran ; ne fermez pas cet onglet.',
  },
  screens: {
    title: 'Écrans',
    newScreen: 'Ajouter un écran',
    screenName: 'Où est-il ?',
    unknown: 'Cet écran n’est pas reconnu. Demandez une nouvelle adresse à l’accueil.',
    tapToEnable: 'Touchez pour démarrer',
    stateOnline: 'En ligne',
    stateOffline: 'Hors ligne',
    stateError: 'Erreur',
    lastPlayed: 'Dernier briefing le',
    neverPlayed: 'N’a jamais joué de briefing',
    stillLoading: 'Chargement du briefing…',
    stuck: 'Le briefing n’avance pas.',
    stuckHint: 'La lecture a été autorisée mais rien ne bouge — regardez le fichier et la sortie audio de l’écran.',
    playFailed: 'Ce briefing ne se lance pas. C’est le fichier qu’il faut regarder, pas la télécommande.',
    tapHint: 'Ce navigateur a refusé de lancer le son. Un seul geste l’autorise pour toute la session — un écran correctement réglé ne demande jamais.',
    qrCaption: 'Scannez pour vous enregistrer avant d’entrer',
    remote: 'Écrans',
    play: 'Jouer',
    pause: 'Pause',
    stop: 'Éteindre',
    pageKind: 'Page',
    showQr: 'Afficher le QR-code',
    noScreens: 'Aucun écran pour l’instant.',
    noKiosks: 'Aucune tablette pour l’instant.',
    noKiosksHint: 'Déclarez-en une, puis ouvrez son adresse sur la tablette. Elle reste sur cette page toute la journée.',
    noScreensHint: 'Déclarez-en un, puis tapez son adresse dans le navigateur du téléviseur. Rien à installer.',
    noMedia: 'Aucune vidéo pour les expériences que vous jouez. Le studio les dépose avec l’expérience.',
    resume: 'Reprendre',
    ackSent: 'Envoyé — l’écran doit venir le chercher…',
    ackTaken: 'L’écran a pris la commande.',
    ackSilent: 'L’écran ne répond pas. Est-il allumé, et est-ce bien celui de ce lieu ?',
    briefing: 'Briefing',
    trailer: 'Bande-annonce',
    lastSeen: 'Vu',
    never: 'jamais',
    address: 'Adresse à taper sur le téléviseur',
  },
  group: {
    title: 'Mes lieux',
    allVenues: 'Tous les lieux',
    enterHint: 'Choisissez un lieu pour y travailler',
    revenue: 'Recettes',
    seats: 'Places',
    contacts: 'Contacts',
    lastDays: '{n} derniers jours',
    sharedContacts: '{n} connus de plus d’un lieu',
    noSharedYet: 'Aucun connu de plus d’un lieu pour l’instant.',
    byVenue: 'Lieu par lieu',
    upcoming: 'Déjà vendu',
    team: 'Équipe',
    sharedTeam: '{n} travaillent dans plus d’un lieu',
    noTeam: 'Aucun membre dans les lieux que vous pouvez voir.',
    targetVenues: 'Envoyer à',
    targetHint: 'Seulement les lieux où vous pouvez envoyer. Un contact connu de deux d’entre eux ne reçoit qu’un courriel.',
    thisVenueOnly: 'Ce lieu seulement',
  },
  empty: {
    experiencesHint: 'Une expérience, c’est ce que vous vendez : sa durée, sa capacité, ses tarifs. Créez-en une et le calendrier se remplit tout seul depuis sa programmation.',
    campaignsHint: 'Une campagne écrit à un segment de votre propre fichier — et Portage Cloud vous dit combien de billets elle a vendus, pas combien de gens l’ont ouverte.',
    flowsHint: 'Un parcours, c’est ce que les visiteurs remplissent avant de jouer : quelques questions, un NDA si l’expérience n’est pas sortie, et la décharge. Sur une tablette, par QR-code, ou par un lien dans leur confirmation.',
    sitesHint: 'Un site donne à cette expérience sa propre page sur sa propre adresse, avec le tunnel de réservation dedans — pas un lien vers la place de marché de quelqu’un d’autre.',
    filteredHint: 'Rien ne correspond à ce filtre. Élargissez-le, ou effacez la recherche.',
  },
  eov: {
    title: 'Fin de visite',
    hint: 'Quand une carte passe dans « Terminé », son casque reprend ces réglages. Un casque hors ligne à ce moment les reçoit à son retour, et au plus tard avant son appairage suivant.',
    noneHint: 'Choisissez un préréglage du studio : chaque casque de cette salle y reviendra à la fin de chaque visite.',
    noneRoom: 'Aucun réglage de fin de visite pour cette salle. Ils se choisissent depuis Portage Cloud, dans Salles.',
    noPresets: 'Le studio n’a pas encore de préréglage. Créez-en un dans la console Portage → Inventaire du studio → Préréglages de casque.',
    none: '— Aucun —',
    fromCloud: 'Choisi depuis Portage Cloud.',
    on: 'activé',
    off: 'coupé',
    errors: {
      preset_not_found: 'Ce préréglage n’existe plus.',
      venue_without_studio: 'Ce lieu n’est rattaché à aucun studio : il n’a pas de préréglage.',
      cloud_only: 'Les réglages de fin de visite se choisissent depuis Portage Cloud.',
    },
  },
  presets: {
    hint: 'Un préréglage est un jeu de valeurs de casque — volume, luminosité, kiosque… Un lieu en choisit un comme réglages de fin de visite : chaque casque y revient quand une carte passe « Terminé ». Un préréglage ne touche qu’aux réglages qu’il porte.',
    create: 'Nouveau préréglage',
    none: 'Aucun préréglage pour l’instant.',
    name: 'Nom',
    namePlaceholder: 'Fin de visite — standard',
    note: 'Note',
    values: 'Valeurs',
    edit: 'Modifier',
    untouched: 'non touché',
    save: 'Enregistrer le préréglage',
  },
  xcfg: {
    tab: 'Configuration',
    none: 'Aucune configuration distante pour cette expérience.',
    noneHint: 'Le studio n’a déclaré aucun réglage pour cette expérience, ou n’a encore rien publié.',
    hint: 'Ce que joue ce lieu, d’après la publication n° {n} du studio. Seul le studio la change : demandez-lui une modification.',
    editInConsole: 'Modifier dans la console Portage',
    key: 'Réglage',
    value: 'Valeur jouée',
    origin: 'Origine',
    set: 'Réglé par le studio',
    default: 'Défaut',
    on: 'activé',
    off: 'coupé',
  },
  rooms: {
    logs: {
      title: 'Journal du serveur de l’expérience',
      subtitleRoom: 'Lu sur cette machine (dossier Unity monté dans Portage Room). Clés, jetons et courriels sont masqués.',
      subtitleCloud: 'Demandé à la salle : elle répond à sa prochaine synchronisation, en quelques secondes si elle est en ligne. Clés, jetons et courriels sont masqués.',
      file: 'Journal',
      player: 'Unity, lancement en cours',
      playerPrev: 'Unity, lancement précédent',
      sdk: 'SDK Portage',
      lines: 'Lignes',
      lastLines: '{n} dernières lignes',
      filter: 'Filtrer…',
      problemsOnly: 'Problèmes seulement',
      refresh: 'Rafraîchir',
      download: 'Télécharger',
      waiting: 'Demande envoyée à la salle, en attente de sa réponse…',
      truncated: 'Début coupé : seule la fin du fichier est affichée.',
      empty: 'Aucun journal dans le dossier monté pour l’instant (Player.log, Player-prev.log, portageroom_*.jsonl).',
      notMounted: 'Le dossier Unity du serveur de l’expérience n’est pas monté dans Portage Room. Ajoutez ce volume au « docker run » de portage-salle (en lecture seule), puis recréez le conteneur :',
      errors: { room_silent: 'La salle n’a pas répondu en une minute : est-elle en ligne ?', not_found: 'Ce journal n’existe plus sur la machine.', studio_only: 'Réservé aux comptes du studio.', not_a_room: 'Seul Portage Room lit le journal directement.' } as Record<string, string>,
    },
    link: {
      title: 'Activité du lien',
      outboxTitle: 'Envoyé par la salle au nuage',
      outboxInRoom: 'Ce que la salle envoie se lit dans Portage Room (sa file d’envoi y vit).',
      commandsTitle: 'Commandes envoyées au serveur de l’expérience',
      none: 'Rien pour l’instant.',
      when: 'Quand',
      what: 'Quoi',
      state: 'État',
      error: 'Erreur',
      target: 'Cible',
      sent: 'Envoyé',
      refused: 'Refusé',
      retrying: 'En échec, réessai',
      pending: 'En attente',
      acked: 'Acquittée',
      failed: 'Refusée',
      delivered: 'Remise',
    },
    showLog: 'Voir le journal',
    showLink: 'Voir l’activité',
    none: 'Aucune salle déclarée pour l’instant.',
    noneHint: 'Une salle est un espace où tourne une expérience, avec son propre serveur de l’expérience. Nommez-la ici, puis rattachez-lui son serveur.',
    headsets: '{n} casque{s}',
    server: 'Serveur de l’expérience',
    noServer: 'Aucun serveur ne sert encore cette salle : rattachez-le plus bas, une fois qu’il a donné signe de vie.',
    network: 'Réseau et moteur',
    networkOnRoom: 'Se règle sur Portage Room : le jeton des casques ne le quitte pas.',
    map: 'Carte d’espace',
    mapOnCloud: 'Se pousse depuis Portage Cloud, qui tient la bibliothèque de cartes du studio.',
    noMaps: 'La bibliothèque du studio n’a pas encore de carte (console Portage → Inventaire du studio → Cartes d’espace).',
    pickMap: 'Carte de la bibliothèque du studio',
    unassignedTitle: 'Serveurs sans salle',
    unassignedHint: 'Un serveur de l’expérience qui a donné signe de vie sans salle : choisissez celle qu’il sert.',
    fromCloud: 'Les salles se déclarent dans Portage Cloud et descendent sur ce Portage Room. On y rattache ici les serveurs et on y règle le réseau de chaque salle.',
    ready: '✓ Prête à jouer',
    notReady: 'Pas encore prête',
    reportedByRoom: 'rapporté par Portage Room à {date}',
    computedByCloud: 'calculé par Portage Cloud — le réseau et le jeton ne sont connus que de Portage Room',
    refusedClear: 'Remettre à zéro',
    refusedCleared: '{n} refus écarté(s).',
    outboxKinds: {
      'visit.create': 'Carte de visite créée',
      'visit.move': 'Carte déplacée',
      'visit.pair': 'Casque appairé',
      'visit.group': 'Groupe',
      'visit.help': 'Appel à l’aide',
      'visit.personal': 'Paramètres personnels',
      'command.ack': 'Accusé de commande',
      'run.upsert': 'Séance',
      'events.relay': 'Télémétrie',
      'assign.relay': 'Attribution de casque',
      'fleet.add.relay': 'Casque ajouté',
      'message.trace': 'Message aux casques',
      'alert.trace': 'Alerte',
      'diagnostics.relay': 'Diagnostic',
    } as Record<string, string>,
    refusedStatus: {
      '400': 'refusé, contenu invalide',
      '401': 'refusé, clé non reconnue',
      '403': 'refusé, non autorisé',
      '404': 'refusé, inconnu du nuage',
      '409': 'refusé, conflit',
      '413': 'refusé, trop gros',
      '422': 'refusé, incomplet',
    } as Record<string, string>,
    checks: {
      server: 'Serveur de l’expérience',
      agent: 'Pilotage des casques',
      xroam: 'Serveur XROAM',
      cloud: 'Lien avec Portage Cloud',
      battery: 'Batteries',
      thermal: 'Chaleur',
      asleep: 'Veille',
      remote_file: 'Fichier Portage des casques',
      network: 'Réglages réseau',
      token: 'Jeton des casques',
      headsets: 'Casques',
      versions: 'Versions',
      map: 'Carte d’espace',
      software: 'Version de Portage',
      schema: 'Base de la salle',
    },
    notReadyCount: 'Pas encore prête — {summary}',
    countFail: '{n} à régler',
    countWarn: '{n} à surveiller',
    countUnknown: '{n} à vérifier',
    andMoreOne: 'et 1 autre',
    fromCloudShort: 'Les salles viennent de Portage Cloud.',
    unplacedShow: 'Voir la liste',
    unplacedHide: 'Masquer',
    moreDetails: 'Détails',
    lessDetails: 'Moins',
    noTargetLine: 'Aucun APK publié pour ce lieu : la colonne État apparaîtra dès qu’un le sera.',
    goto: 'Y aller',
    andMore: 'et {n} autres',
    families: { network: 'Réseau', security: 'Sécurité', headsets: 'Casques', content: 'Contenu' },
    boardTitle: 'Contrôles',
    boardHint: 'Un feu par salle. Rouge : on ne peut pas jouer. Orange : ça joue, à surveiller. Vert : prête.',
    boardCounts: '{fail} à régler · {warn} à surveiller',
    boardSee: 'Voir',
    serverSettings: 'Réglages du serveur',
    noHeadsetToMeasure: 'Aucun casque ne répond : rien à mesurer.',
    toFix: '{n} à régler',
    serverOnRoom: 'Son nom, son adresse et sa salle se règlent sur Portage Room (l’engrenage à côté du serveur).',
    serverRoom: 'Salle qu’il sert',
    serverRoomHint: 'La salle dont il pilote les casques. Le déplacer déplace sa checklist et ses casques.',
    serverState: 'État',
    serverSeen: 'dernier signe de vie {when}',
    serverIdentity: 'Identité',
    serverVersion: 'Version',
    serverToken: 'Jeton des casques tenu',
    serverTokenMatch: '✓ le même que la salle',
    serverTokenMismatch: 'différent de la salle : les casques n’apparaîtront pas au parc',
    serverForget: 'Oublier ce serveur',
    serverForgetTitle: 'Oublier {name} ?',
    serverForgetBody: 'Pour une machine remplacée. Sa fiche part ; ses séances et son historique restent. S’il revient avec la clé de la salle, il réapparaît tout seul.',
    serverForgetOnline: 'Seul un serveur qui ne donne plus signe de vie peut être oublié.',
    serverForgotten: 'Serveur oublié.',
    serverSettingsOf: 'Réglages — {name}',
    serverName: 'Nom',
    serverIp: 'Adresse sur le réseau de la salle',
    serverIpHint: 'L’IP par laquelle les casques joignent cette machine sur le Wi‑Fi de la salle. Elle pré-remplit l’onglet « Réseau » du serveur de l’expérience.',
    serverAppears: 'Un serveur de l’expérience apparaît tout seul à son premier signe de vie, avec la clé de la salle (Salles → Clés). Ici, on le nomme et on dit où il est.',
    lights: { fail: 'Ne peut pas jouer', warn: 'À surveiller', ok: 'Prête', unknown: 'Inconnu' },
    fields: {
      agentUrl: 'l’adresse de pilotage des casques (7791)',
      token: 'le jeton des casques',
      networkServer: 'le serveur de jeu Portage Room',
      xroamIp: 'le serveur XROAM',
    },
    help: {
      'agent.fail.unreachable': {
        why: 'L’adresse de pilotage ne répond pas à {items} : les casques ne recevraient ni commandes ni réglages.',
        fix: 'Vérifiez que le serveur de l’expérience (ou le serveur Atlantis en hybride) tourne, et l’adresse dans l’onglet « Réseau » du serveur de l’expérience.',
        gotoRoom: 'network',
      },
      'agent.unknown.not_set': { why: 'Pas encore d’adresse de pilotage des casques.', fix: 'À remplir dans l’onglet « Réseau » du serveur de l’expérience.', gotoRoom: 'network' },
      'agent.unknown.room_only': {
        why: 'Seul Portage Room peut le joindre, et il n’a rien rapporté depuis 10 minutes.',
        fix: 'Vérifiez que Portage Room est allumé et relié à Internet.',
      },
      'xroam.fail.not_set': { why: 'Aucune adresse de serveur XROAM pour cette salle.', fix: 'À remplir dans le bloc du serveur de l’expérience, onglet « XROAM ».', gotoRoom: 'xroam' },
      'xroam.fail.unreachable': {
        why: 'La machine XROAM ne répond pas à {items}, ni son secours : les expériences hybrides ne peuvent pas démarrer.',
        fix: 'Allumez le serveur XROAM, vérifiez qu’il est sur le réseau de la salle, et l’adresse et le port. Sous Windows, le pare-feu jette en silence le test (TCP) quand aucune règle n’ouvre le port : ajoutez une règle TCP sur 7777, en plus de l’UDP.',
        gotoRoom: 'network',
      },
      'xroam.warn.backup_only': {
        why: 'La machine XROAM principale ne répond pas ({items}) ; seul le secours répond.',
        fix: 'Ça joue sur le secours. Vérifiez le serveur principal avant la prochaine séance.',
        gotoRoom: 'network',
      },
      'xroam.warn.no_account': {
        why: 'Le compte XROAM du lieu n’est pas en place : ça joue, mais les visiteurs ne sont pas inscrits chez XROAM.',
        fix: 'Le Studio Rabaska le met en place (console → Moteurs → Comptes des lieux).',
      },
      'xroam.warn.account_blocked': {
        why: 'Le formulaire d’XROAM a changé : les inscriptions sont retenues le temps que le studio vérifie la correspondance.',
        fix: 'Le Studio Rabaska les relâche depuis la console → Moteurs. Rien n’est perdu.',
      },
      'xroam.unknown.room_only': {
        why: 'Seul Portage Room peut joindre la machine XROAM, et il n’a rien rapporté depuis 10 minutes.',
        fix: 'Vérifiez que Portage Room est allumé et relié à Internet.',
      },
      'cloud.fail.error': {
        why: 'Portage Room n’arrive pas à se synchroniser avec Portage Cloud : {items}.',
        fix: 'Vérifiez son accès à Internet. Il continue de fonctionner en local et rattrape quand le lien revient.',
      },
      'cloud.fail.no_sync': {
        why: 'Aucune synchronisation avec Portage Cloud depuis {since}.',
        fix: 'Vérifiez son accès à Internet. Il continue de fonctionner en local et rattrape quand le lien revient.',
      },
      'cloud.fail.no_report': {
        why: 'Portage Room n’a rien rapporté depuis {since} : éteint, sans Internet, ou qui ne joint pas ce nuage.',
        fix: 'Vérifiez Portage Room et son accès à Internet.',
      },
      'cloud.warn.refused': {
        why: '{items} message(s) de la salle ont été refusés par Portage Cloud ces dernières 24 heures.',
        fix: 'Prévenez le Studio Rabaska : un geste fait en salle n’est pas arrivé au bureau.',
      },
      'cloud.warn.pending': {
        why: '{items} message(s) attendent de monter à Portage Cloud.',
        fix: 'En général une connexion lente ou coupée ; ça rattrape seul.',
      },
      'battery.warn': { why: '{n} sous 30 % et pas en charge : {items}.', fix: 'Rechargez-les entre deux séances.' },
      'thermal.warn': { why: '{n} bridé(s) par la chaleur : {items}.', fix: 'Laissez-les refroidir (hors du soleil, ventilation) avant la prochaine séance : l’image peut saccader.' },
      'asleep.warn': {
        why: '{n} attribué(s) à un visiteur mais en veille : {items}. Ils répondent au réseau mais refuseraient toute commande.',
        fix: 'Mettez-les sur la tête, ou appuyez sur leur bouton d’alimentation, pour les réveiller.',
      },
      'remote_file.warn': {
        why: 'Sur {n} casque{s}, portageroom-remote.json n’est pas ce que Portage a écrit (absent, illisible, ou une autre adresse, un autre jeton, un autre état de « Couper Portage ») : {items}.',
        fixRoom: 'Sous « Casques et versions », cochez-les puis « Mettre à jour… » → « Reconfigurer seulement ». Un fichier disparu veut souvent dire que l’app a été désinstallée (Build and Run depuis un poste de dev).',
        fixCloud: 'Reconfigurez-les depuis Portage Room (Salles → Versions).',
        gotoRoom: 'versions',
      },
      'server.fail.none': {
        why: 'Aucun serveur de l’expérience n’est rattaché à cette salle.',
        fix: 'Démarrez le build serveur Unity sur la machine de la salle. Il apparaît sous « Serveurs sans salle » : choisissez-y cette salle.',
      },
      'server.fail.running_mute': {
        why: 'Le serveur de l’expérience tourne — il répond au pilotage ({items}) — mais n’envoie rien à Portage.',
        fix: 'Sur sa machine, vérifiez sa clé (C:\\ProgramData\\Portage\\portageroom.json, champ « ingestKey » : la clé du serveur de CETTE salle, pas celle d’un autre lieu ; et pas de « disabled »: true) et l’adresse de Portage qu’il vise (127.0.0.1:8080 quand Portage tourne sur la même machine). Son journal dit « clé refusée » ou « injoignable ».',
      },
      'server.warn.device_rejected': {
        why: 'Le serveur bat, mais Portage refuse une partie de ce qu’il envoie : {items}. Ces appareils manquent au tableau.',
        fix: 'Un champ que Portage n’accepte pas : transmettez cette ligne à Portage Room (SDK) ou à l’équipe Portage.',
      },
      'server.fail.rejected': {
        why: 'Le serveur de l’expérience envoie, mais Portage Room refuse ce qu’il envoie (400 invalid_body) : {items}.',
        fix: 'Le SDK envoie un champ que Portage n’accepte pas : transmettez cette ligne à Portage Room (SDK) ou à l’équipe Portage. Relancer n’y changera rien.',
      },
      'server.fail.offline': {
        why: 'Le serveur de l’expérience ne donne plus signe de vie depuis {since}.',
        fix: 'Vérifiez que la machine est allumée et que le build serveur tourne ; il se reconnecte seul.',
      },
      'network.fail.missing': {
        why: 'Il manque {items}.',
        fixRoom: 'À remplir dans l’onglet « Réseau » du serveur de l’expérience plus bas. Les casques les reçoivent à l’installation.',
        fixCloud: 'Ils se saisissent dans Portage Room (Salles → cette salle → serveur de l’expérience) : le jeton des casques ne quitte pas la salle.',
        gotoRoom: 'network',
      },
      'network.fail.port_clash': {
        why: 'Le serveur de l’expérience (réseau Portage) et le serveur XROAM sont tous deux sur {items} : un seul des deux pourra écouter ce port.',
        fixRoom: 'Changez l’un des deux ports dans l’onglet « Réseau » du serveur de l’expérience (réseau Portage : 7780 par défaut, et FISHNET_PORT sur le serveur de l’expérience), puis « Reconfigurer » les casques.',
        fixCloud: 'À changer sur Portage Room (Salles → cette salle → serveur de l’expérience), avec FISHNET_PORT sur le serveur de l’expérience.',
        gotoRoom: 'network',
      },
      'network.unknown.room_only': {
        why: 'Seul Portage Room peut le vérifier, et il n’a rien rapporté depuis 10 minutes.',
        fix: 'Vérifiez que Portage Room est allumé et relié à Internet.',
      },
      'token.unknown.no_server': {
        why: 'Aucun serveur d’expérience ne s’est encore présenté dans cette salle : personne ne peut confirmer le jeton des casques.',
        fix: 'Lancez le serveur de l’expérience (Atlantis en hybride) et vérifiez qu’il joint cette instance : son journal doit dire « agent démarré vers … », sans « battement refusé ». S’il apparaît sous « Serveurs sans salle », rangez-le ici.',
      },
      'token.fail.missing': {
        why: 'Aucun jeton des casques : les casques n’auraient rien à présenter au serveur de l’expérience (le nôtre, ou Atlantis en hybride), et n’apparaîtraient pas. Ce n’est pas la clé d’ingestion.',
        fix: 'Depuis le SDK 0.17.27, il se dérive de la clé d’ingestion : donnez à ce Portage Room son portageroom.json (PORTAGEROOM_FILE, champ « ingestKey »), le même fichier que le serveur de l’expérience. Pour un serveur encore sur un SDK plus ancien : collez son remote-token.txt dans l’onglet « Jeton des casques », sous Dépannage.',
        gotoRoom: 'network',
      },
      'token.fail.file_missing': {
        why: 'Le fichier du jeton est déclaré mais introuvable : {items}.',
        fix: 'Un serveur construit sous un autre nom de produit l’écrit ailleurs : corrigez ROOM_TOKEN_FILE, ou collez le jeton dans l’onglet « Jeton des casques ».',
        gotoRoom: 'network',
      },
      'token.warn.file_missing': {
        why: 'Le fichier du jeton est introuvable ({items}) ; c’est le jeton collé qui sert, et il correspond.',
        fix: 'Ça fonctionne. Corrigez ROOM_TOKEN_FILE pour qu’un nouveau jeton du serveur soit repris sans copier-coller.',
      },
      'token.fail.mismatch': {
        why: 'Le jeton enregistré n’est pas celui du serveur de l’expérience (empreintes différentes) : les casques seraient refusés.',
        fix: 'Le serveur de l’expérience et ce Portage Room doivent lire le MÊME portageroom.json : même ingestKey, même roomTokenVersion. Un serveur sur un SDK antérieur à 0.17.27 garde son remote-token.txt : mettez-le à jour, ou collez ce jeton sous Dépannage. Puis « Reconfigurer » les casques.',
        gotoRoom: 'network',
      },
      'token.unknown.not_announced': {
        why: 'Le serveur de l’expérience n’annonce pas l’empreinte de son jeton : impossible de vérifier.',
        fix: 'Mettez le SDK Portage Room du build serveur en 0.13.1 ou plus.',
      },
      'token.unknown.room_only': {
        why: 'Seul Portage Room peut le vérifier, et il n’a rien rapporté depuis 10 minutes.',
        fix: 'Vérifiez que Portage Room est allumé et relié à Internet.',
      },
      'headsets.fail.0': {
        why: 'Le serveur de l’expérience n’a vu aucun casque.',
        fix: 'Allumez les casques et lancez l’expérience. Pour un casque neuf : Salles → « Ajouter des casques ».',
      },
      'headsets.warn': {
        why: 'En cours d’usage mais muet : {items}. Quelqu’un porte un casque que Portage ne suit plus.',
        fix: 'Vérifiez ce casque : allumé, sur le Wi‑Fi de la salle, expérience lancée. Un casque au repos dans le bac n’est pas un problème et n’apparaît pas ici.',
      },
      'versions.warn': {
        why: '{n} ne sont pas en {target} : {items}.',
        fixRoom: 'Utilisez « Mettre à jour » sous Versions.',
        fixCloud: 'La mise à jour se lance depuis Portage Room (Salles → Versions).',
        gotoRoom: 'versions',
        gotoCloud: 'versions',
      },
      'versions.unknown.nothing_published': {
        why: 'Le studio n’a publié aucun APK pour ce lieu : aucune version à attendre.',
        fix: 'Le studio publie depuis la console Portage → Inventaire du studio → APK → « Publié pour ».',
      },
      'schema.fail.behind': {
        why: 'La base de la salle a {room} migration(s), son code en attend {cloud} : tout ce qui touche les tables nouvelles échoue (erreurs 500), et le serveur de l’expérience ne peut pas battre.',
        fix: 'Lancez les migrations avec la même image (docker run … server/scripts/migrate.ts, voir docs/SALLE-SEULE.md), puis redémarrez l’application.',
      },
      'server.warn.outbox_dropped_visits': {
        why: 'Le serveur a dû jeter des événements de sa boîte d’envoi, faute d’avoir joint Portage longtemps, y compris des débuts ou des fins de visite (événements / visites) : {items}. Des visites manquent à l’Analytique sur cette période.',
        fix: 'Rien à refaire : il a rattrapé ce qu’il pouvait. Cherchez pourquoi il ne joignait plus Portage (instance arrêtée, réseau), pour que ça ne se reproduise pas.',
      },
      'server.warn.outbox_dropped_unknown': {
        why: 'Le serveur a dû jeter des événements de sa boîte d’envoi, faute d’avoir joint Portage longtemps : {items}. Des détails manquent à l’Analytique sur cette période, et peut-être des visites : son SDK (antérieur à 0.17.8) ne distingue pas les deux.',
        fix: 'Rien à refaire. Cherchez pourquoi il ne joignait plus Portage, et passez son SDK en 0.17.8 ou plus pour savoir si des visites sont perdues.',
      },
      'server.warn.outbox_dropped': {
        why: 'Le serveur a dû jeter des événements de sa boîte d’envoi, faute d’avoir joint Portage longtemps : {items}. Il manque à l’Analytique des détails de scènes, de métriques et de performances sur cette période ; les visites, elles, sont entières.',
        fix: 'Rien à refaire : il a rattrapé ce qu’il pouvait. Cherchez pourquoi il ne joignait plus Portage (instance arrêtée, réseau), pour que ça ne se reproduise pas.',
      },
      'software.warn.behind_schema': {
        why: 'Cette salle tourne sur Portage {room}, le nuage sur {cloud} : {lag} migration(s) de retard. Elle joue encore, mais ignore sans le dire ce que le nuage lui envoie de nouveau.',
        fixRoom: 'Mettre à jour cette machine : l’image au tag du nuage ({cloud}), ses migrations, puis relancer l’application.',
        fixCloud: 'La mise à jour se fait sur la machine du lieu : l’image au tag du nuage ({cloud}), ses migrations, puis une relance.',
      },
      'software.warn.behind': {
        why: 'Cette salle tourne sur Portage {room}, plus ancien que le nuage ({cloud}). Elle joue encore ; des nouveautés récentes n’y sont pas.',
        fixRoom: 'Mettre à jour cette machine : l’image au tag du nuage ({cloud}), ses migrations, puis relancer l’application.',
        fixCloud: 'La mise à jour se fait sur la machine du lieu : l’image au tag du nuage ({cloud}), ses migrations, puis une relance.',
      },
      'software.warn.untracked': {
        why: 'Cette salle tourne sur une version de Portage antérieure au suivi des versions, donc plus ancienne que le nuage ({cloud}).',
        fixRoom: 'Mettre à jour cette machine : l’image au tag du nuage ({cloud}), ses migrations, puis relancer l’application.',
        fixCloud: 'La mise à jour se fait sur la machine du lieu : l’image au tag du nuage ({cloud}), ses migrations, puis une relance.',
      },
      'map.unknown.never_pushed': {
        why: 'Aucune carte d’espace n’a été poussée sur les casques de cette salle.',
        fix: 'Déposez le relevé de la salle dans « Carte d’espace » plus bas, puis posez-le sur les casques.',
        gotoCloud: 'map',
        gotoRoom: 'map',
      },
      'map.warn': {
        why: '{n} n’ont pas encore confirmé l’installation : {items}.',
        fix: 'Ils l’installent au prochain lancement de l’expérience : allumez-les.',
      },
      'map.fail': {
        why: 'L’installation a échoué sur {n} : {items}.',
        fix: 'Repoussez la carte ; si ça échoue encore, ouvrez ses réglages (Salles, ou « ··· › Paramètres du casque » sur sa carte).',
        gotoCloud: 'map',
      },
    },
    mapStatus: '{name} : {ok} installée(s), {pending} en attente, {failed} en échec — poussée le {date}',
    versions: 'Casques et versions',
    target: 'Publié pour ce lieu : {label} {version}',
    installed: 'Installée',
    behind: 'En retard',
    ahead: 'Plus récente que l’APK publié',
    upToDate: 'À jour',
    installWhat: 'APK à installer',
    unplaced: '{n} casques ne sont rangés dans aucune salle.',
    placeHere: 'Les ranger dans {room}',
    placed: '{n} casques rangés dans {room}.',
    installOn: 'Installer {version} sur {n} casque{s}',
    notInstalled: 'Pas installée',
    awaitingHeadset: '{version} installée, en attente du casque',
    noHeadsetsHere: 'Aucun casque dans cette salle. Ajoutez-en depuis Casques (+) en choisissant cette salle.',
    checking: 'Vérification des salles : serveur, pilotage, XROAM, casques… quelques secondes.',
    headsetsLoading: 'Chargement des casques de la salle et de leurs versions…',
    ipColumn: 'IP Wi-Fi',
    liveColumn: 'En direct',
    headsetSearch: 'Rechercher un casque : nom, série, IP…',
    picked: '{n} sélectionné(s)',
    pickAllHeadsets: 'Sélectionner les {n} casques',
    pickAllFiltered: 'Sélectionner les {n} casques du filtre',
    unpickAll: 'Tout désélectionner',
    noHeadsetMatch: 'Aucun casque ne correspond.',
    pageOf: '{from}–{to} sur {n}',
    pagePrev: 'Page précédente',
    pageNext: 'Page suivante',
    ipUnknown: 'Inconnue',
    ipSet: 'Renseigner',
    ipEdit: 'Modifier',
    noIpLate: 'Non comptés, faute d’adresse IP : {names}. L’installation passe par ADB en Wi-Fi (casque : Paramètres → Wi-Fi → le réseau → adresse IP).',
  },
  install: {
    cloudOnlyRoom: 'L’installation se fait depuis Portage Room.',
    cloudHint: 'C’est lui qui est sur le réseau des casques, et le jeton des casques ne le quitte pas. Ouvrez Portage Room pour installer.',
    room: 'Salle',
    settings: 'Ce que {room} pose sur chaque casque',
    settingsHint: 'Rempli une fois, modifiable à tout moment. Les casques reçoivent ces adresses et le jeton des casques à chaque installation ; « Reconfigurer » pousse un changement sans réinstaller.',
    settingsReady: '{room} : réglages réseau complets.',
    settingsMissing: '{room} : réglages réseau incomplets (canal d’appareil ou jeton des casques) — à compléter dans',
    portageBlock: 'Expériences Portage Room',
    xroamBlock: 'Expériences XROAM (hybrides)',
    agentUrl: 'Adresse de pilotage des casques (7791)',
    hostLabel: 'Machine du serveur de l’expérience (IP)',
    hostHintPortage: 'Son adresse sur le Wi‑Fi de la salle, telle que les casques la voient. Ils s’y pilotent (7791) et y jouent (Portage Room).',
    hostHintXroam: 'L’adresse du serveur Atlantis sur le Wi‑Fi de la salle, telle que les casques la voient. Ils s’y pilotent (7791) et y jouent (XROAM).',
    splitAddresses: 'Adresses séparées',
    joinAddresses: 'Une seule machine',
    thisMachine: 'L’adresse de cet ordinateur',
    hostDetected: 'Rempli avec l’adresse que Portage connaît pour cette machine (annoncée par le serveur de l’expérience, ou posée au lancement). Vérifiez-la avant d’enregistrer.',
    thisMachineUnknown: 'L’adresse réseau de cette machine n’est pas encore connue : le serveur de l’expérience ne l’a pas annoncée et le lancement ne l’a pas posée. Saisissez-la une fois (sur Mac : Réglages Système → Réseau).',
    networkServer: 'Serveur de jeu Portage Room',
    networkPort: 'Port',
    xroamIp: 'Serveur XROAM',
    xroamSecondaryIp: 'XROAM de secours',
    xroamPort: 'Port XROAM',
    portClash: 'Le serveur de l’expérience (réseau Portage) et le serveur XROAM sont tous deux sur {at} : un seul des deux pourra écouter ce port. Le réseau Portage écoute sur 7780 par défaut — changez l’un des deux ports (et FISHNET_PORT sur le serveur de l’expérience), puis « Reconfigurer » les casques.',
    token: 'Jeton des casques',
    tokenFromFile: 'Lu sur le serveur de l’expérience (empreinte {fp}).',
    tokenDerived: 'Dérivé de la clé d’ingestion de portageroom.json, version {v} (empreinte {fp}). Rien à coller : le serveur de l’expérience dérive le même (SDK 0.17.27).',
    tokenOverride: 'Forcé par PORTAGEROOM_REMOTE_TOKEN sur ce Portage Room (empreinte {fp}) — dépannage.',
    tokenTroubleshoot: 'Dépannage : coller un jeton à la main',
    tokenTroubleshootHint: 'Seulement pour un serveur de l’expérience encore sur un SDK antérieur à 0.17.27 (son remote-token.txt). Utilisé seulement si c’est le jeton que le serveur annonce.',
    tokenStored: 'Saisi (empreinte {fp}). Tapez-en un nouveau pour le remplacer.',
    tokenMissing: 'Le mot de passe que les casques présentent au serveur de l’expérience (le fichier remote-token.txt que le serveur de l’expérience a créé dans son dossier de données). Collez-le une fois ; il ne se réaffiche jamais.',
    tokenMatches: '✓ Le serveur de l’expérience tient le même jeton.',
    tokenMismatch: 'Le serveur de l’expérience annonce un autre jeton (empreinte {fp}) : les casques n’apparaîtraient pas au parc. Vérifiez qu’il lit le même portageroom.json (ingestKey, roomTokenVersion).',
    tokenMismatchShort: 'Le jeton des casques ne correspond pas à celui du serveur de l’expérience : installation refusée.',
    save: 'Enregistrer',
    what: 'Expérience à installer',
    noBuilds: 'Aucun APK publié pour ce lieu pour l’instant (console Portage → Expériences → les APK de l’expérience).',
    where: 'Casques',
    noHeadsets: 'Aucun casque dont l’adresse est connue. Balayez le réseau avec « Ajouter des casques », dans Salles.',
    headset: 'Casque',
    state: 'État',
    authHint: 'La première fois, chaque casque demande « Autoriser le débogage USB ? » : acceptez-le casque sur la tête, en cochant « Toujours autoriser ».',
    launch: 'Installer sur {n} casques',
    reconfigure: 'Reconfigurer {n} casque{s}',
    apply: 'Mettre à jour {n} casque{s}…',
    tabNetwork: 'Réseau',
    tabXroam: 'XROAM (hybride)',
    tabToken: 'Jeton des casques',
    tabMissing: 'à compléter',
    xroamSameHost: 'Serveur XROAM : {ip}, la machine de l’onglet Réseau (« Adresses séparées » là-bas pour une autre).',
    applyTitle: 'Mettre à jour {n} casque{s}',
    andMore: 'et {n} autres',
    applyInstall: 'Installer un APK',
    applyInstallHint: 'Installe l’APK, réécrit les fichiers de la salle et relance l’app.',
    publishedGroup: 'Publiés',
    testGroup: 'APK de test (cette salle)',
    testBadge: 'TEST',
    testFollowsRoom: 'type de la salle',
    testNote: 'APK de test de cette salle : jamais publié ni envoyé au nuage. Une version plus ancienne est acceptée par défaut.',
    testCloudDown: 'Le nuage ne répond pas : seuls les APK de test de cette salle sont proposés.',
    testAdd: 'Ajouter un APK de test',
    testAddHint: 'Il reste dans cette salle jusqu’à ce qu’on le retire, jamais envoyé au nuage.',
    testFolder: 'Vous pouvez aussi le déposer dans ce dossier du serveur de salle :',
    testStored: 'APK de test {version} ajouté.',
    testRemoved: 'APK de test retiré.',
    testRemove: 'Retirer cet APK de test',
    testRemoveTitle: 'Retirer cet APK de test ?',
    testRemoveBody: '{name} quitte la liste de cette salle et son fichier est effacé. Les casques qui l’ont le gardent.',
    testRemoveFolder: 'Le fichier est aussi effacé du dossier surveillé.',
    applyReconfigure: 'Reconfigurer seulement',
    applyWarn: 'L’app est relancée sur chaque casque : la personne qui le porte sera interrompue.',
    applyHint: '« Mettre à jour » installe un APK ou reconfigure les casques cochés, après une confirmation. « Vérifier » lit seulement leurs fichiers, sans rien changer.',
    helpLabel: 'Aide',
    disablePortage: 'Désactiver Portage sur ces casques',
    disablePortageHint: 'Interrupteur d’urgence : l’app tourne sans Portage sur ces casques (ni suivi, ni pilotage). Décochez puis reconfigurez pour le rétablir.',
    reconfigureHint: 'Réécrit les fichiers de la salle (agent, jeton, serveurs) et relance l’app, sans réinstaller — par exemple quand l’adresse du serveur XROAM change.',
    reconfigureStarted: 'Reconfiguration lancée sur {n} casque{s}.',
    cancel: 'Annuler',
    cancelAll: 'Annuler les {n} en cours',
    cancelled: '{n} installation(s) annulée(s).',
    retry: 'Réessayer',
    retryAll: 'Réessayer les {n} échec{s}',
    retryStarted: 'Nouvel essai lancé sur {n} casque{s}.',
    retryDowngrade: 'Réessayer en acceptant la version plus ancienne',
    replaceApp: 'Remplacer l’app…',
    details: 'Détails',
    queued: 'En file, {pos}ᵉ ({width} à la fois)',
    summary: { running: '{n} en cours', queued: '{n} en attente', failed: '{n} échec{s}', done: '{n} réussi{s}' },
    summaryAll: 'Tout afficher',
    options: 'Options d’installation',
    allowDowngrade: 'Accepter une version plus ancienne',
    allowDowngradeHint: 'Android ne l’accepte que si l’app déjà installée est un build de debug ; sinon, publiez l’APK le plus récent.',
    replaceAppOption: 'Remplacer l’app (désinstaller puis installer)',
    replaceAppHint: 'Pour une app signée avec une autre clé.',
    replaceAppWarn: 'Les données propres à l’app sont effacées sur ces casques (sauvegardes, journaux, préférences). Portage repose ensuite l’identité du casque, le jeton des casques et la configuration XROAM.',
    replaceTitle: 'Remplacer l’app sur {n} casque{s}',
    replaceConfirm: 'Désinstaller et réinstaller',
    newerInstalled: 'Version plus récente que {version} déjà installée sur : {names}. Android refusera l’installation sans « Accepter une version plus ancienne ».',
    adbVersion: 'Lue par ADB le {when} (le casque ne bat pas)',
    check: 'Vérifier ({n})',
    checkHint: 'Lit portageroom-remote.json sur ces casques par ADB et le compare à ce que Portage a écrit — même quand Portage y est coupé. Ne change rien.',
    checkDone: '{read} lu(s), {missed} injoignable(s) en ADB.',
    portageColumn: 'Portage',
    remoteFile: {
      ok: 'Conforme',
      disabled: 'Coupé',
      drift: 'Différent',
      missing: 'Fichier absent',
      unreadable: 'Fichier illisible',
      not_installed: 'App absente',
    },
    remoteFileDiff: { agent: 'adresse', token: 'jeton', disabled: 'état de Portage', lbeMap: 'carte LBE attendue', keepAwake: 'garder éveillé' },
    remoteFileRead: 'Lu {when}',
    remoteFileDiffers: 'Diffère : {what}',
    remoteFileNever: 'non lu',
    started: 'Installation lancée sur {n} casque{s}.',
    progress: 'Installations',
    last: 'Dernière étape',
    states: {
      queued: 'En attente',
      awaiting_authorization: 'Accepter dans le casque',
      downloading: 'Téléchargement',
      installing: 'Installation',
      configuring: 'Configuration',
      restarting: 'Relance',
      done: 'Installé',
      failed: 'Échec',
    },
    codes: {
      adb_unreachable: 'ADB ne répond pas sur ce casque (débogage Wi‑Fi coupé ?)',
      adb_unauthorized: 'Le casque n’a pas accepté la clé de la salle',
      INSTALL_FAILED_VERSION_DOWNGRADE: 'Une version plus récente est déjà installée',
      INSTALL_FAILED_UPDATE_INCOMPATIBLE: 'Signature différente : seul « Remplacer l’app » l’installe',
      INSTALL_FAILED_INSUFFICIENT_STORAGE: 'Stockage du casque plein',
      version_mismatch: 'Installé, mais pas la version attendue',
      provisioning_incomplete: 'Réglages de la salle incomplets',
      checksum_mismatch: 'Le fichier téléchargé est corrompu',
      cloud_unreachable: 'Portage Cloud injoignable',
      push_failed: 'Un fichier de configuration n’a pas pu être écrit',
      files_dir_missing: 'L’app n’a pas créé son dossier une fois lancée : casque endormi ?',
      downgrade_refused: 'Android refuse la version plus ancienne : l’app installée n’est pas un build de debug. Publiez un APK plus récent, ou remplacez l’app',
      no_identity: 'Aucune identité lisible sur ce casque',
      not_installed: 'L’app n’est pas installée sur ce casque : installez-la d’abord',
      interrupted: 'Interrompue par un redémarrage du serveur de l’expérience : relancez ce casque',
      remote_file_mismatch: 'portageroom-remote.json, relu après l’écriture, n’est pas ce qui a été écrit',
      install_failed: 'L’installation a échoué : ce qu’adb a répondu est en dessous',
      cancelled: 'Annulée',
      timeout: 'L’installation a dépassé 15 minutes : casque endormi ou Wi‑Fi trop lent ?',
      internal_error: 'Erreur inattendue du serveur de l’expérience : le détail est en dessous',
      build_not_found: 'Cet APK n’existe plus dans Portage Cloud',
      INSTALL_FAILED_INVALID_APK: 'Le fichier APK n’est pas valide pour ce casque',
      INSTALL_PARSE_FAILED_NO_CERTIFICATES: 'L’APK n’est pas signé',
      uninstall_failed: 'La désinstallation a échoué : ce qu’adb a répondu est en dessous',
    },
    errors: {
      zone_layout_unavailable: 'La disposition des zones n’a pas pu être rapatriée de Portage Cloud.',
      zone_layout_mismatch: 'Le ZoneLayout.json relu sur le casque ne correspond pas : poussez-le de nouveau.',
      provisioning_incomplete: 'Les réglages de la salle sont incomplets : agent, jeton et adresse du serveur.',
      cloud_unreachable: 'Portage Cloud est injoignable : la liste des APK n’est pas disponible.',
      no_cloud: 'Ce Portage Room n’est pas relié à Portage Cloud.',
      build_not_found: 'Cet APK n’existe plus.',
      probe_build: 'Cet APK ne porte aucun paquet Portage : il n’y a pas de fichier Portage à lire sur les casques.',
      build_required: 'Choisissez un APK à installer. Reconfigurer et Vérifier fonctionnent sans.',
    },
  },
  builds: {
    title: 'Expériences à installer (APK)',
    declination: 'Déclinaison',
    declinations: { portage: 'Portage (complète)', hybrid: 'Hybride (XROAM + Portage)', probe: 'Probe (XROAM, observée)' },
    declinationHints: {
      portage: 'Construit avec Portage Room : Portage fait tout (réseau, casques, séance).',
      hybrid: 'Construit sur XROAM avec nos paquets dedans : XROAM fait tourner la séance, Portage pilote les casques.',
      probe: 'L’APK d’un tiers, sans nos paquets : installé avec sa seule configuration XROAM, observé mais pas piloté.',
    },
    playsAs: 'la joue en {declination}',
    hint: 'Déposer n’installe rien : publiez l’APK pour un lieu, et le lieu choisit ensuite quand l’installer, salle par salle. Chaque APK est construit pour une déclinaison (Portage, hybride, probe), et n’est proposé qu’aux lieux qui jouent cette expérience dans cette déclinaison. Toujours la même source de signature pour un paquet.',
    releasedTo: 'Publié pour',
    released: 'Publié : le lieu peut l’installer dans ses salles.',
    unreleased: 'Publication retirée pour ce lieu.',
    noInstances: 'Aucun lieu ne joue encore cette expérience.',
    declinationsShort: { portage: 'Portage', hybrid: 'Hybride', probe: 'Probe' },
    drop: 'Glissez l’APK ici, ou cliquez pour le choisir',
    dropHint: 'Le paquet, la version et son code sont lus dans le fichier.',
    change: 'Changer',
    apk: 'Paquet',
    versionName: 'Version',
    versionCodeShort: 'code {code}',
    size: 'Taille',
    upload: 'Déposer',
    uploading: 'Envoi… {pct} %',
    stored: 'APK {version} déposé.',
    none: 'Aucun APK déposé pour l’instant.',
    remove: 'Supprimer',
    removeTitle: 'Supprimer l’APK {version} ?',
    removeBody: 'Les lieux pour qui il était publié ne se le verront plus proposer. Les casques qui l’ont déjà le gardent.',
    errors: {
      apk_unreadable: 'Le manifeste de ce fichier est illisible : est-ce bien un APK ?',
      already_stored: 'Cet APK, exactement, est déjà déposé.',
      too_large: 'Fichier trop lourd (1,9 Go au plus).',
      unsupported_type: 'Choisissez un fichier .apk.',
      content_mismatch: 'Ce fichier n’est pas un APK.',
      '401': 'Votre session a expiré : reconnectez-vous.',
    },
  },
  views: {
    emptyTitle: 'Aucune vue en direct',
    emptyHint: 'Lancez la vue d’un casque pour voir ce que voit la personne. Tous les casques du lieu sont listés ci-dessous, avec ce qui manque.',
    start: 'Lancer la vue',
    stop: 'Arrêter la vue',
    starting: 'Lancement…',
    started: 'Vue en direct demandée sur {name}.',
    confirmTitle: 'Lancer la vue pendant une séance ?',
    confirmBody: '{name} est dans une expérience. La vue en direct coûte de la chaleur et de la batterie à un casque porté.',
    why: {
      offline: 'Éteint ou injoignable',
      stopped: 'Vue coupée',
      no_source: 'Ne diffuse pas',
      no_address: 'Diffuse sans adresse',
      not_on_lan: 'Visible depuis le réseau du lieu seulement',
      error: 'Diffusion en panne',
    },
  },
  pause: {
    pause: 'Mettre en pause l’expérience de cette personne',
    resume: 'Reprendre l’expérience de cette personne',
    pauseShort: 'Pause',
    resumeShort: 'Reprendre',
    pauseGroup: 'Pause du groupe',
    resumeGroup: 'Reprendre le groupe',
    xroam: 'Cette expérience est pilotée par XROAM : la pause individuelle n’est pas disponible.',
    notPaused: 'Pas mis en pause',
    notResumed: 'Pas repris',
    states: {
      pending: 'Pause demandée…',
      paused: 'En pause',
      held: 'En pause — en attente du casque',
      resuming: 'Reprise en cours…',
    },
    codes: {
      unsupported: 'l’expérience ne sait pas mettre une personne en pause',
      alert_active: 'une alerte est en cours',
      not_playing: 'pas dans une expérience',
      room_paused: 'toute la salle est en pause',
      timeout: 'pas de réponse du casque',
      expired: 'le serveur de l’expérience ne l’a pas relevée',
      partial: 'certains casques n’ont pas suivi',
    },
    errors: {
      unsupported: 'Cette expérience est pilotée par XROAM : la pause individuelle n’est pas disponible.',
      not_playing: 'Cette personne n’est pas dans une expérience en ce moment.',
      not_a_room: 'La pause part de Portage Room seulement.',
      no_server_holds_device: 'Aucun serveur de l’expérience ne tient ce casque.',
      no_server_device: 'Aucun serveur de l’expérience dans ce lieu.',
    },
  },
  msg: {
    toHeadset: 'Message dans le casque',
    toPerson: 'Message à {name}',
    write: 'Message',
    writeTo: 'Écrire à {name}',
    toGroup: 'Message au groupe',
    placeholder: 'Affiché dans le casque — 140 caractères au plus',
    duration: 'À l’écran',
    send: 'Envoyer',
    outcomes: {
      pending: 'En route…',
      delivered: 'Affiché',
      failed: 'Non affiché',
      expired: 'Pas délivré à temps',
      timeout: 'Pas de réponse du serveur de l’expérience',
    },
    codes: {
      offline: 'casque hors ligne',
      paused: 'casque non porté',
      alert_active: 'une alerte est à l’écran',
      unsupported: 'ce build ne sait pas afficher de message',
      partial: 'certains casques du groupe ne l’ont pas affiché',
    },
    errors: {
      not_a_room: 'Les messages partent de Portage Room seulement.',
      no_server_holds_device: 'Aucun serveur de l’expérience ne tient ce casque : lancez l’expérience d’abord.',
      no_server_device: 'Aucun serveur de l’expérience dans ce lieu.',
      target_device_not_found: 'Ce casque n’est pas dans ce lieu.',
      target_group_not_found: 'Ce groupe n’existe plus.',
    },
  },
  alert: {
    button: 'Alerte',
    ongoing: 'Alerte en cours',
    drillOngoing: 'Exercice en cours',
    raiseTitle: 'Alerte d’évacuation',
    raiseHint: 'Tous les casques du lieu afficheront « Retirez votre casque », et les séances se mettent en pause.',
    drill: 'Exercice',
    real: 'Alerte réelle',
    hold: 'Maintenir 2 secondes',
    confirmReal: 'Déclencher une alerte RÉELLE dans tout le lieu ?',
    confirmDrill: 'Lancer un exercice dans tout le lieu ?',
    confirm: 'Déclencher l’alerte',
    title: 'Alerte en cours',
    drillTitle: 'Exercice en cours',
    raisedBy: 'Déclenchée par {name} à {time}',
    shownOf: '{n} casques sur {total} ont affiché l’alerte',
    missing: 'Non affiché — aller voir',
    strangers: 'Casques inconnus qui l’ont affichée',
    servers: 'Serveurs d’expérience',
    serverOk: 'Tous affichés',
    serverDelivered: 'Reçue',
    serverPending: 'En attente',
    serverFailed: 'Échec',
    clear: 'Lever l’alerte',
    cleared: 'Levée',
    codes: {
      offline: 'hors ligne',
      paused: 'non porté',
      unsupported: 'build trop ancien',
      partial: 'partiel',
      expired: 'pas délivrée à temps',
      timeout: 'pas de réponse',
    },
    errors: {
      not_a_room: 'L’alerte part de Portage Room seulement.',
      alert_active: 'Une alerte est déjà en cours.',
      no_server_device: 'Aucun serveur de l’expérience dans ce lieu : évacuez à la voix.',
    },
  },
  fadd: {
    room: 'Salle de ces casques',
    roomHint: 'C’est elle qui les liste, les installe et filtre le tableau. Modifiable ensuite sur la fiche du casque.',
    listOptional: 'Liste de casques (facultative)',
    csvHintWithScan: 'Avec un balayage, une colonne d’adresses suffit à rapprocher ; les numéros de série aident, les noms sont le but.',
    listRead: '{n} ligne(s) lue(s).',
    needSerialOrIp: 'Attribuez au moins la colonne des séries ou celle des adresses.',
    scanSummary: '{n} appareil(s) ont répondu, dont {known} qui ont dit qui ils sont.',
    toMatch: 'Rapprocher ({n})',
    matchTitle: 'Rapprocher les casques de leur nom',
    matchHint: 'Une ligne par casque. Portage a rapproché par numéro de série quand il en a lu un, par adresse sinon — ceux-là sont à vérifier. Corrigez un nom ou une série manquante ici même.',
    exportCsv: 'Exporter en CSV',
    colSource: 'Provenance',
    colNote: 'Rapprochement',
    sources: { scan: 'Balayage', list: 'Liste', both: 'Balayage + liste' },
    notes: {
      serial: 'par numéro de série',
      ip: 'par l’IP — à vérifier',
      conflict: 'conflit : la liste annonce {serial} à cette adresse',
      not_seen: 'pas vu sur le réseau',
      no_serial: 'sans numéro de série — à compléter',
      waiting: 'en attente d’accord dans le casque',
    },
    serialPlaceholder: 'numéro de série',
    namePlaceholder: 'sans nom',
    notAddable: '{n} ligne(s) sans numéro de série ne seront pas ajoutées.',
    keptLabel: 'nom gardé — la liste disait « {label} »',
    title: 'Ajouter des casques',
    hint: 'Trouvez-les sur le réseau ou déposez un tableur. Rien n’est écrit avant votre confirmation.',
    confirmTitle: 'Vérifier avant d’ajouter',
    confirmHint: 'Voici exactement ce que Portage va écrire. Les lignes en rouge seront laissées de côté.',
    scan: 'Balayage auto',
    csv: 'Import CSV',
    scanHint: 'Les casques qui répondent sur ce bloc sont listés — les connus voient leur adresse mise à jour, les inconnus qui donnent un numéro de série sont ajoutés.',
    scanKnown: '{n} casques connus ont répondu',
    scanNew: '{n} nouveaux casques ont donné leur numéro de série',
    scanAnonymous: '{n} casques ont répondu sans dire qui ils sont :',
    scanWaiting: '{n} casques attendent votre accord — acceptez dans chaque casque, puis relancez le balayage :',
    scanAnonymousHint: 'Un casque PICO ne publie pas son numéro de série sur le réseau. Portage le reconnaît à son adresse une fois qu’elle est connue ; pour les autres, il faut le numéro de série une fois.',
    toCsv: 'Compléter ces adresses dans un CSV',
    csvLabel: 'Numéros de série, adresses fixes et noms',
    csvHint: 'Un casque par ligne. Virgule, point-virgule ou tabulation — le séparateur est détecté. Les colonnes sont reconnues par leur en-tête (série, ip, nom…) et se corrigent ci-dessous.',
    csvFile: 'Choisir un fichier',
    columns: 'Ce que porte chaque colonne',
    colIgnore: 'Ignorer',
    colSerial: 'Numéro de série',
    colIp: 'IP fixe',
    colName: 'Nom',
    colAction: 'Ce qui se passe',
    pickAll: 'Tout / rien',
    colGroup: 'Sous-groupe',
    needSerial: 'Choisissez la colonne du numéro de série : c’est la seule identité que donne un casque.',
    review: 'Vérifier {n} casques',
    nothingToAdd: 'Rien à ajouter.',
    back: 'Retour',
    commit: 'Ajouter {n} casques',
    done: '{n} casques inscrits au parc.',
    grouping: 'Rangement',
    groupApart: 'Mettre cet ajout dans un groupe à part',
    groupName: 'Nom du groupe',
    defaultGroup: 'Arrivage',
    groupExists: 'Ce groupe existe déjà : les casques y seront ajoutés.',
    groupRoom: 'Les groupes appartiennent au studio : Portage Cloud les écrit à la synchronisation de cette salle.',
    splitNone: 'Sans sous-groupes',
    splitCount: 'En N sous-groupes',
    splitSize: 'N casques par sous-groupe',
    splitHint: 'Dans l’ordre de la liste — celui où les casques sont sortis de la caisse.',
    headsets: '{n} casques',
    actions: {
      create: 'Nouveau',
      claim: 'Arrive',
      update: 'Mis à jour',
      same: 'Inchangé',
      skip: 'Écarté',
    },
    why: {
      empty: 'Pas de numéro de série',
      duplicate: 'Série listée deux fois',
      bad_ip: 'Adresse IP illisible',
      no_ip: 'Sans adresse IP : l’installation passe par ADB en Wi-Fi',
      ip_taken: 'Adresse déjà portée par un autre casque ici',
      elsewhere: 'Actuellement à {venue} — le déplacer depuis la console Portage',
      foreign_studio: 'Appartient à un autre studio',
    },
    groupProblems: {
      venue_without_studio: 'Ce lieu n’est rattaché à aucun studio : ses casques ne peuvent pas être rangés en groupes.',
      unknown_serial: 'Un sous-groupe contient un casque qui sera écarté.',
      grouped_twice: 'Un casque est rangé dans deux sous-groupes.',
      name_taken: 'Un de ces noms est déjà pris par un autre groupe du studio. Renommez-le.',
    },
  },
  parc: {
    headsets: 'Casques',
    devices: 'Appareils',
    sellable: 'Capacité vendable',
    add: 'Ajouter des casques',
    install: 'Installation',
  },
  res: {
    title: 'Ressources',
    catalogue: 'Catalogue',
    newResource: 'Ajouter une ressource',
    name: 'Nom',
    kind: 'Type',
    headset: 'Casques',
    room: 'Salle',
    vehicle: 'Véhicule',
    staff: 'Personnel',
    other: 'Autre',
    quantity: 'Combien vous en avez',
    unitCapacity: 'Personnes par unité',
    active: 'Comptée',
    inactive: 'Ignorée',
    usedBy: 'Mobilisée par',
    linkExperience: 'Ajouter une expérience',
    perParticipant: 'Par personne',
    perSession: 'Par séance',
    holdMinutes: 'Indisponible après (min)',
    holdHint: 'Un casque doit être nettoyé et rechargé avant le groupe suivant.',
    unlink: 'Retirer',
    load: 'Charge du jour',
    saturated: 'Tout est pris',
    exceeded: 'Dépassé — la programmation demande plus que le parc',
    noResources: 'Aucune ressource partagée.',
    noResourcesHint: 'Une ressource, c’est ce que deux expériences se disputent : un parc de casques, une salle, un opérateur. Déclarez-la une fois et Portage Cloud refuse la vente qui la ferait déborder — même quand la salle a encore des places.',
    unlinked: 'Mobilisée par aucune expérience — elle ne bloquera jamais une vente.',
    modeHint: 'Par personne pour des casques, par séance pour une salle ou un opérateur.',
    sellable: 'Capacité vendable',
    fleetDeclared: 'vendus',
    fleetReal: 'au parc',
    fleetFault: '(dont {n} en panne)',
    fleetMatch: 'Conforme au parc.',
    fleetSpare: '{n} gardés en réserve.',
    fleetOver: 'Survente : {n} places sans casque.',
  },
  tpl: {
    title: 'Gabarits du studio',
    contentLang: 'Langue du texte',
    langMissing: 'il manque {langs}',
    start: 'Partir d’un gabarit',
    none: 'Aucun gabarit pour cette juridiction.',
    noneHint:
      'Studio Rabaska les écrit par pays et par province, parce qu’une décharge signée au Québec et une signée en France ne sont pas le même texte. Demandez-en un au studio s’il vous en manque un là où vous exploitez.',
    jurisdiction: 'Écrit pour',
    wholeCountry: 'tout le pays',
    use: 'Utiliser celui-ci',
    used: 'Ajouté au parcours',
    notice:
      'Un gabarit est un point de départ, pas un avis juridique. Dès que vous l’ajoutez, le texte est le vôtre : modifiez-le, et faites-le valider par votre conseil avant la mise en service.',
    stale: 'Le studio a publié une nouvelle version de {name}',
    staleFrom: 'Vous avez copié la v{from}, la version courante est la v{to}.',
    staleWhat: 'Ce qui a changé',
    staleKeep:
      'Rien ne bouge tout seul — votre texte reste exactement tel quel. Comparez, puis décidez.',
    dismiss: 'Compris',
    kindRegistration: 'Inscription',
    kindNda: 'NDA',
    kindWaiver: 'Décharge',
    country: 'Pays',
    region: 'Province ou état',
    regionHint: 'Laisser vide quand le gabarit vaut pour tout le pays.',
    usage: 'Utilisé par',
    steps: 'étapes de parcours',
    upToDate: 'à jour',
    publish: 'Publier une nouvelle version',
    publishHint:
      'Incrémente la version et avertit chaque opérateur qui l’a copié. Une coquille corrigée n’en a pas besoin.',
    changelog: 'Ce qui change dans cette version',
    retire: 'Retirer',
    retireHint: 'Cesse de le proposer. Les parcours qui l’ont copié gardent leur texte.',
    newTemplate: 'Nouveau gabarit',
    saved: 'Enregistré',
  },
  studio: {
    homeVenue: 'Lieu du parc au retour',
    homeVenueHint: '« Rapatrier au studio » y renvoie les casques de cette entité. Laissez vide pour les rentrer sans lieu, en caisse.',
    homeVenueNone: 'Aucun : sans lieu',
    identity: 'Entités du studio',
    administration: 'Administration',
    infoTab: 'Infos générales',
    identityHint:
      'Nommée dans les accords de confidentialité signés dans les lieux dont elle répond, par {{studio.nomLegal}} et {{studio.adresse}}. Les gabarits renvoient à ces valeurs plutôt que de les écrire — sinon un déménagement obligerait à rééditer chaque gabarit et à faire resigner tout le monde.',
    newStudio: 'Nouvelle entité',
    none: 'Aucune entité déclarée.',
    noneHint:
      'Une entité est la personne morale qui répond d’un ensemble de lieux. Elle est nommée dans les accords de confidentialité signés là-bas, et c’est elle qui écrit les gabarits que ces lieux se voient proposer.',
    orphans: '{n} lieu(x) ne sont rattachés à aucune entité.',
    orphansHint:
      'Ils ne se voient proposer aucun gabarit — ni NDA, ni décharge. Rattachez-les depuis Lieux, sur la fiche de chacun.',
    attached: 'Entité responsable',
    attachedHint:
      'Nommée dans les accords signés ici, et source des gabarits que ce lieu se voit proposer.',
    attachedNone: 'Non rattaché — aucun gabarit ne sera proposé',
    name: 'Nom',
    legalName: 'Raison sociale',
    legalNameHint: 'Celle qui apparaît dans un accord. Retombe sur le nom ci-dessus si elle est vide.',
    legalHint: 'Ceux de l’entité qui répond des documents signés — pas ceux du lieu, ni ceux du partenaire.',
    address: 'Adresse',
    city: 'Ville',
    region: 'Province ou état',
    postalCode: 'Code postal',
    country: 'Pays',
    email: 'Courriel de contact',
    website: 'Site web',
    preview: 'Tel que ce sera écrit dans un document signé',
  },
  media: {
    title: 'Médias',
    intro:
      'Le briefing que jouent vos écrans de salle. Il appartient à l’EXPÉRIENCE du studio, comme ses variables : le studio la fabrique, donc son briefing, dans toutes les langues. Un lieu ne peut jouer que les médias des expériences qu’il joue.',
    kind: 'Type',
    briefing: 'Briefing',
    trailer: 'Bande-annonce',
    locale: 'Langue',
    upload: 'Téléverser un fichier',
    size: 'Taille',
    none: 'Aucun média sur cette expérience.',
    noneHint:
      'Une ligne par langue — un briefing est doublé, pas sous-titré. MP4, WebM ou MP3, 500 Mo au maximum.',
    tooLarge: 'Ce fichier est trop lourd — 500 Mo au maximum.',
    uploadFailed: 'Le téléversement a échoué. Rien n’a été enregistré.',
  },
  profile: {
    title: 'Mon compte',
    identity: 'Identité',
    name: 'Nom',
    email: 'Courriel',
    emailHint: 'Identifie votre compte et sert à vous connecter. Un propriétaire ou un gérant peut le changer.',
    changePhoto: 'Changer la photo',
    removePhoto: 'Retirer',
    tooLarge: 'Cette image est trop lourde — 2 Mo au maximum.',
    badImage: 'Ce fichier n’est pas une image lisible. JPEG, PNG ou WebP.',
    preferences: 'Préférences',
    language: 'Langue de l’interface',
    languageHint: 'Vous suit d’un lieu et d’un poste à l’autre. Ce n’est pas la langue du lieu.',
    password: 'Mot de passe',
    currentPassword: 'Mot de passe actuel',
    newPassword: 'Nouveau mot de passe',
    passwordHint: 'Dix caractères au minimum.',
    confirmPassword: 'Confirmer',
    changePassword: 'Changer le mot de passe',
    passwordOnCloud: 'Votre mot de passe est détenu par Portage Cloud, pas par ce Portage Room. Changez-le depuis le back-office du nuage — Portage Room l’y vérifie à la connexion.',
    mismatch: 'Les deux nouveaux mots de passe ne correspondent pas.',
    wrongPassword: 'Ce n’est pas votre mot de passe actuel.',
    weak: 'Trop court — dix caractères au minimum.',
  },
  studioTeam: {
    title: 'Équipe',
    hint: 'Qui travaille pour cette entité. Ce sont des superadmins : ils ouvrent la console et agissent dans tous les lieux, quelle que soit leur entité. On les invite par un lien — la personne choisit elle-même son mot de passe.',
    leaveTeam: 'Retirer de l’équipe',
    addToTeam: 'Ranger dans cette équipe',
    unassigned: 'Superadmins sans entité (ils gardent tout leur accès)',
    placed: 'Rangé dans l’équipe',
    unplaced: 'Retiré de l’équipe — il reste superadmin',
    add: 'Ajouter quelqu’un',
    email: 'Courriel',
    name: 'Nom',
    addHint: 'Un compte qui existe est promu et garde son mot de passe. Un nouveau reçoit un lien à usage unique, valable une heure.',
    send: 'Inviter',
    invited: 'Invitation prête : transmettez le lien ci-dessous',
    promoted: 'Compte promu superadmin du studio',
    linkFor: 'Lien pour {name}',
    linkExpires: 'Valable une fois, jusqu’à {time}. Transmettez-le en privé.',
    copy: 'Copier le lien',
    hideLink: 'Masquer',
    none: 'Aucun superadmin du studio.',
    you: 'vous',
    remove: 'Retirer l’accès studio',
    removeTitle: 'Retirer {name} de l’équipe du studio ?',
    removeBody: 'La personne garde son compte et ses accès aux lieux où elle est membre, mais perd la console et l’accès à tous les lieux.',
    removed: 'Retiré de l’équipe du studio',
    errors: {
      not_yourself: 'Vous ne pouvez pas vous retirer vous-même.',
      last_superadmin: 'Il doit toujours rester au moins un superadmin du studio.',
    },
  },
  personal: {
    addBlank: 'Paramètre vide',
    reuse: 'Réutiliser « {name} »',
    defaultShort: 'défaut',
    sharedWith: 'Partagé avec {names} : une modification vaut pour toutes.',
    hiddenAt: 'Masqué à {venues}',
    languageAtOnboarding: 'Demander la langue à l’enregistrement',
    languageAskedHint: 'Le visiteur choisit la langue de l’expérience en fin d’enregistrement, parmi les langues du lieu ; elle part au casque. La console peut encore la changer pendant la visite.',
    languageNotAskedHint: 'Pas demandée à l’enregistrement : l’expérience la fait choisir (Atlantis : l’interphone). La console peut la changer pendant la visite ; Atlantis revient au défaut du lieu entre deux visiteurs.',
    languageOnboardingLabel: 'Langue de l’expérience',
    languagePreviewHint: 'Les langues du lieu, celle de l’enregistrement présélectionnée.',
    preview: 'Aperçu · écran du visiteur',
    previewTitle: 'Vos préférences',
    title: 'Paramètres personnels',
    variables: 'Variables',
    untitled: 'Paramètre sans nom',
    defaultIs: 'par défaut : {value}',
    appliesCount: '{n} expérience(s)',
    edit: 'Modifier',
    done: 'Terminé',
    optionValue: 'Valeur lue par le casque',
    appliesTo: 'S’applique à',
    hiddenForHint: 'Par expérience dans un lieu : proposé partout ailleurs.',
    noInstance: 'Aucun lieu ne joue encore ces expériences.',
    hint: 'Des bascules et des choix que le VISITEUR fait à l’enregistrement (par exemple mode guidé ou explorateur). L’opérateur peut les changer sur sa carte ; le casque qu’il porte les lit. La langue est toujours proposée.',
    none: 'Aucun paramètre pour cette expérience',
    noneHint: 'Ajoutez-en un pour le proposer dans le formulaire d’enregistrement.',
    add: 'Ajouter un paramètre',
    addAssistance: 'Modèle : mode d’assistance (Atlantis)',
    key: 'Clé lue par le casque',
    keyHint: 'En minuscules, points permis : ce que l’expérience lit avec PlayerParams.',
    labelFr: 'Libellé (français)',
    labelEn: 'Libellé (anglais)',
    type: 'Type',
    toggle: 'Bascule (oui / non)',
    choice: 'Choix',
    options: 'Choix possibles',
    addOption: 'Ajouter un choix',
    optionIcon: 'Pictogramme',
    descriptionFr: 'Phrase affichée sous le choix (FR)',
    descriptionEn: 'Phrase affichée sous le choix (EN)',
    iconNone: 'Aucun',
    icons: { map: 'Carte', compass: 'Boussole', search: 'Loupe', eye: 'Œil', people: 'Personnes', clock: 'Horloge', help: 'Main tendue', globe: 'Globe' },
    default: 'Par défaut',
    hiddenFor: 'Masqué pour',
    duplicateKey: 'Deux paramètres ont la même clé, ou l’un s’appelle « language » (réservé).',
    person: 'Paramètres personnels',
    personOf: 'Paramètres — {name}',
    language: 'Langue',
    groupDefault: 'langue du groupe ({language})',
    headsetDefault: 'la langue de l’expérience',
    applied: 'Envoyés au casque qu’elle porte.',
    noParams: 'Cette expérience n’a pas encore de paramètre personnel : seulement la langue.',
    formTitle: 'Vos préférences',
  },
  checkReport: {
    title: 'Rapport de vérification',
    summary: '{read} lu(s), {missed} injoignable(s) en ADB, {problems} à regarder.',
    portageOk: 'Portage : {n} conforme(s)',
    xroamOk: 'XROAM : {n} conforme(s)',
    duplicates: 'Numéros XROAM en double : {numbers}',
    duplicatesHint: 'Deux casques au même numéro : le serveur XROAM garde le premier et éjecte le second, qui reste hors ligne jusqu’au prochain lancement de son app.',
    unreachable: '{n} injoignable(s)',
    fix: 'Corriger les doublons ({n})',
    fixHint: 'Chaque casque prend le numéro de son nom (Casper-07 → 7), ou un libre ; Portage réécrit son fichier XROAM et relance l’app. Refusé sur un casque appairé à un visiteur.',
    duplicate: 'Doublon',
    expandHint: 'Cliquez une ligne pour le détail du fichier XROAM.',
    change: 'Changer',
    apply: 'Appliquer',
    applyHint: 'Réécrit le fichier XROAM avec ce numéro et relance l’app. L’appairage visiteur de XROAM suit le numéro : refusé sur un casque appairé à un visiteur.',
    renumbered: 'Renumérotés : {list}.',
    outcome: { unauthorized: 'ADB non autorisé', refused: 'ADB refusé', unreachable: 'Injoignable' },
    xroamState: { ok: 'Conforme', drift: 'Différent', missing: 'Fichier absent', unreadable: 'Fichier illisible' },
    xroamDriftNumber: 'Numéro différent',
    xroamDriftOther: 'Réglage différent',
    xroamValues: '{what} : {read} sur le casque, {expected} attendu',
    xroamField: { deviceId: 'numéro', ip: 'serveur XROAM', secondaryIp: 'adresse de secours', port: 'port', displayName: 'Nom affiché' },
    errors: {
      device_in_visit: 'Un casque est appairé à un visiteur : renumérotez-le entre deux visites',
      number_taken: 'Ce numéro est déjà pris dans la salle',
      number_invalid: 'Un numéro de 1 à 9999',
      no_ip: 'Casque sans adresse IP connue',
      not_found: 'Casque inconnu',
    },
  },
  ipChanged: {
    badge: 'Nouvelle',
    hint: 'Le casque a annoncé une nouvelle adresse (c’était {previous}, changée le {when}). Portage la suit tout seul.',
  },
  linkQr: {
    show: 'QR-code',
    hint: 'À scanner avec l’appareil qui doit ouvrir cette adresse.',
    copy: 'Copier l’adresse',
    print: 'Imprimer',
  },
  pages: {
    title: 'Pages dynamiques',
    hint: 'Une page faite par le studio (un classement de sortie…) que Portage remplit avec les visites du jour de la salle et fait tourner dans les langues du lieu. Ouvrez son adresse sur le PC de l’écran : celle de Portage Room (elle continue sans internet), ou celle-ci.',
    hintRoom: 'Les pages se déclarent dans Portage Cloud et descendent ici. Sur le PC de l’écran, ouvrez l’adresse ci-dessous sur CE Portage Room : elle continue sans internet.',
    create: 'Nouvelle page',
    defaultName: 'Classement de sortie',
    experience: 'Expérience',
    room: 'Salle',
    template: 'Gabarit',
    name: 'Nom',
    empty: 'Aucune page dynamique',
    emptyHint: 'Créez-en une par salle : elle montre les visites du jour de cette salle.',
    nothingOffered: 'Aucun gabarit disponible : le studio en dépose un sur l’expérience (Console › Expérience › Pages).',
    disabled: 'Coupée',
    shown: 'Affichée',
    notShown: 'Aucun écran',
    enabled: 'Page active',
    copy: 'Copier',
    open: 'Ouvrir',
    demo: 'Aperçu avec exemples',
    addressHint: 'Sur un Portage Room : http://<son adresse>:8080{path}. Le jeton fait l’autorisation : un écran, une adresse.',
    rotate: 'Nouvelle adresse',
    rotateHint: 'Remplace le jeton : l’ancienne adresse cesse de marcher.',
    rotateTitle: 'Changer l’adresse de cette page ?',
    rotateBody: 'Les écrans qui l’affichent devront ouvrir la nouvelle adresse.',
    remove: 'Retirer',
    removePageTitle: 'Retirer « {name} » ?',
    removePageBody: 'Les écrans qui l’affichent montreront « page introuvable ». Les visites restent.',
    templatesTitle: 'Pages dynamiques',
    templatesHint: 'Les gabarits de pages de cette expérience : un .zip ou un .tar.gz avec la page Handlebars, son portage.json (langues, réglages du lieu, champs calculés depuis les métriques) et ses fichiers. Les lieux créent ensuite leur page par salle, et ne changent que les réglages que le gabarit leur ouvre.',
    templatesEmpty: 'Pas encore de gabarit',
    templatesEmptyHint: 'Déposez une archive, ou demandez à Claude d’en poser un officiel (MCP install_page_template).',
    fields: 'Champs',
    sorting: 'Classement',
    sortingValue: 'par {field}, les {n} dernières visites du jour',
    venueParams: 'Réglages du lieu',
    files: 'Fichiers',
    pick: 'Choisir une archive',
    pickEmpty: 'Aucune archive choisie',
    upload: 'Déposer',
    uploaded: 'Gabarit enregistré.',
    inUse: 'Une page d’un lieu utilise encore ce gabarit.',
    removeTitle: 'Retirer le gabarit « {name} » ?',
    removeBody: 'Possible seulement quand plus aucune page de lieu ne l’utilise.',
    errors: {
      bundle_unreadable: 'Archive illisible',
      bundle_too_large: 'Archive trop lourde (20 Mo au plus)',
      manifest_missing: 'portage.json introuvable',
      manifest_invalid: 'portage.json invalide',
      entry_missing: 'La page nommée dans portage.json manque',
      template_invalid: 'La page Handlebars ne compile pas',
    },
  },
  timings: {
    showUnreported: 'Afficher les {n} scènes jamais rapportées par un casque',
    hideUnreported: 'Masquer les scènes jamais rapportées',
    title: 'Minutage des scènes',
    hint: 'La durée prévue de chaque scène, importée de Reed (minutage de base). En salle, une carte clignote en rouge quand la personne reste dans sa scène plus que prévu, majoré du pourcentage du lieu.',
    empty: 'Pas encore de minutage',
    emptyHint: 'Demandez à Claude de l’importer depuis Reed (outil MCP set_scene_timings). Le nuage Portage ne joint pas Reed directement.',
    importedFrom: 'Depuis {source}, importé le {at}.',
    manual: 'saisi à la main',
    scene: 'Scène',
    sceneId: 'Identifiant de scène rapporté par le casque',
    noScene: 'aucun : pas d’alerte',
    planned: 'Prévue',
  },
  game: {
    heatTitle: 'Carte de chaleur',
    heatHint: 'Le temps passé par case de 50 cm sur une journée, mesuré sur les casques réels, sur le plan importé de Creator. Même palette que la simulation de Creator ; rouge = les 5 % de cases les plus fréquentées, pour que quelques points très chargés n’effacent pas le reste.',
    heatDay: 'Journée',
    heatEmpty: 'Pas de carte de chaleur pour ce jour',
    heatEmptyHint: 'Elle se remplit quand le serveur de l’expérience envoie les positions (SDK 0.17.30 et plus).',
    heatNoPlan: 'Ce lieu n’a pas encore de plan : il s’importe depuis Creator (outil MCP set_floor_plan).',
    heatTotal: '{h} h de présence cumulées',
    heatUpdated: 'mise à jour à {time}',
    heatPlan: 'plan {plan}',
    lossTitle: 'Chiffres incomplets.',
    lossBody: 'Des serveurs ont dû jeter des événements de leur boîte d’envoi après une longue absence : {items}. Des détails de scènes, de métriques et de performances manquent sur la période ; les visites, elles, sont entières.',
    lossBodyVisits: 'Des serveurs ont dû jeter des événements de leur boîte d’envoi après une longue absence : {items}. Des VISITES manquent sur la période, pas seulement des détails : les comptes et les durées sont sous-estimés.',
    lossVisits: 'dont {n} débuts ou fins de visite',
    lossBodyUnknown: 'Des serveurs ont dû jeter des événements de leur boîte d’envoi après une longue absence : {items}. Des détails manquent sur la période, et peut-être des visites : leur SDK (antérieur à 0.17.8) ne distingue pas les deux.',
    title: 'Analytique',
    periods: { '7': '7 jours', '30': '30 jours', '90': '90 jours', '365': '1 an' },
    experience: 'Expérience',
    allExperiences: 'Toutes les expériences',
    venue: 'Lieu',
    allVenues: 'Tous les lieux',
    version: 'Version',
    allVersions: 'Toutes les versions',
    compareBy: 'Comparer par',
    compareNone: 'Rien',
    compare: { ageBand: 'Tranche d’âge', language: 'Langue', appVersion: 'Version d’APK', variantId: 'Variante A/B', model: 'Modèle de casque', venue: 'Lieu' },
    perf: {
      title: 'Performances des casques',
      hint: 'Ce que les casques ont tenu pendant les visites, scène par scène (SDK Portage Room 0.17.3 et suivants). Une image plus lente que la fréquence du casque compte « sous la cible ».',
      meanFps: 'Images/s moyennes',
      belowTarget: 'Temps sous la cible',
      p95: 'Temps d’image p95',
      drain: 'Batterie consommée',
      thermalMax: 'Température max',
      hot: 'Temps en surchauffe',
      memMin: 'Mémoire libre min',
      scene: 'Scène',
      basis: 'Mesuré sur {n} visite(s).',
    },
    livePlaying: '{n} en jeu maintenant',
    liveRecent: '{aborted} abandon(s) en 30 min · {finished} terminée(s) aujourd’hui',
    empty: 'Aucune visite enregistrée sur cette période',
    emptyHint: 'Les visites viennent des casques, par le SDK Portage Room (début de visite, chapitres, fin). Un build sans le SDK n’envoie rien.',
    visits: 'Visites',
    medianDuration: 'Durée médiane',
    finished: 'Terminées',
    perDay: 'Visites par jour',
    journey: 'Parcours',
    journeyHint: 'La part des visites qui atteint chaque chapitre, et le temps moyen passé dedans. Un clic sur un chapitre : ses visites.',
    openVisits: 'Voir les visites',
    allSessions: 'Voir toutes les sessions',
    sessionsTitle: 'Sessions',
    sessionsHint: 'Une par personne et par casque, sur les 90 derniers jours — sans nom. Orange : au-delà du temps prévu dans Portage Creator ; rouge : au-delà de la marge de dépassement du lieu.',
    chapterTime: 'Dans {chapter}',
    plannedShort: 'prévu {t}',
    overrunOnly: 'Seulement les dépassements',
    sortRecent: 'Les plus récentes',
    sortLongest: 'Les plus longues',
    outcomeAll: 'Toutes',
    groupColumn: 'Groupe',
    groupOf: '{label} · {n}',
    groupUnnamed: 'Groupe',
    groupFilter: 'Groupe {label}',
    clearFilter: 'Retirer',
    alone: 'Seul',
    chaptersColumn: 'Chapitres',
    overruns: '{n} dépassement{s}',
    profile: 'Profil',
    wholeVisit: 'Visite entière',
    wholeVisitLine: 'moyenne {avg} · médiane {median}',
    plannedTotal: 'prévu {planned} dans Portage Creator',
    detailChapters: 'Chapitres',
    detailLog: 'Journal',
    aborted: 'Abandons :',
    reasons: { quit: 'Quitté', sleep: 'Casque en veille', silent: 'Silencieux', left_experience: 'Sorti de l’expérience', unknown: 'Inconnu' },
    metrics: 'Métriques de l’expérience',
    metricsHint: 'Ce que chaque expérience mesure, tel que le studio le déclare : la clé que l’APK envoie, et comment la compter.',
    storagePerVisit: 'Disque consommé par visite',
    walkPerVisit: 'Distance parcourue par visite',
    bandAttendance: 'Fréquentation',
    bandInWork: 'Dans l’expérience',
    bandInTitle: 'Dans {title}',
    walkSub: 'max {max} · {total} au total · {n} visites',
    objects: {
      title: 'Objets manipulés',
      hint: 'Combien de visiteurs ont touché, saisi, collecté ou posé chaque objet. Une ligne par objet et par geste ; un clic sur une colonne chiffrée trie dans un sens, puis dans l’autre.',
      empty: 'Aucun objet manipulé sur cette période',
      emptyHint: 'Le casque envoie Metrics.Record("<interact|collect|place>.<geste>.<scène>.<objet>", 1) une fois par visite, objet et geste (interact.language.<langue> sans scène). Un objet absent de la liste n’a été manipulé par personne.',
      scene: 'Scène',
      allScenes: 'Toutes les scènes',
      allActions: 'Tous',
      action: 'Geste',
      actions: {
        'interact.touch': 'touché',
        'interact.grab': 'saisi',
        'interact.assisted': 'aidé',
        'interact.language': 'langue choisie',
        'collect.scanned': 'collecté · scanné',
        'collect.grabbed': 'collecté · saisi',
        'collect.assisted': 'collecté · aidé',
        'collect.attempted': 'collecte tentée',
        'place.manual': 'posé',
        'place.assisted': 'posé · aidé',
      } as Record<string, string>,
      count: '{n} objets',
      object: 'Objet',
      visitors: 'Visiteurs',
      visitShare: 'Part des visites',
      sceneShare: 'Part de la scène',
      legacy: 'ancien format',
      legacyHint: 'Une partie de ces visiteurs vient de builds jusqu’à 0.5.1215, qui envoyaient des millisecondes tenues et comptaient les rebonds de contact.',
      legacyNote: 'Les lignes « ancien format » comptent des visites de builds jusqu’à 0.5.1215 : une visite qui a eu l’objet compte, mais ces builds envoyaient aussi les rebonds de contact. Choisissez une version dans le filtre du haut pour les séparer.',
      showAll: 'Tout afficher ({n})',
      showLess: 'En afficher moins',
      filter: 'Filtrer par nom (ex. collect)',
      filterLabel: 'Filtrer les objets par nom',
      noMatch: 'Aucun objet ne correspond à ce filtre.',
      sortBy: 'Trier par {column}',
    },
    declare: 'Déclarer les métriques',
    noMetrics: 'Aucune métrique reçue.',
    undeclared: 'non déclarée',
    venueVisible: 'Visible au lieu',
    pickToDeclare: 'Choisissez une expérience pour déclarer ses métriques.',
    sandbox: 'bac à sable',
    sandboxExcluded: '« Tous les lieux » écarte les lieux bac à sable ({venues}) : choisissez-en un dans le filtre Lieu pour voir ses playtests.',
    aggregates: { sum: 'total par visite', max: 'maximum', avg: 'moyenne', last: 'dernière valeur de la visite', flag: 'atteint ou non', duration: 'durée' },
    comparison: 'Comparé par {by}',
    segment: 'Segment',
    unknown: 'Inconnu',
    visitsHint: 'Le détail des 90 derniers jours — sans nom ni carte.',
    noVisits: 'Aucune visite ici.',
    when: 'Début',
    outcome: 'Issue',
    outcomes: { finished: 'Terminée', aborted: 'Abandon', open: 'En cours' },
    duration: 'Durée',
    lastScene: 'Dernier chapitre',
    timeline: 'Chronologie de la visite',
    declareTitle: 'Métriques de l’expérience',
    declareHint: 'L’APK envoie un événement metric { clé, valeur } ; ici, on dit comment le compter. Une clé non déclarée est gardée et signalée.',
    declareEmptyHint: 'Ajoutez les clés que cette expérience émet.',
    addMetric: 'Ajouter une métrique',
    atlantisPreset: 'Préremplir les 4 mesures d’Atlantis',
    metricKey: 'Clé',
    metricLabelFr: 'Libellé (français)',
    metricLabelEn: 'Libellé (anglais)',
    aggregate: 'Compter en',
    unit: 'Unité',
    duplicateKey: 'Deux métriques ont la même clé.',
    venueSection: 'Jeu',
    venueSectionHint: 'Comment se passent les visites chez vous : durée, visites terminées, chapitres, et les métriques que le studio partage avec vous.',
  },
  incidents: {
    title: 'Incidents d’exploitation',
    hint: 'Chaque « on a dû aider » noté dans un lieu, avec sa catégorie, la note telle quelle, le chapitre où était la personne et l’état du casque à ce moment. Jamais le nom du visiteur.',
    empty: 'Aucun incident sur cette période.',
    all: 'Toutes les catégories',
    allVenues: 'Tous les lieux',
    when: 'Quand',
    venue: 'Lieu',
    category: 'Catégorie',
    headset: 'Casque',
    chapter: 'Chapitre',
    note: 'Note',
    by: 'Par',
    noHeadset: 'aucun casque appairé',
    state: 'Le casque à ce moment',
    worn: 'porté',
    notWorn: 'pas porté',
    charging: 'en charge',
    asleep: 'en veille',
    thermal: 'thermique Android {level}/6',
    crashLink: '↯ Un plantage de ce casque au même moment',
    showAll: 'Voir tout ({n})',
    showLess: 'Afficher moins',
    stage: { arrived: 'arrivée', registered: 'enregistrée', paired: 'appairée', in_room: 'en salle', done: 'terminée' } as Record<string, string>,
  },
  diag: {
    lastCommand: 'Dernière commande Portage',
    outcomes: { acked: 'faite', failed: 'refusée', pending: 'en attente' },
    session: 'La séance à ce moment',
    logTail: 'Fin du journal Unity ({n} lignes)',
    yes: 'oui',
    no: 'non',
    sessionKeys: {
      groupSize: 'Groupe (visiteur compris)',
      serverConnected: 'Relié à son serveur de séance',
      visibleAvatars: 'Autres avatars affichés',
      personal: 'Paramètres personnels (valeurs)',
      config: 'Configuration distante (empreinte, origine, variante)',
      scene: 'Scène',
      sceneSeconds: 'Dans la scène depuis (s)',
      sessionSeconds: 'Dans l’expérience depuis (s)',
      role: 'Rôle',
      memoryFreeMB: 'Mémoire libre (Mo)',
      fps: 'Images/s',
      uptimeSeconds: 'App lancée depuis (s)',
    },
    room: 'Salle',
    temperature: 'Température',
    battery: 'Batterie',
    thermalLevel: 'niveau {n}',
    charging: 'en charge',
    unknownDevice: 'Hors du parc',
    resolve: 'Classer',
    originLabel: 'D’où il vient',
    origins: { app: 'App', sdk: 'SDK Portage', engine: 'Moteurs tiers', library: 'Unity et bibliothèques' } as Record<string, string>,
    originHints: {
      app: 'Le code de l’expérience elle-même — et les problèmes sans pile (gel, fin brutale), qu’on ne peut pas attribuer.',
      sdk: 'Le SDK Portage Room embarqué dans l’expérience.',
      engine: 'Un moteur tiers (XROAM…), reconnu à ses préfixes de pile dans le catalogue. Rien à corriger de notre côté.',
      library: 'Unity, Pico, Meta, XR Interaction Toolkit… ou une pile qui ne contient que de l’Unity et du .NET.',
    } as Record<string, string>,
    noIssuesFiltered: 'Rien pour ces origines',
    noIssuesFilteredHint: 'Il y a des problèmes d’autres origines : activez leur filtre ci-dessus.',
    resolveHint: 'Le retire de la liste. Il revient s’il se reproduit.',
    resolvedToast: 'Classé. Il reviendra s’il se reproduit.',
    resolveConfirmTitle: 'Classer ce problème ?',
    resolveConfirmBody: '« {message} » sort de la liste. Il revient de lui-même s’il se reproduit, et reste retrouvable sous « Classés ».',
    unresolve: 'Ressortir',
    unresolvedToast: 'De retour dans la liste.',
    viewOpen: 'En cours',
    viewResolved: 'Classés',
    noResolved: 'Rien de classé sur cette période',
    agoNow: 'à l’instant',
    agoMinutes: 'il y a {n} min',
    agoHours: 'il y a {n} h',
    agoDays: 'il y a {n} j',
    pageOf: '{from}–{to} sur {n}',
    previous: 'Précédent',
    next: 'Suivant',
    times: 'Fois',
    timesHint: 'Nombre d’occurrences de cette erreur sur ce casque',
    noSnapshot: 'Rapport reçu avant qu’on relève l’état des casques : ni nom ni température.',
    title: 'Diagnostic',
    allWorks: 'Toutes les expériences',
    days: '{n} jours',
    health: 'Séances sans plantage',
    healthHint: 'Par expérience et par version : la question à se poser avant de déployer — est-ce que ça va mieux qu’avant ?',
    noSessions: 'Aucune séance rapportée pour l’instant',
    noSessionsHint: 'Le serveur de l’expérience envoie les séances par version dès que le diagnostic du SDK Portage Room est branché dans l’expérience.',
    work: 'Expérience',
    version: 'Version',
    sessions: 'Séances',
    crashed: 'Plantées',
    crashFree: 'Sans plantage',
    issues: 'Problèmes',
    issuesHint: 'Regroupés par empreinte, les plus fréquents d’abord. Sur un casque, c’est un casque à regarder ; sur quarante, un bogue à corriger.',
    noIssues: 'Rien de rapporté sur cette période',
    noIssuesHint: 'Les exceptions, blocages et fins brutales arrivent ici depuis les serveurs d’expérience, jamais directement des casques.',
    problem: 'Problème',
    occurrences: 'Occurrences',
    devices: 'Appareils',
    venues: 'Lieux',
    versions: 'Versions',
    lastSeen: 'Vu en dernier',
    kept: 'Conservé',
    keep: 'Conserver',
    unkeep: 'Ne plus conserver',
    retentionHint: 'Les rapports et leurs captures s’effacent 30 jours après leur arrivée. « Conserver » soustrait ce problème à l’effacement.',
    keptHint: 'Conservé depuis le {date} : ses rapports et captures ne s’effacent pas à 30 jours.',
    reportLine: '{n} occurrence(s), {from} → {to}',
    breadcrumbs: 'Chemin avant le problème ({n} étapes)',
    screenshot: 'Capture au moment du problème',
    showScreenshot: 'Voir la capture',
    natures: {
      exception: 'Exception',
      unhandled: 'Non rattrapée',
      hang: 'Blocage',
      abnormal_exit: 'Fin brutale',
      assert: 'Assertion',
      error: 'Erreur',
      warning: 'Avertissement',
    },
  },
  eng: {
    title: 'Moteur de l’expérience',
    catalogue: 'Moteurs',
    integrations: 'Intégrations',
    intro:
      'Quelle plateforme fait tourner cette expérience en salle. Portage par défaut ; une expérience sous licence peut tourner chez un partenaire, et Portage Cloud y inscrit alors chaque visiteur à la fin de l’enregistrement.',
    engine: 'Moteur',
    nativeHint:
      'Tourne sur l’ingestion de Portage. Rien d’autre à configurer — voir docs/INTEGRATION-UNITY.md pour ce que le build Unity envoie.',
    credentials: 'Votre compte chez eux',
    required: 'requis',
    consentNote:
      'Leurs conditions et leur politique de confidentialité sont montrées au visiteur à la fin de votre parcours, et c’est la case qu’il coche que Portage Cloud transmet. Personne n’est inscrit sans avoir accepté.',
    mapping: 'Règles de correspondance',
    mappingHint:
      'Leur formulaire à gauche, d’où vient la valeur à droite. C’est ici que nos prénom et nom séparés deviennent leur Name unique, et que notre date de naissance devient leur âge.',
    enable: 'Inscrire les visiteurs chez ce partenaire à la fin du parcours',
    cannotEnable: 'Pas encore activable — il manque :',
    blocked: 'Envois retenus',
    driftTitle: 'Leur formulaire a changé',
    driftWhat:
      'Rien ne part, et rien n’est perdu : ce qui était en file attend. Relisez leur formulaire, vérifiez que les règles tiennent toujours, puis relâchez.',
    probe: 'Lire leur formulaire maintenant',
    probeResult: 'Ce que leur formulaire déclare aujourd’hui',
    probeChanged: 'diffère de ce qu’on connaissait',
    probeSame: 'inchangé',
    release: 'Relâcher et rejouer',
    log: 'Livraisons',
    noLog: 'Rien n’a encore été envoyé.',
    noLogHint:
      'Une livraison se met en file à la fin de chaque enregistrement et part dans la minute. Le visiteur ne l’attend jamais.',
    when: 'Quand',
    status: 'Statut',
    who: 'Inscrit',
    externalId: 'Leur identifiant',
    detail: 'Détail',
    attempts: 'tentatives',
    statuses: { pending: 'En file', held: 'Retenue', sent: 'Envoyée', failed: 'Échouée' },
    noBridge: 'Aucun moteur tiers disponible.',
    noBridgeHint:
      'C’est Studio Rabaska qui les déclare. Un moteur est une plateforme partenaire qui fait tourner une expérience en salle ; une fois déclarée, n’importe quel lieu peut y pointer une expérience et fournir ses propres identifiants.',
    accountHint: 'Un seul compte pour tout le lieu. Chaque expérience que vous pointez vers {engine} l’utilise.',
    accountSet: 'Ce lieu a un compte chez {engine}.',
    accountNone: 'Ce lieu n’a pas encore de compte chez ce partenaire — rien ne peut partir.',
    accountMissing: 'Configurez d’abord le compte dans les réglages du lieu : rien n’aurait pu partir.',
    goToSettings: 'Réglages du lieu',
    studioSetsUp: 'En tant que studio, vous mettez en place les moteurs de ce lieu dans la console.',
    declination: 'Déclinaison',
    declinations: { hybrid: 'Hybride — nos paquets dedans', probe: 'Probe — observée seulement' },
    declinationHints: {
      hybrid: 'Le partenaire fait tourner la séance avec nos paquets dedans : Portage pilote les casques. Les APK hybrides sont proposés.',
      probe: 'L’APK d’un partenaire, sans nos paquets : Portage l’installe et l’observe, sans le piloter. Les APK probe sont proposés.',
    },
    openInConsole: 'Ouvrir dans la console → Moteurs',
    noPartner: 'Aucun moteur partenaire pour ce lieu',
    noPartnerHint: 'Vous avez un compte XROAM (ou un autre partenaire) ? Dites-le au Studio Rabaska : le studio s’occupe de la partie technique, rien à saisir ici.',
    inPlace: 'En place',
    inPlaceHint: 'Mis en place par le studio. Les visiteurs sont inscrits chez {engine} à la fin de l’enregistrement.',
    blockedVenueHint: 'Le formulaire du partenaire a changé : les inscriptions sont retenues le temps que le studio vérifie la correspondance. Rien n’est perdu — elles partent une fois relâchées.',
    signupOn: 'visiteurs inscrits chez eux',
    signupOff: 'inscription coupée',
    runsOn: 'Tourne sur {engine}',
    setByStudio: 'Mis en place par le Studio Rabaska.',
    venueAccounts: 'Comptes des lieux',
    venueAccountsHint: 'Chaque lieu nous dit s’il a XROAM ; on pose ici ses identifiants techniques et la correspondance des champs. Le lieu n’en voit que l’état et le journal des inscriptions.',
    incomplete: 'Pas encore utilisable — il manque :',
    usedBy: 'Utilisé par',
    usedByNone: 'Aucune expérience ne pointe encore vers ce moteur.',
    enginePairing: 'Lire l’appairage chez le moteur',
    enginePairingHint: 'En hybride, le moteur dit lequel de ses utilisateurs est sur quel casque, et la salle appaire la carte d’elle-même : par l’identifiant du moteur, sinon par le prénom et le nom. Un utilisateur que Portage ne connaît pas reçoit sa propre carte. En salle et terminé suivent toujours le casque. Désactivé : Portage seul, comme avant.',
    overrides: 'Cette expérience a ses propres identifiants',
    overridesHint:
      'Laisser vide pour utiliser le compte du lieu. Utile quand une salle a son propre point d’inscription chez le partenaire — le texte grisé montre ce que porte le compte du lieu.',
    checkNow: 'Vérifier maintenant',
    checkNowHint: 'Relit le formulaire d’inscription de chaque partenaire (XROAM…) pour repérer un champ qui a changé. Se fait tout seul deux fois par jour (6 h et 18 h, heure de Montréal) ; ce bouton le force maintenant.',
    autoCheck: 'Formulaires des partenaires vérifiés automatiquement à 6 h et 18 h',
    lastRead: 'dernière lecture le {at}',
    newEngine: 'Nouveau moteur',
    noEngine: 'Aucun moteur déclaré.',
    catalogueHint:
      'Un moteur est une plateforme qui fait tourner une expérience en salle. En déclarer un ici permet à n’importe quel lieu d’y pointer une expérience, d’y coller ses propres identifiants et de faire correspondre les champs — sans une ligne de code.',
    kind: 'Type',
    native: 'Portage',
    bridge: 'Pont',
    usage: 'Lieux',
    active: 'activés',
    fingerprint: 'Empreinte du formulaire',
    vendor: 'Éditeur',
    definition: 'Déclaration',
    definitionHint:
      'Leurs champs, les appels à rejouer, et où lire leur formulaire vivant. C’est une recopie de ce que vous lisez chez eux — le garder en JSON évite une couche de traduction entre les deux.',
    badJson: 'JSON invalide :',
    nothingToProbe: 'Aucun moteur ne déclare de sonde.',
    probeUnread: 'n’a pas pu être lu — vérifiez les identifiants et la sonde',
    sources: {
      ignore: 'Ne pas envoyer',
      fullName: 'Prénom et nom',
      firstName: 'Prénom',
      lastName: 'Nom',
      email: 'Courriel',
      age: 'Âge, depuis la date de naissance',
      birthdate: 'Date de naissance',
      gender: 'Genre',
      civility: 'Civilité',
      locale: 'Langue',
      engineConsent: 'Leurs conditions, acceptées',
      hasGuardian: 'A un tuteur',
      yes: 'Toujours oui',
      no: 'Toujours non',
    },
  },
  notify: {
    title: 'Notifications',
    discord: 'Discord',
    url: 'Adresse du webhook',
    urlHint:
      'Depuis Paramètres du salon › Intégrations, dans Discord. Laissez vide pour n’envoyer rien. Seule une adresse discord.com est acceptée : ce champ fait sortir des noms de visiteurs de Portage Cloud.',
    events: 'Ce qu’on annonce',
    eventsHint: 'Une ligne dans le salon à chaque fois. Rien n’est annoncé deux fois.',
    noneChosen: 'Rien de coché — le canal est posé mais restera muet.',
    notDiscord: 'Ce n’est pas une adresse de webhook Discord.',
    test: 'Tester',
    testOk: 'Envoyé — regardez le salon.',
    testTimeout: 'Pas de réponse en 5 s. Réseau sortant filtré ?',
    testGone: 'Discord ne connaît plus ce webhook — recréez-le.',
    testFailed: 'Échec ({code}).',
    names: {
      arrival: 'Billet scanné',
      onboarding: 'Enregistrement signé',
      delivery_sent: 'Inscrit chez un partenaire',
      delivery_failed: 'Inscription en échec',
      retention_due: 'Dossiers à revoir',
      crash: 'Plantage dans un casque',
      app_error: 'Erreur dans un casque',
    },
    hints: {
      arrival: 'Quelqu’un se présente et son billet est accepté à la porte.',
      onboarding: 'Un visiteur a fini de signer — décharge, NDA, ce que votre parcours demande.',
      delivery_sent: 'Son compte existe chez la plateforme partenaire ; la salle le connaîtra.',
      delivery_failed: 'Toutes les tentatives ont échoué. Il faut regarder — la salle ne le connaîtra pas.',
      retention_due:
        'Hebdomadaire. Liste ce qui a dépassé sa durée de conservation. Rien n’est jamais supprimé automatiquement — quelqu’un valide.',
      crash: 'L’expérience a planté, gelé ou quitté dans un casque. Une fois par problème : nouveau, revenu après classement, ou après un jour de silence.',
      app_error: 'Une erreur sans plantage. Même cadence — peut être bavard sur une expérience en développement.',
    },
  },
  imp: {
    title: 'Importer des contacts',
    subtitle: 'Depuis un fichier CSV',
    drop: 'Déposez un fichier CSV ici',
    dropHint: 'ou cliquez pour en choisir un',
    explain:
      'Exportez votre liste de participants depuis Eventbrite (ou un autre outil) en CSV, puis déposez-la ici. L’étape suivante sert à dire quelle colonne est quoi — rien à renommer avant. Le fichier est lu sur ce poste ; rien n’est envoyé tant que vous n’avez pas confirmé.',
    emptyFile: 'Ce fichier n’a aucune ligne lisible.',
    badEncoding:
      'Les accents de ce fichier sont cassés — il n’est probablement pas en UTF-8. Réexportez-le, ou ouvrez-le dans Excel et enregistrez en « CSV UTF-8 ».',
    failed: 'L’import n’a pas pu s’exécuter. Rien n’a été écrit.',
    lines: 'lignes',
    delimiter: 'séparateur',
    ragged: 'lignes irrégulières',
    needEmail: 'Pointez une colonne sur Courriel — c’est ce qui identifie un contact.',
    ignore: 'Ignorer cette colonne',
    emptyColumn: 'vide sur toutes les lignes',
    fields: {
      email: 'Courriel',
      firstName: 'Prénom',
      lastName: 'Nom',
      phone: 'Téléphone',
      locale: 'Langue',
      marketingOptIn: 'Abonné',
      tags: 'Étiquettes',
      notes: 'Notes',
    },
    tagAll: 'Étiqueter tous les contacts importés',
    tagHint: 'Les rend retrouvables ensuite, et permet de défaire un mauvais import en filtrant dessus.',
    tagPlaceholder: 'Eventbrite — mars 2026',
    consent: 'Je détiens le consentement marketing des personnes de ce fichier.',
    consentHint:
      'Sans cette case, la colonne d’abonnement est lue mais ignorée, et tout le monde entre non abonné. Acheter un billet n’est pas s’abonner à une infolettre.',
    noConsentNote:
      'Personne ne sera marqué abonné : vous n’avez pas déclaré détenir le consentement. Vous pourrez toujours leur écrire une fois qu’ils s’abonnent.',
    back: 'Retour',
    checkFile: 'Vérifier le fichier',
    willCreate: 'Nouveaux',
    willComplete: 'Complétés',
    unchanged: 'Inchangés',
    invalid: 'Écartés',
    duplicate: 'Doublons',
    dedup: '{n} lignes répètent une adresse déjà présente dans le fichier — un export de billetterie répète l’acheteur sur chaque billet. {unique} personnes seront écrites.',
    line: 'Ligne',
    outcome: 'Ce qui se passe',
    noEmail: 'sans adresse',
    badEmail: 'adresse illisible',
    doImport: 'Importer {n} contacts',
    importing: 'Import en cours…',
    finished: 'Import terminé.',
    created: 'Créés',
    touched: 'Complétés',
    skipped: 'Écartés',
  },
  analytics: {
    title: 'Analytique',
    tabSales: 'Ventes',
    tabAudience: 'Public',
    tabExperience: 'En salle',
    allVenues: 'Tous les lieux',
    venues: 'Lieux',
    period: 'Période',
    last30: '30 jours',
    last90: '90 jours',
    last365: '12 mois',
    custom: 'Sur mesure',
    from: 'Du',
    to: 'Au',
    exportCsv: 'CSV',
    exportWorkbook: 'Classeur complet',
    exporting: 'Préparation…',
    revenue: 'Recette',
    refunded: 'remboursé',
    bookings: 'Réservations',
    seats: 'Places',
    fillRate: 'Remplissage',
    seatsSold: 'Places vendues',
    seatsAttended: 'Places venues',
    perDay: 'par jour',
    perWeek: 'par semaine',
    perMonth: 'par mois',
    partialLast: 'Le segment en pointillé est la période en cours, qui n’est pas finie.',
    byExperience: 'Par expérience',
    byTicketType: 'Par tarif',
    byChannel: 'Canal de vente',
    leadTime: 'Combien de temps à l’avance on réserve',
    leadMedian: 'Médiane',
    days: 'jours',
    leadPast: 'après coup',
    leadSameDay: 'le jour même',
    leadEve: 'la veille',
    leadWeek: 'dans la semaine',
    leadMonth: 'dans le mois',
    leadMore: 'plus d’un mois',
    newVsReturning: 'Nouveaux et revenants',
    fresh: 'Première visite',
    returning: 'Déjà venus',
    ageBands: 'Âge à la visite',
    ageCoverage: 'Date de naissance réelle connue pour {known} des {total} places venues ({percent} %).',
    ageFallback: 'Le reste n’est connu que par le tarif, ci-dessous.',
    ageMasked: 'Les tranches sous {min} personnes sont masquées plutôt qu’affichées — sur un créneau creux, une case désigne quelqu’un.',
    masked: 'trop peu',
    minorsAdults: 'Adultes et mineurs',
    adults: 'Adultes',
    minors: 'Mineurs',
    visitHours: 'Quand les gens viennent',
    hoursHint: 'Places par jour et par heure, dans l’heure locale de chaque lieu.',
    colourBy: 'Détailler par',
    everyone: 'Tout le monde',
    sceneTimes: 'Temps passé par scène',
    sceneHint: 'Secondes médianes dans chaque scène, croisées avec l’âge du visiteur. C’est le chiffre auquel répond un curseur de réglage distant.',
    roomDuration: 'Temps en salle',
    stages: 'Où se forme la file',
    stagesHint: 'Temps médian entre chaque palier du parcours d’un visiteur. Les cartes sorties sont exclues.',
    visitors: 'Visiteurs',
    toRegister: 'Arrivée → enregistrement',
    toPair: 'Enregistrement → casque',
    toStart: 'Casque → départ',
    nominal: 'Nominale',
    median: 'Médiane',
    runs: 'Séances jouées',
    visits: 'passages',
    noData: 'Rien sur cette fenêtre.',
    noDataHint: 'Élargissez la période, ou choisissez un autre lieu.',
    noTelemetry: 'Aucune expérience n’a encore remonté de données.',
    noTelemetryHint: 'Les temps de scène viennent du build serveur Unity. Le contrat est dans docs/INTEGRATION-UNITY.md.',
    unknownAge: 'âge inconnu',
  },
  ops: {
    portageDisabled: 'Portage désactivé (reconfiguration)',
    storageFree: 'disque {free} libres',
    storageOf: 'sur {total}',
    title: 'Exploitation',
    allRooms: 'Toutes les salles',
    roomFree: 'Libre',
    roomsMoved: 'Les salles, leurs serveurs, leurs clés et leur réseau se règlent dans',
    nothingReadYet: 'Ce casque n’a pas encore rapporté ses réglages. Les blocs marqués « Non lu » n’affichent aucune valeur : ils attendent celle du casque.',
    tagUnread: 'Non lu',
    tagFailed: 'Lecture en échec',
    stateOn: 'Activé',
    stateOff: 'Coupé',
    ipdModeAuto: 'Automatique',
    ipdModeManual: 'Manuel',
    settingGroups: {
      sound: 'Son et image',
      vision: 'Vision',
      control: 'Contrôle',
      actions: 'Actions',
    },
    ipdAutomatic: 'Mesuré par le casque : coupez « IPD automatique » pour le régler à la main.',
    settingHelp: {
      volume: 'Le volume du son dans le casque.',
      brightness: 'La luminosité de l’écran du casque.',
      passthrough: 'Montre la pièce réelle à travers les caméras du casque, à la place de l’expérience.',
      recenter: 'Réaligne la position du visiteur sur l’origine de la salle.',
      capture: 'Prend une capture de ce que voit le visiteur.',
      kiosk: 'Bloque les boutons système, pour que le visiteur ne puisse pas quitter l’expérience.',
      ipd: 'L’écart entre les deux lentilles, à régler sur l’écart des yeux du visiteur pour une image nette.',
      ipd_auto: 'Le casque mesure lui-même l’écart des yeux quand on le met.',
      cast: 'Diffuse en direct ce que voit le visiteur, pour le suivre depuis la console.',
      language: 'La langue de l’expérience : sous-titres, voix et menus.',
    },
    fleet: 'Parc',
    devices: 'Appareils',
    gear: 'Matériel',
    dense: 'Dense',
    cosy: 'Confortable',
    large: 'Grand',
    fold: 'Replier l’entête — une bande de cartes de plus',
    unfold: 'Réafficher l’entête',
    declared: 'Déclarés',
    seen: 'Qui répondent',
    usable: 'Utilisables',
    charging: 'En charge',
    offline: 'Silencieux',
    fault: 'En panne',
    lowBattery: 'Batterie faible',
    battery: 'Batterie',
    lastSeen: 'Vu',
    never: 'jamais',
    label: 'Libellé',
    pool: 'Parc',
    noDevices: 'Aucun appareil ne s’est encore annoncé.',
    sdkIdentity: 'Identité du SDK : {id} (le numéro affiché est celui de la boîte)',
    serialUnknown: 'Identité fabriquée par le SDK : le numéro de série de la boîte n’a pas encore été lu. « Vérifier » ou un balayage le lit par ADB.',
    noDevicesHint: 'Le build Unity annonce ses casques et son serveur dès qu’il porte une clé d’ingestion. Le studio l’émet par salle, dans Distribution.',
    gearSearch: 'Nom, série ou IP',
    gearSortLabel: 'Trier par',
    gearSort: { state: 'À traiter d’abord', name: 'Nom', battery: 'Batterie (la plus basse d’abord)', ip: 'Adresse IP' },
    gearFilter: { all: 'Tous', usable: 'Disponibles', in_experience: 'En séance', charging: 'En charge', offline: 'Hors ligne', fault: 'En défaut', low: 'Batterie basse' },
    gearNoMatch: 'Aucun appareil ne correspond à cette recherche.',
    openRun: 'Ouvrir une séance',
    start: 'Lancer',
    pause: 'Mettre en pause',
    resume: 'Reprendre',
    end: 'Terminer',
    abort: 'Interrompre',
    pending: 'En attente',
    briefing: 'Briefing',
    running: 'En cours',
    paused: 'En pause',
    ended: 'Terminée',
    aborted: 'Interrompue',
    delivered: 'La salle a acquitté',
    unlinked: 'non rattaché',
    config: 'Réglages distants',
    scenes: 'Scènes',
    schemaMissing: 'Le studio n’a pas encore publié de paramètre réglable pour cette expérience.',
    venueDefault: 'Défaut du lieu',
    studioDefault: 'Défaut du studio',
    forSegment: 'Pour ce tarif',
    reset: 'Revenir au défaut',
    savedReload: 'Enregistré — la salle a été invitée à relire.',
    sceneReport: 'Temps passé par scène',
    sceneHint: 'Temps médian dans chaque scène, croisé avec le billet que porte le visiteur. C’est ce qui dit à quelle scène donner plus de temps, et à qui.',
    visits: 'visites',
    median: 'médiane',
    noScenes: 'Aucune télémétrie de scène pour l’instant.',
    actInRoom: 'En séance',
    actWorn: 'Porté',
    actOffHead: 'Posé',
    rooms: 'Salles',
    roomOnline: 'Serveur en ligne',
    roomOffline: 'Serveur silencieux',
    roomUnassigned: 'Sans serveur',
    roomHeadsets: 'casques',
    roomName: 'Nom de la salle',
    newRoom: 'Ajouter une salle',
    roomOf: 'Salle',
    noRoom: 'Aucune salle',
    studioOnly: 'Réservé au studio',
    settings: 'Réglages',
    settingsOf: 'Réglages — {name}',
    temperature: 'Température',
    temperatureNone: 'Non rapportée',
    headroomOf: '{n} % du seuil de bridage',
    throttlesAt: 'bride à {t}',
    shutsDownAt: 'éteint à {t}',
    capsUnknown: 'Pas encore annoncé',
    capsUnknownHint: 'Ce casque n’a jamais battu. Ses réglages apparaissent dès que le serveur de la salle l’a vu.',
    capsNone: 'Aucun réglage distant',
    capsNoneHint: 'Le casque a battu et n’annonce aucun réglage applicable. Son build est peut-être antérieur au canal d’appareil.',
    noServerHolds: 'Aucun serveur ne tient ce casque',
    noServerHoldsHint: 'Il est allumé, mais aucun serveur de l’expérience ne le rapporte. Lancez l’expérience de sa salle, ou reposez-le sur son socle.',
    sending: 'Envoi…',
    readAt: 'lu sur le casque à {time}',
    ago: 'il y a {age}',
    ageMin: 'min',
    ageHour: 'h',
    ageDay: 'j',
    notReadYet: 'pas encore lu',
    neverReadable: 'la salle ne rapporte jamais cette valeur',
    requestedValue: 'dernière demande : {value}, à {time}',
    setValue: 'Régler',
    stream: 'Vue en direct',
    streamOffline: 'Le casque est éteint ou injoignable. Sa vue revient dès qu’il bat.',
    streamNotOnLan: 'Une vue en direct ne se regarde que depuis le réseau du lieu. Vous êtes connecté de l’extérieur.',
    streamNoSource: 'Ce casque ne publie pas de vue en direct. Le build de sa salle n’en propose pas encore.',
    streamStopped: 'La vue en direct est coupée sur ce casque.',
    streamNoAddress: 'Le casque diffuse mais n’a transmis aucune adresse. Sa salle est peut-être à moitié mise à jour — ou c’est le casque qui n’en rend pas.',
    streamError: 'Le service de diffusion est en panne sur ce casque : {reason}. Le redémarrer n’y changera rien tant que le service n’est pas relevé.',
    streamUnverified: 'Joint à son adresse enregistrée. Le casque ne rapporte pas s’il diffuse, donc cette image peut être son écran d’attente.',
    settingBroken: 'lecture impossible : {reason}',
    streamReload: 'Recharger',
    asleepTitle: 'En veille depuis {time}',
    asleepHint: 'Le casque répond au réseau, mais son application est suspendue (retiré, écran éteint) : il refuserait toute commande. Mettez-le sur la tête, ou appuyez sur son bouton d’alimentation, pour le réveiller.',
    asleepRetry: 'Réessayer quand même',
    asleep: 'En veille',
    asleepShort: 'En veille : réveillez d’abord le casque',
    captureTitle: 'Capture d’écran — {time}',
    captureDownload: 'Télécharger',
    captureBack: 'Revenir au direct',
    captureKept: 'Gardée sur Portage Room seulement, effacée au bout d’une semaine.',
    captureInRoom: 'Capture prise. Elle reste dans la salle : consultez-la sur Portage Room.',
    captureMissing: 'Le casque a pris la capture, mais l’image n’est pas arrivée à Portage Room.',
    streams: 'Vues',
    unverified: 'non confirmé',
    streamsOnline: '{n} sur {total} diffusent',
    streamsHidden: 'réafficher {n} masqués',
    streamCrop: 'Recadrer',
    streamMaximise: 'Agrandir',
    streamRestore: 'Retour',
    streamHide: 'Masquer',
    wallEnter: 'Mode mur',
    wallLeave: 'Quitter le mur',
    discover: 'Balayer le réseau',
    discoverHint: 'Cherche les casques qui répondent sur le réseau du lieu et inscrit l’adresse de ceux qu’il sait nommer. Depuis Portage Room seulement — le nuage n’est pas sur votre réseau.',
    discoverCidr: 'Bloc d’adresses à balayer',
    discoverRun: 'Balayer',
    discoverMatched: '{n} casques nommés et mis à jour',
    discoverUnknown: '{n} ont répondu sans dire qui ils sont',
    discoverNothing: 'Rien n’a répondu sur ce bloc.',
    discoverNotRoom: 'Seul Portage Room peut balayer : le nuage n’est pas sur le réseau du lieu.',
    discoverTooWide: 'Ce bloc est trop large. Le réseau d’un lieu est un /24 — /22 est le plus large accepté.',
    discoverBadCidr: 'Écrivez un bloc du genre 10.30.30.0/24.',
    streamsNoHeadset: 'Aucun casque dans ce lieu pour l’instant.',
    applyFailed: 'Refusé : {reason}',
    duringRun: 'Pas pendant qu’un groupe est en séance',
    confirmTitle: 'Appliquer maintenant ?',
    confirmBody: 'Quelqu’un est en séance avec ce casque. {name} prend effet immédiatement.',
    allowed: 'autorisée',
    blocked: 'bloquée',
    apply: 'Appliquer',
    run: 'Lancer',
    setVolume: 'Volume',
    setBrightness: 'Luminosité',
    setSleep: 'Autoriser la veille',
    setIpdAuto: 'IPD automatique',
    setIpd: 'Écart pupillaire',
    setCapture: 'Capture d’écran',
    setRecenter: 'Recentrer la vue',
    setPassthrough: 'Passthrough',
    setKiosk: 'Mode kiosque',
    setCast: 'Vue en direct',
    setLanguage: 'Langue',
    turnOn: 'Activer',
    turnOff: 'Désactiver',
    failUnsupported: 'le casque ne sait pas le faire',
    failNotBound: 'service entreprise non lié',
    failPermission: 'permission manquante sur le casque',
    failInvalidValue: 'valeur non comprise',
    failOutOfRange: 'valeur hors bornes',
    failOffline: 'casque injoignable',
    failTimeout: 'pas de réponse du casque',
    failNoDevice: 'casque inconnu de la salle',
    failDeviceError: 'le casque a refusé',
    failPartial: 'appliqué à une partie des casques seulement',
  },
  customers: {
    title: 'Clients',
    signatures: 'Enregistrements signés',
    noSignatures: 'Rien de signé pour l’instant',
    noSignaturesHint: 'Les décharges et NDA signés par ce foyer apparaîtront ici, avec le parcours et la date.',
    invalidate: 'Invalider',
    invalidateTitle: 'Invalider cet enregistrement ?',
    invalidateBody: '{name} a signé {flow} le {date}. Le supprimer efface le texte signé et redemandera le parcours à la prochaine visite.',
    invalidateKeeps: 'La réservation, le billet et la fiche client ne bougent pas.',
    erase: 'Effacer ce visiteur',
    eraseTitle: 'Effacer les données de ce visiteur ?',
    eraseBody:
      'Vide le nom, le courriel et le téléphone de {name} de la fiche client, du foyer, des réservations et de l’historique de salle. Les compteurs, les montants et les dates sont conservés : les chiffres ne bougent pas.',
    eraseKeeps:
      'Les décharges signées sont conservées entières — ce sont des documents opposables — mais détachées de tout le reste. C’est le seul endroit où le nom demeure. C’est irréversible.',
    erased: 'Visiteur anonymisé. Décharges conservées, détachées.',
    segment: 'Segment',
    allContacts: 'Tous les contacts',
    bookings: 'Réservations',
    visits: 'Visites',
    lifetimeValue: 'Valeur cumulée',
    lastVisit: 'Dernière visite',
    tags: 'Étiquettes',
    notes: 'Note interne',
    notesHint: 'Visible par votre équipe seulement. Enregistrée quand vous cliquez ailleurs.',
    history: 'Historique',
    since: 'depuis',
    noHistory: 'Aucune réservation.',
    unsubscribeAction: 'Le désabonner',
    subscribed: 'Abonné',
    unsubscribed: 'Désabonné',
    emailColumn: 'Courriels',
    pendingWaivers: 'Décharges en attente',
    pendingWaiversHint: 'Signées, mais rattachées à aucune réservation — le plus souvent par le QR mural, avant de réserver ou sous un autre courriel. Elles se rattachent seules dès qu’une réservation correspond.',
    minor: 'Mineur',
    channelKiosk: 'Tablette',
    channelLink: 'Lien',
    channelQr: 'QR mural',
    channelCounter: 'Comptoir',
    noConsent: 'Sans consentement',
    export: 'Exporter en CSV',
    exportNote: 'Vos contacts, à vous d’emporter. Fichier complet, sans limite.',
    searchPlaceholder: 'Nom, courriel, téléphone',
    noResults: 'Aucun contact ne correspond.',
    newContact: 'Nouveau contact',
    newContactHint: 'Un contact n’a pas besoin de réservation. S’il réserve plus tard, la vente retombe sur cette fiche.',
    createContact: 'Créer le contact',
    email: 'Courriel',
    firstName: 'Prénom',
    lastName: 'Nom',
    optInLabel: 'A accepté de recevoir des courriels',
    optInHint: 'À cocher seulement s’il l’a dit. Sans ça le contact existe, mais aucune campagne ne l’atteint.',
    knownAt: 'Connu de',
    sourceBooking: 'A réservé',
    sourceOnboarding: 'Enregistré sur place',
    sourceLead: 'A laissé son adresse',
    sourceManual: 'Ajouté par l’équipe',
    sourceImport: 'Importé',
    notKnownHere: 'N’est pas un contact de ce lieu — les campagnes d’ici ne l’atteindront pas.',
  },
    retention: {
      title: 'Revue de conservation',
      backlog: '{n} dossiers · le plus ancien en retard de {days} jour(s)',
      hint: 'Rien n’est jamais supprimé automatiquement. Ces dossiers ont dépassé leur durée de conservation et attendent une décision.',
      waivers: 'Décharges au-delà de leur durée',
      customers: 'Fiches clients sans activité',
      overdue: 'en retard de {days} jour(s)',
      deleteWaivers: 'Supprimer {n} décharge(s)',
      anonymiseCustomers: 'Anonymiser {n} fiche(s)',
      confirmWaivers: 'Supprimer ces décharges ?',
      confirmWaiversBody:
        'Une décharge est un document opposable, et le seul endroit où un nom demeure après anonymisation. La supprimer est définitif.',
      confirmCustomers: 'Anonymiser ces fiches ?',
      confirmCustomersBody:
        'Les noms, courriels et téléphones sont vidés. Les compteurs, les montants et les dates sont conservés : les chiffres ne bougent pas.',
      done: '{waivers} décharge(s) supprimée(s), {customers} fiche(s) anonymisée(s).',
    },
  experiences: {
    title: 'Expériences',
    duration: 'Durée',
    capacity: 'Capacité',
    published: 'En vente',
    draft: 'Hors billetterie',
    upcomingSessions: 'créneaux à venir',
    ticketTypes: 'Tarifs',
    schedules: 'Programmation',
    noExperiences: 'Aucune expérience pour le moment.',
  },
}

export type Strings = typeof en
export type Locale = 'fr' | 'en'
export const dictionaries: Record<Locale, Strings> = { fr: fr as Strings, en }
